{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2024                                      }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCDataGrid;

interface

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF FMXLIB}
{$DEFINE SUPPORTABSTRACT}
{$ENDIF}
{$IFDEF VCLLIB}
{$DEFINE SUPPORTABSTRACT}
{$ENDIF}

uses
  {$IFDEF VCLLIB}
  Windows, Messages,
  {$ENDIF}
  Classes, SysUtils, Types, WEBLib.TMSFNCRadioGroup, WEBLib.TMSFNCPopup, WEBLib.TMSFNCCustomControl, WEBLib.TMSFNCDataGridCell, WEBLib.TMSFNCDataGridRenderer,
  WEBLib.TMSFNCDataGridData, WEBLib.Graphics, WEBLib.TMSFNCBitmapContainer, WEBLib.TMSFNCGraphics, WEBLib.StdCtrls, WEBLib.TMSFNCScrollBar, WEBLib.TMSFNCDataGridCore,
  WEBLib.TMSFNCDataGridBase, WEBLib.ExtCtrls, WEBLib.TMSFNCButton, WEBLib.Menus, WEBLib.Controls, WEBLib.Forms, WEBLib.TMSFNCTypes, WEBLib.TMSFNCCustomComponent, WEBLib.TMSFNCGraphicsTypes
  ,Generics.Collections, Rtti, Generics.Defaults
  {$IFNDEF WEBLIB}
  ,UITypes
  {$ENDIF}
  {$IFDEF FMXLIB}
  ,FMX.Types, FMX.ListBox
  {$ENDIF}
  {$IFDEF SUPPORTABSTRACT}
  ,TMS.TMSFNCDataSet, Data.DB
  {$ENDIF}
  ;

const
  TTMSFNCDataGridDocURL = TTMSFNCBaseDocURL + 'tmsfncuipack/components/ttmsfncdatagrid/';
  {$IFDEF ANDROID}
  SCROLLINGDELAY = 40;
  {$ELSE}
  SCROLLINGDELAY = 0;
  {$ENDIF}

const
  MAJ_VER = 1; // Major version nr.
  MIN_VER = 2; // Minor version nr.
  REL_VER = 0; // Release nr.
  BLD_VER = 0; // Build nr.

  // version history
  // v0.9.0.0 : first beta release
  // v0.9.0.1 : Fixed: Access violation while expanding in connection with TTMSFNCDataGridDatabaseAdapter
  //          : Improved: Added GridColumn & ColumnAtField properties to TTMSFNCDataGridDatabaseAdapter to get easier access to grid column properties
  // v0.9.0.2 : New: gsmDisjunctCellRange mode to select items like in Windows Explorer (selection types & mode might change in future BETA updates)
  //          : Improved: Ability to mark filter items with TStringList objects in OnNeedFilterDropDown event (filter items might change in future BETA updates)
  //          :         : - Sample: AValues.AddObject(">= 10", TMSFNCDataGrid1.Filter.CommonDoubleQuotedItem);
  //          :         : - 1) CommonItem (counts towards global check all item)
  //          :         : - 2) CommonDoubleQuotedItem (counts towards global check all item, double quoted)
  //          :         : - 3) UniqueItem (does not count towards global check all item)
  //          :         : - 4) UniqueDoubleQuotedItem (does not count towards global check all item, double quoted)
  //          : Improved: Removed DataGrid property to avoid circular references in TTMSFNCDataGridDatabaseAdapter
  //          : Fixed: Issue with mouse wheel scrolling in combination with sub grid in VCL
  //          : Fixed: Issue updating sub-grid in VCL
  // v0.9.1.0 : New: Sides property at cell Layout
  //          : New: Options.URL.AutoDetect (automatically detects HTTP, HTTPS, FTP, FILE, WWW, MAILTO as links)
  //          : New: Options.URL.AutoOpen (automatically opens URLs in the default application)
  //          : Fixed: Issue with progressbar rendering in VCL
  //          : Fixed: Issue toggling Booleans property in OnCellCheckChanged event
  //          : Fixed: Issue handling mouse wheel in detail grid in a master-detail relationship
  // v1.0.0.0 : first release
  //          : Fixed: Issue with DisplayToRealCell function not returning a value
  //          : Fixed: Issue with TAB key causing exception in TMS WEB Core
  // v1.0.0.1 : Fixed: Issue with modal dialogs in combination with double-click events
  // v1.0.0.2 : Fixed: Issue with column settings related to cell controls not being transfered properly
  //          : Fixed: Issue focusing filter edit on iOS & Android
  //          : Improved: Strings[Col, Row] property is writable now
  // v1.0.0.3 : Fixed: Issue updating grid through editable cell controls
  //          : Fixed: Issue with selection count when there is no selection
  //          : Fixed: Issue calculating stretch in VCL with indexes
  // v1.0.0.4 : Fixed: Issue with stretching index based & scrolling
  //          : Improved: Cell merging able to overwrite and extend existing merges
  // v1.1.0.0 : New: Options.Keyboard.HomeKeyHandling, Options.Keyboard.EndKeyHandling
  //          : New: Options.Filtering.HideEmptyGroups
  //          : Fixed: Issue with disappearing cell controls when updating grid in VCL
  // v1.2.0.0 : New: Filter Row (Options.Filtering.Controls := [gfcEditor]);
  //          : New: OnBeforeCreateCustomFilterEditor, OnCreateCustomFilterEditor, OnAfterCreateCustomFilterEditor events
  //          : New: FirstPage, LastPage, NextPage, PreviousPage, PageIndex and PageCount
  //          : Improved: Performance
  //          : Improved: Filtering algorithm and combinations of AND / OR
  //          : Improved: Exposed EditingCell, IsEditing
  //          : Improved: Layouts[] priority only for normal cells by default overridable with OnGetCellLayoutHasPriority
  //          : Improved: Options.IO.TrimSpaces to trim spaces around text while importing from CSV
  //          : Fixed: Issue with mouse wheel scrolling selecting last row
  //          : Fixed: consistent prefix for TTMSFNCDataGridRowType values
  //          : Fixed: Issue with text clipping
  //          : Fixed: respect spaces around text while importing from CSV
  //          : Fixed: Rounding issue stretching Columns and Rows on VCL

type
  TTMSFNCCustomDataGrid = class;

  TTMSFNCDataGridKeyboardMode = (gkmDefault, gkmGrid);

  TTMSFNCDataGridHeaderFooter = class(TPersistent)
  private
    FVisible: Boolean;
    FOnChangeRebuild: TNotifyEvent;
    FSize: Integer;
    FFill: TTMSFNCGraphicsFill;
    FStroke: TTMSFNCGraphicsStroke;
    FOnChange: TNotifyEvent;
    procedure SetSize(const Value: Integer);
    procedure SetVisible(const Value: Boolean);
    procedure SetFill(const Value: TTMSFNCGraphicsFill);
    procedure SetStroke(const Value: TTMSFNCGraphicsStroke);
  protected
    procedure DoFillChanged(Sender: TObject);
    procedure DoStrokeChanged(Sender: TObject);
    property Fill: TTMSFNCGraphicsFill read FFill write SetFill;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
    property Size: Integer read FSize write SetSize default 50;
    property Stroke: TTMSFNCGraphicsStroke read FStroke write SetStroke;
    property Visible: Boolean read FVisible write SetVisible default False;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  end;

  TTMSFNCDataGridHeader = class(TTMSFNCDataGridHeaderFooter)
  published
    property Size;
    property Visible;
  end;

  TTMSFNCDataGridFooter = class(TTMSFNCDataGridHeaderFooter)
  published
    property Size;
    property Visible;
  end;

  {$IFNDEF WEBLIB}
  TTMSFNCDataGridDesigner = class(TTMSFNCCustomControl)
  private
    {$IFDEF FMXLIB}
    FFirstMouseDown, FFirstMouseUp: Boolean;
    {$ENDIF}
    FLoadSampleBitmap, FCloseBitmap: TTMSFNCBitmap;
    FSortBitmap: TTMSFNCBitmap;
    FEditBitmap: TTMSFNCBitmap;
    FFilterBitmap: TTMSFNCBitmap;
    FGrid: TTMSFNCCustomDataGrid;
    FClose: TTMSFNCButton;
    FLoadSample: TButton;
    FSort, FFilter, FEdit: TCheckBox;
    FRecreate: Boolean;
    FCreateTimer: TTimer;
  protected
    function HandleDesignHitTest(const APoint: TPoint): Boolean; override;
    function GetControlBitmap(AControl: TControl): TTMSFNCBitmap;
    procedure ApplyStyle; override;
    procedure ResetToDefaultStyle; override;
    procedure AddControls;
    procedure RemoveControls;
    procedure RecreateControls;
    procedure UpdateControlAfterResize; override;
    procedure DoLoadSampleClick(Sender: TObject);
    procedure DoCloseClick(Sender: TObject);
    procedure DoSortClick(Sender: TObject);
    procedure DoFilterClick(Sender: TObject);
    procedure DoEditClick(Sender: TObject);
    procedure DoCreateTimer(Sender: TObject);
    procedure HandleMouseDown(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseUp(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure Draw({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
  end;
  {$ENDIF}

  TTMSFNCCustomDataGrid = class(TTMSFNCDataGridBase, ITMSFNCBitmapContainer{$IFDEF SUPPORTABSTRACT}, ITMSFNCDataObject{$ENDIF})
  private
    FFocusedRenderer, FDownRenderer: TTMSFNCDataGridRenderer;
    FFooter: TTMSFNCDataGridFooter;
    FHeader: TTMSFNCDataGridHeader;
    {$IFNDEF WEBLIB}
    FDesignerControl: TTMSFNCDataGridDesigner;
    {$ENDIF}
    FDesigner: Boolean;
    function GetAdapter: TTMSFNCDataGridDataAdapter;
    function GetBitmapContainer: TTMSFNCBitmapContainer;
    function GetBitmapNames(AColumn, ARow: Integer): string;
    function GetBitmaps(AColumn, ARow: Integer): TTMSFNCBitmap;
    function GetBooleans(AColumn, ARow: Integer): Boolean;
    function GetBottomRow: Integer;
    function GetCellAppearance: TTMSFNCDataGridCellAppearance;
    function GetCellData(AColumn, ARow: Integer): TTMSFNCDataGridCellItem;
    function GetCells(AColumn, ARow: Integer): TTMSFNCDataGridCellValue;
    function GetColumnCount: Integer;
    function GetColumnOrder(AColumn: Integer): Integer;
    function GetColumnPos(AColumn: Integer): Double;
    function GetColumnReverseOrder(AColumn: Integer): Integer;
    function GetColumns: TTMSFNCDataGridColumns;
    function GetColumnWidths(AColumn: Integer): Double;
    function GetComments(AColumn, ARow: Integer): string;
    function GetControls(AColumn, ARow: Integer): TTMSFNCDataGridCellControl;
    function GetCustomHorizontalScrollBar: TTMSFNCScrollBar;
    function GetCustomScrollBars: Boolean;
    function GetCustomVerticalScrollBar: TTMSFNCScrollBar;
    function GetDefaultColumnWidth: Double;
    function GetDefaultRowHeight: Double;
    function GetFilter: TTMSFNCDataGridDataFilter;
    function GetFixedBottomRowCount: Integer;
    function GetFixedColumnCount: Integer;
    function GetFixedRightColumnCount: Integer;
    function GetFixedRowCount: Integer;
    function GetFloats(AColumn, ARow: Integer): Double;
    function GetFocusedCell: TTMSFNCDataGridCellCoord;
    function GetFreezeColumnCount: Integer;
    function GetFreezeRowCount: Integer;
    function GetGlobalFont: TTMSFNCAppearanceGlobalFont;
    function GetGrids(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
    function GetHasRowOrder(ARow: Integer): Boolean;
    function GetHeader: TTMSFNCDataGridHeader;
    function GetHiddenColumnCount: Integer;
    function GetHiddenColumns(AColumn: Integer): Boolean;
    function GetHiddenColumnWidths(AColumn: Integer): Double;
    function GetHiddenRowCount: Integer;
    function GetHiddenRowHeights(ARow: Integer): Double;
    function GetHiddenRows(ARow: Integer): Boolean;
    function GetHints(AColumn, ARow: Integer): string;
    function GetHorizontalScrollBar: TScrollBar;
    function GetHorizontalScrollBarVisible: Boolean;
    function GetInts(AColumn, ARow: Integer): Int64;
    function GetLayouts(AColumn, ARow: Integer): TTMSFNCDataGridCellLayout;
    function GetLeftColumn: Integer;
    function GetManagedObjects(AColumn, ARow: Integer): TObject;
    function GetMergedColumns(AColumn: Integer): Boolean;
    function GetMergedRows(ARow: Integer): Boolean;
    function GetObjects(AColumn, ARow: Integer): TObject;
    function GetOnAfterApplyFilter: TTMSFNCDataGridAfterApplyFilterEvent;
    function GetOnAfterCloseInplaceEditor: TTMSFNCDataGridAfterCloseInplaceEditorEvent;
    function GetOnAfterCopyToClipboard: TTMSFNCDataGridAfterClipboardEvent;
    function GetOnAfterCutToClipboard: TTMSFNCDataGridAfterClipboardEvent;
    function GetOnAfterDrawCell: TTMSFNCDataGridAfterDrawCellEvent;
    function GetOnAfterDrawGrid: TTMSFNCDataGridAfterDrawGridEvent;
    function GetOnAfterOpenInplaceEditor: TTMSFNCDataGridAfterOpenInplaceEditorEvent;
    function GetOnAfterPasteFromClipboard: TTMSFNCDataGridAfterClipboardEvent;
    function GetOnAfterShowFilter: TTMSFNCDataGridAfterShowFilterEvent;
    function GetOnAfterShowFindDialog: TTMSFNCDataGridAfterShowFindDialogEvent;
    function GetOnAfterShowReplaceDialog: TTMSFNCDataGridAfterShowReplaceDialogEvent;
    function GetOnAfterSortColumn: TTMSFNCDataGridAfterSortColumnEvent;
    function GetOnAppendColumn: TTMSFNCDataGridAppendColumnEvent;
    function GetOnAppendRow: TTMSFNCDataGridAppendRowEvent;
    function GetOnBeforeApplyFilter: TTMSFNCDataGridBeforeApplyFilterEvent;
    function GetOnBeforeCloseInplaceEditor: TTMSFNCDataGridBeforeCloseInplaceEditorEvent;
    function GetOnBeforeCopyToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    function GetOnBeforeCutToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    function GetOnBeforeDrawCell: TTMSFNCDataGridBeforeDrawCellEvent;
    function GetOnBeforeOpenInplaceEditor: TTMSFNCDataGridBeforeOpenInplaceEditorEvent;
    function GetOnBeforePasteFromClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    function GetOnBeforeShowFilter: TTMSFNCDataGridBeforeShowFilterEvent;
    function GetOnBeforeShowFindDialog: TTMSFNCDataGridBeforeShowFindDialogEvent;
    function GetOnBeforeShowReplaceDialog: TTMSFNCDataGridBeforeShowReplaceDialogEvent;
    function GetOnBeforeSortColumn: TTMSFNCDataGridBeforeSortColumnEvent;
    function GetOnCanAppendColumn: TTMSFNCDataGridCanAppendColumnEvent;
    function GetOnCanAppendRow: TTMSFNCDataGridCanAppendRowEvent;
    function GetOnCanDeleteColumn: TTMSFNCDataGridCanDeleteColumnEvent;
    function GetOnCanDeleteRow: TTMSFNCDataGridCanDeleteRowEvent;
    function GetOnCanDragColumn: TTMSFNCDataGridCanDragColumnEvent;
    function GetOnCanDragRow: TTMSFNCDataGridCanDragRowEvent;
    function GetOnCanDropColumn: TTMSFNCDataGridCanDropColumnEvent;
    function GetOnCanDropRow: TTMSFNCDataGridCanDropRowEvent;
    function GetOnCanEditCell: TTMSFNCDataGridCanEditCellEvent;
    function GetOnCanInsertColumn: TTMSFNCDataGridCanInsertColumnEvent;
    function GetOnCanInsertRow: TTMSFNCDataGridCanInsertRowEvent;
    function GetOnCanSizeColumn: TTMSFNCDataGridCanSizeColumnEvent;
    function GetOnCanSizeRow: TTMSFNCDataGridCanSizeRowEvent;
    function GetOnCellAnchorClick: TTMSFNCDataGridCellAnchorClickEvent;
    function GetOnCellButtonClick: TTMSFNCDataGridCellClickEvent;
    function GetOnCellCheckBoxChange: TTMSFNCDataGridCellClickEvent;
    function GetOnCellClick: TTMSFNCDataGridCellClickEvent;
    function GetOnCellDblClick: TTMSFNCDataGridCellClickEvent;
    function GetOnCellEditGetData: TTMSFNCDataGridCellEditGetDataEvent;
    function GetOnCellEditSetData: TTMSFNCDataGridCellEditSetDataEvent;
    function GetOnCellEditValidateData: TTMSFNCDataGridCellEditValidateDataEvent;
    function GetOnCellRadioButtonChange: TTMSFNCDataGridCellClickEvent;
    function GetOnCellRightClick: TTMSFNCDataGridCellClickEvent;
    function GetOnCustomColumnCalculation: TTMSFNCDataGridDataCustomColumnCalculationEvent;
    function GetOnCustomRowCalculation: TTMSFNCDataGridDataCustomRowCalculationEvent;
    function GetOnColumnDropped: TTMSFNCDataGridColumnDroppedEvent;
    function GetOnColumnSized: TTMSFNCDataGridColumnSizedEvent;
    function GetOnCustomCompare: TTMSFNCDataGridDataCustomCompareEvent;
    function GetOnDeleteColumn: TTMSFNCDataGridDeleteColumnEvent;
    function GetOnDeleteRow: TTMSFNCDataGridDeleteRowEvent;
    function GetOnGetColumnCalculationOptions: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent;
    function GetOnGetRowCalculationOptions: TTMSFNCDataGridDataGetRowCalculationOptionsEvent;
    function GetOnGetCellClass: TTMSFNCDataGridGetCellClassEvent;
    function GetOnGetCellData: TTMSFNCDataGridDataGetCellDataEvent;
    function GetOnGetCellDataProperties: TTMSFNCDataGridDataGetCellDataPropertiesEvent;
    function GetOnGetCellDisplayValue: TTMSFNCDataGridDataGetCellDisplayValueEvent;
    function GetOnGetCellFormatting: TTMSFNCDataGridDataGetCellFormattingEvent;
    function GetOnGetCellLayout: TTMSFNCDataGridGetCellLayoutEvent;
    function GetOnGetCellProperties: TTMSFNCDataGridGetCellPropertiesEvent;
    function GetOnGetCustomGroup: TTMSFNCDataGridDataGetCustomGroupEvent;
    function GetOnGetGroupOptions: TTMSFNCDataGridDataGetGroupOptionsEvent;
    function GetOnGetInplaceEditorClass: TTMSFNCDataGridGetInplaceEditorClassEvent;
    function GetOnGetInplaceEditorProperties: TTMSFNCDataGridGetInplaceEditorPropertiesEvent;
    function GetOnGetInplaceEditorRect: TTMSFNCDataGridGetInplaceEditorRectEvent;
    function GetOnGetInplaceEditorTarget: TTMSFNCDataGridGetInplaceEditorTargetEvent;
    function GetOnGetInplaceEditorType: TTMSFNCDataGridGetInplaceEditorTypeEvent;
    function GetOnGetSortOptions: TTMSFNCDataGridDataGetSortOptionsEvent;
    function GetOnHScroll: TTMSFNCDataGridScrollEvent;
    function GetOnInsertColumn: TTMSFNCDataGridInsertColumnEvent;
    function GetOnInsertRow: TTMSFNCDataGridInsertRowEvent;
    function GetOnLoadCellData: TTMSFNCDataGridDataCellDataEvent;
    function GetOnLogMessage: TTMSFNCDataGridLogMessageEvent;
    function GetOnNeedFilterDropDownData: TTMSFNCDataGridNeedFilterDropDownData;
    function GetOnRowDropped: TTMSFNCDataGridRowDroppedEvent;
    function GetOnRowCollapse: TTMSFNCDataGridDataRowCollapseEvent;
    function GetOnRowExpand: TTMSFNCDataGridDataRowExpandEvent;
    function GetOnRowSized: TTMSFNCDataGridRowSizedEvent;
    function GetOnSaveCellData: TTMSFNCDataGridDataCellDataEvent;
    function GetOnSelectCell: TTMSFNCDataGridSelectCellEvent;
    function GetOnSelectingCell: TTMSFNCDataGridSelectingCellEvent;
    function GetOnSizeColumn: TTMSFNCDataGridSizeColumnEvent;
    function GetOnSizeRow: TTMSFNCDataGridSizeRowEvent;
    function GetOnVScroll: TTMSFNCDataGridScrollEvent;
    function GetOptions: TTMSFNCDataGridOptions;
    function GetReadOnlys(AColumn, ARow: Integer): Boolean;
    function GetRightColumn: Integer;
    function GetRowCount: Integer;
    function GetRowHeights(ARow: Integer): Double;
    function GetRowNodeChildCount(ARow: Integer): Integer;
    function GetRowNodeLevel(ARow: Integer): Integer;
    function GetRowNodeState(ARow: Integer): TTMSFNCDataGridRowNodeState;
    function GetRowOrder(ARow: Integer): Integer;
    function GetRowPos(ARow: Integer): Double;
    function GetRowReverseOrder(ARow: Integer): Integer;
    function GetRowText(ARow: Integer): string;
    function GetRowTypes(ARow: Integer): TTMSFNCDataGridRowType;
    function GetScrollMode: TTMSFNCDataGridScrollMode;
    function GetScrollUpdate: TTMSFNCDataGridScrollUpdate;
    function GetSelectedCellCount: Integer;
    function GetSelectedCells(AColumn, ARow: Integer): Boolean;
    function GetSelectedColumnCount: Integer;
    function GetSelectedColumns(AColumn: Integer): Boolean;
    function GetSelectedRowCount: Integer;
    function GetSelectedRows(ARow: Integer): Boolean;
    function GetSelection: TTMSFNCDataGridCellCoordRange;
    function GetSortIndexDictionary: TTMSFNCDataGridSortIndexDictionary;
    function GetStartColumn: Integer;
    function GetStartRow: Integer;
    function GetStates(AColumn, ARow: Integer): TTMSFNCDataGridCellState;
    function GetStopColumn: Integer;
    function GetStopRow: Integer;
    function GetStoredCells(AColumn, ARow: Integer): TTMSFNCDataGridCellValue;
    function GetStretchScrollBars: Boolean;
    function GetStrings(AColumn, ARow: Integer): string;
    function GetStrippedCells(AColumn, ARow: Integer): string;
    function GetTopRow: Integer;
    function GetTotalColumnWidth: Double;
    function GetTotalRowHeight: Double;
    function GetVerticalScrollBar: TScrollBar;
    function GetVerticalScrollBarVisible: Boolean;
    function GetVisibleColumnCount: Integer;
    function GetVisibleRowCount: Integer;
    function IsDefaultColumnWidthStored: Boolean;
    function IsDefaultRowHeightStored: Boolean;
    procedure SetAdapter(const Value: TTMSFNCDataGridDataAdapter);
    procedure SetBitmapContainer(const Value: TTMSFNCBitmapContainer);
    procedure SetBooleans(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetCellAppearance(const Value: TTMSFNCDataGridCellAppearance);
    procedure SetCells(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellValue);
    procedure SetColumnCount(const Value: Integer);
    procedure SetColumns(const Value: TTMSFNCDataGridColumns);
    procedure SetColumnWidths(AColumn: Integer; const Value: Double);
    procedure SetComments(AColumn, ARow: Integer; const Value: string);
    procedure SetControls(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellControl);
    procedure SetCustomScrollBars(const Value: Boolean);
    procedure SetDefaultColumnWidth(const Value: Double);
    procedure SetDefaultRowHeight(const Value: Double);
    procedure SetFixedBottomRowCount(const Value: Integer);
    procedure SetFixedColumnCount(const Value: Integer);
    procedure SetFixedRightColumnCount(const Value: Integer);
    procedure SetFixedRowCount(const Value: Integer);
    procedure SetFocusedCell(const Value: TTMSFNCDataGridCellCoord);
    procedure SetFooter(const Value: TTMSFNCDataGridFooter);
    procedure SetFreezeColumnCount(const Value: Integer);
    procedure SetFreezeRowCount(const Value: Integer);
    procedure SetGlobalFont(const Value: TTMSFNCAppearanceGlobalFont);
    procedure SetHeader(const Value: TTMSFNCDataGridHeader);
    procedure SetHiddenColumns(AColumn: Integer; const Value: Boolean);
    procedure SetHiddenRows(ARow: Integer; const Value: Boolean);
    procedure SetHints(AColumn, ARow: Integer; const Value: string);
    procedure SetHorizontalScrollBarVisible(const Value: Boolean);
    procedure SetLeftColumn(const Value: Integer);
    procedure SetManagedObjects(AColumn, ARow: Integer; const Value: TObject);
    procedure SetMergedColumns(AColumn: Integer; const Value: Boolean);
    procedure SetMergedRows(ARow: Integer; const Value: Boolean);
    procedure SetObjects(AColumn, ARow: Integer; const Value: TObject);
    procedure SetOnAfterApplyFilter(const Value: TTMSFNCDataGridAfterApplyFilterEvent);
    procedure SetOnAfterCloseInplaceEditor(const Value: TTMSFNCDataGridAfterCloseInplaceEditorEvent);
    procedure SetOnAfterCopyToClipboard(const Value: TTMSFNCDataGridAfterClipboardEvent);
    procedure SetOnAfterCutToClipboard(const Value: TTMSFNCDataGridAfterClipboardEvent);
    procedure SetOnAfterDrawCell(const Value: TTMSFNCDataGridAfterDrawCellEvent);
    procedure SetOnAfterDrawGrid(const Value: TTMSFNCDataGridAfterDrawGridEvent);
    procedure SetOnAfterOpenInplaceEditor(const Value: TTMSFNCDataGridAfterOpenInplaceEditorEvent);
    procedure SetOnAfterPasteFromClipboard(const Value: TTMSFNCDataGridAfterClipboardEvent);
    procedure SetOnAfterShowFilter(const Value: TTMSFNCDataGridAfterShowFilterEvent);
    procedure SetOnAfterShowFindDialog(const Value: TTMSFNCDataGridAfterShowFindDialogEvent);
    procedure SetOnAfterShowReplaceDialog(const Value: TTMSFNCDataGridAfterShowReplaceDialogEvent);
    procedure SetOnAfterSortColumn(const Value: TTMSFNCDataGridAfterSortColumnEvent);
    procedure SetOnAppendColumn(const Value: TTMSFNCDataGridAppendColumnEvent);
    procedure SetOnAppendRow(const Value: TTMSFNCDataGridAppendRowEvent);
    procedure SetOnBeforeApplyFilter(const Value: TTMSFNCDataGridBeforeApplyFilterEvent);
    procedure SetOnBeforeCloseInplaceEditor(const Value: TTMSFNCDataGridBeforeCloseInplaceEditorEvent);
    procedure SetOnBeforeCopyToClipboard(const Value: TTMSFNCDataGridBeforeClipboardEvent);
    procedure SetOnBeforeCutToClipboard(const Value: TTMSFNCDataGridBeforeClipboardEvent);
    procedure SetOnBeforeDrawCell(const Value: TTMSFNCDataGridBeforeDrawCellEvent);
    procedure SetOnBeforeOpenInplaceEditor(const Value: TTMSFNCDataGridBeforeOpenInplaceEditorEvent);
    procedure SetOnBeforePasteFromClipboard(const Value: TTMSFNCDataGridBeforeClipboardEvent);
    procedure SetOnBeforeShowFilter(const Value: TTMSFNCDataGridBeforeShowFilterEvent);
    procedure SetOnBeforeShowFindDialog(const Value: TTMSFNCDataGridBeforeShowFindDialogEvent);
    procedure SetOnBeforeShowReplaceDialog(const Value: TTMSFNCDataGridBeforeShowReplaceDialogEvent);
    procedure SetOnBeforeSortColumn(const Value: TTMSFNCDataGridBeforeSortColumnEvent);
    procedure SetOnCanAppendColumn(const Value: TTMSFNCDataGridCanAppendColumnEvent);
    procedure SetOnCanAppendRow(const Value: TTMSFNCDataGridCanAppendRowEvent);
    procedure SetOnCanDeleteColumn(const Value: TTMSFNCDataGridCanDeleteColumnEvent);
    procedure SetOnCanDeleteRow(const Value: TTMSFNCDataGridCanDeleteRowEvent);
    procedure SetOnCanDragColumn(const Value: TTMSFNCDataGridCanDragColumnEvent);
    procedure SetOnCanDragRow(const Value: TTMSFNCDataGridCanDragRowEvent);
    procedure SetOnCanDropColumn(const Value: TTMSFNCDataGridCanDropColumnEvent);
    procedure SetOnCanDropRow(const Value: TTMSFNCDataGridCanDropRowEvent);
    procedure SetOnCanEditCell(const Value: TTMSFNCDataGridCanEditCellEvent);
    procedure SetOnCanInsertColumn(const Value: TTMSFNCDataGridCanInsertColumnEvent);
    procedure SetOnCanInsertRow(const Value: TTMSFNCDataGridCanInsertRowEvent);
    procedure SetOnCanSizeColumn(const Value: TTMSFNCDataGridCanSizeColumnEvent);
    procedure SetOnCanSizeRow(const Value: TTMSFNCDataGridCanSizeRowEvent);
    procedure SetOnCellAnchorClick(const Value: TTMSFNCDataGridCellAnchorClickEvent);
    procedure SetOnCellButtonClick(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellCheckBoxChange(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellClick(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellDblClick(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellEditGetData(const Value: TTMSFNCDataGridCellEditGetDataEvent);
    procedure SetOnCellEditSetData(const Value: TTMSFNCDataGridCellEditSetDataEvent);
    procedure SetOnCellEditValidateData(const Value: TTMSFNCDataGridCellEditValidateDataEvent);
    procedure SetOnCellRadioButtonChange(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellRightClick(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCustomColumnCalculation(const Value: TTMSFNCDataGridDataCustomColumnCalculationEvent);
    procedure SetOnCustomRowCalculation(const Value: TTMSFNCDataGridDataCustomRowCalculationEvent);
    procedure SetOnColumnDropped(const Value: TTMSFNCDataGridColumnDroppedEvent);
    procedure SetOnColumnSized(const Value: TTMSFNCDataGridColumnSizedEvent);
    procedure SetOnCustomCompare(const Value: TTMSFNCDataGridDataCustomCompareEvent);
    procedure SetOnDeleteColumn(const Value: TTMSFNCDataGridDeleteColumnEvent);
    procedure SetOnDeleteRow(const Value: TTMSFNCDataGridDeleteRowEvent);
    procedure SetOnGetColumnCalculationOptions(const Value: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent);
    procedure SetOnGetRowCalculationOptions(const Value: TTMSFNCDataGridDataGetRowCalculationOptionsEvent);
    procedure SetOnGetCellClass(const Value: TTMSFNCDataGridGetCellClassEvent);
    procedure SetOnGetCellData(const Value: TTMSFNCDataGridDataGetCellDataEvent);
    procedure SetOnGetCellDataProperties(const Value: TTMSFNCDataGridDataGetCellDataPropertiesEvent);
    procedure SetOnGetCellDisplayValue(const Value: TTMSFNCDataGridDataGetCellDisplayValueEvent);
    procedure SetOnGetCellFormatting(const Value: TTMSFNCDataGridDataGetCellFormattingEvent);
    procedure SetOnGetCellLayout(const Value: TTMSFNCDataGridGetCellLayoutEvent);
    procedure SetOnGetCellProperties(const Value: TTMSFNCDataGridGetCellPropertiesEvent);
    procedure SetOnGetCustomGroup(const Value: TTMSFNCDataGridDataGetCustomGroupEvent);
    procedure SetOnGetGroupOptions(const Value: TTMSFNCDataGridDataGetGroupOptionsEvent);
    procedure SetOnGetInplaceEditorClass(const Value: TTMSFNCDataGridGetInplaceEditorClassEvent);
    procedure SetOnGetInplaceEditorProperties(const Value: TTMSFNCDataGridGetInplaceEditorPropertiesEvent);
    procedure SetOnGetInplaceEditorRect(const Value: TTMSFNCDataGridGetInplaceEditorRectEvent);
    procedure SetOnGetInplaceEditorTarget(const Value: TTMSFNCDataGridGetInplaceEditorTargetEvent);
    procedure SetOnGetInplaceEditorType(const Value: TTMSFNCDataGridGetInplaceEditorTypeEvent);
    procedure SetOnGetSortOptions(const Value: TTMSFNCDataGridDataGetSortOptionsEvent);
    procedure SetOnHScroll(const Value: TTMSFNCDataGridScrollEvent);
    procedure SetOnInsertColumn(const Value: TTMSFNCDataGridInsertColumnEvent);
    procedure SetOnInsertRow(const Value: TTMSFNCDataGridInsertRowEvent);
    procedure SetOnLoadCellData(const Value: TTMSFNCDataGridDataCellDataEvent);
    procedure SetOnLogMessage(const Value: TTMSFNCDataGridLogMessageEvent);
    procedure SetOnNeedFilterDropDownData(const Value: TTMSFNCDataGridNeedFilterDropDownData);
    procedure SetOnRowDropped(const Value: TTMSFNCDataGridRowDroppedEvent);
    procedure SetOnRowExpand(const Value: TTMSFNCDataGridDataRowExpandEvent);
    procedure SetOnRowCollapse(const Value: TTMSFNCDataGridDataRowCollapseEvent);
    procedure SetOnRowSized(const Value: TTMSFNCDataGridRowSizedEvent);
    procedure SetOnSaveCellData(const Value: TTMSFNCDataGridDataCellDataEvent);
    procedure SetOnSelectCell(const Value: TTMSFNCDataGridSelectCellEvent);
    procedure SetOnSelectingCell(const Value: TTMSFNCDataGridSelectingCellEvent);
    procedure SetOnSizeColumn(const Value: TTMSFNCDataGridSizeColumnEvent);
    procedure SetOnSizeRow(const Value: TTMSFNCDataGridSizeRowEvent);
    procedure SetOnVScroll(const Value: TTMSFNCDataGridScrollEvent);
    procedure SetOptions(const Value: TTMSFNCDataGridOptions);
    procedure SetReadOnlys(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetRowCount(const Value: Integer);
    procedure SetRowHeights(ARow: Integer; const Value: Double);
    procedure SetRowNodeChildCount(ARow: Integer; const Value: Integer);
    procedure SetRowNodeLevel(ARow: Integer; const Value: Integer);
    procedure SetRowNodeState(ARow: Integer; const Value: TTMSFNCDataGridRowNodeState);
    procedure SetRowText(ARow: Integer; const Value: string);
    procedure SetRowTypes(ARow: Integer; const Value: TTMSFNCDataGridRowType);
    procedure SetStrings(AColumn, ARow: Integer; const Value: string);
    procedure SetScrollMode(const Value: TTMSFNCDataGridScrollMode);
    procedure SetScrollUpdate(const Value: TTMSFNCDataGridScrollUpdate);
    procedure SetSelectedCells(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetSelectedColumns(AColumn: Integer; const Value: Boolean);
    procedure SetSelectedRows(ARow: Integer; const Value: Boolean);
    procedure SetSelection(const Value: TTMSFNCDataGridCellCoordRange);
    procedure SetStretchScrollBars(const Value: Boolean);
    procedure SetTopRow(const Value: Integer);
    procedure SetVerticalScrollBarVisible(const Value: Boolean);
    function GetFilterAppearance: TTMSFNCDataGridFilterAppearance;
    procedure SetFilterAppearance(const Value: TTMSFNCDataGridFilterAppearance);
    function GetDragAppearance: TTMSFNCDataGridDragAppearance;
    procedure SetDragAppearance(const Value: TTMSFNCDataGridDragAppearance);
    function GetControlAligns(AColumn,
      ARow: Integer): TTMSFNCDataGridCellControlAlign;
    function GetControlPositions(AColumn,
      ARow: Integer): TTMSFNCDataGridCellControlPosition;
    function GetControlHeights(AColumn, ARow: Integer): Integer;
    function GetControlTexts(AColumn, ARow: Integer): string;
    function GetControlWidths(AColumn, ARow: Integer): Integer;
    procedure SetControlAligns(AColumn, ARow: Integer;
      const Value: TTMSFNCDataGridCellControlAlign);
    procedure SetControlPositions(AColumn, ARow: Integer;
      const Value: TTMSFNCDataGridCellControlPosition);
    procedure SetControlHeights(AColumn, ARow: Integer; const Value: Integer);
    procedure SetControlTexts(AColumn, ARow: Integer; const Value: string);
    procedure SetControlWidths(AColumn, ARow: Integer; const Value: Integer);
    function GetCellPopupMenu: TPopupMenu;
    procedure SetCellPopupMenu(const Value: TPopupMenu);
    function GetOnCellBeforeShowPopupMenu: TTMSFNCDataGridCellBeforeShowPopupMenuEvent;
    procedure SetOnCellBeforeShowPopupMenu(
      const Value: TTMSFNCDataGridCellBeforeShowPopupMenuEvent);
    function GetOnCellAfterNodeExpand: TTMSFNCDataGridCellAfterNodeExpandEvent;
    procedure SetOnCellAfterNodeExpand(
      const Value: TTMSFNCDataGridCellAfterNodeExpandEvent);
    function GetOnCellAfterNodeCollapse: TTMSFNCDataGridCellAfterNodeCollapseEvent;
    procedure SetOnCellAfterNodeCollapse(
      const Value: TTMSFNCDataGridCellAfterNodeCollapseEvent);
    function GetOnCellBeforeNodeExpand: TTMSFNCDataGridCellBeforeNodeExpandEvent;
    procedure SetOnCellBeforeNodeExpand(
      const Value: TTMSFNCDataGridCellBeforeNodeExpandEvent);
    function GetOnCellBeforeNodeCollapse: TTMSFNCDataGridCellBeforeNodeCollapseEvent;
    procedure SetOnCellBeforeNodeCollapse(
      const Value: TTMSFNCDataGridCellBeforeNodeCollapseEvent);
    function GetOnAfterDrawEmptySpace: TTMSFNCDataGridAfterDrawEmptySpaceEvent;
    function GetOnBeforeDrawEmptySpace: TTMSFNCDataGridBeforeDrawEmptySpaceEvent;
    procedure SetOnAfterDrawEmptySpace(
      const Value: TTMSFNCDataGridAfterDrawEmptySpaceEvent);
    procedure SetOnBeforeDrawEmptySpace(
      const Value: TTMSFNCDataGridBeforeDrawEmptySpaceEvent);
    function GetOnAfterLookup: TTMSFNCDataGridAfterLookupEvent;
    function GetOnBeforeLookup: TTMSFNCDataGridBeforeLookupEvent;
    procedure SetOnAfterLookup(const Value: TTMSFNCDataGridAfterLookupEvent);
    procedure SetOnBeforeLookup(const Value: TTMSFNCDataGridBeforeLookupEvent);
    procedure SetDesigner(const Value: Boolean);
    function GetFilteredRowCount: Integer;
    function GetOnGetCellHint: TTMSFNCDataGridGetCellHintEvent;
    procedure SetOnGetCellHint(const Value: TTMSFNCDataGridGetCellHintEvent);
    function GetColumnCalculation(AColumn: Integer; AName: string): TTMSFNCDataGridDataColumnCalculationArray;
    procedure SetColumnCalculation(AColumn: Integer; AName: string; const Value: TTMSFNCDataGridDataColumnCalculationArray);
    function GetRowCalculation(ARow: Integer; AName: string): TTMSFNCDataGridDataRowCalculationArray;
    procedure SetRowCalculation(ARow: Integer; AName: string; const Value: TTMSFNCDataGridDataRowCalculationArray);
    procedure SetFloats(AColumn, ARow: Integer; const Value: Double);
    procedure SetInts(AColumn, ARow: Integer; const Value: Int64);
    function GetControlMargins(AColumn, ARow: Integer): TTMSFNCMargins;
    function GetHasLayout(AColumn, ARow: Integer): Boolean;
    function GetOnAfterCreateFilterEditor: TTMSFNCDataGridAfterCreateFilterEditorEvent;
    function GetOnBeforeCreateFilterEditor: TTMSFNCDataGridBeforeCreateFilterEditorEvent;
    function GetOnCreateCustomFilterEditor: TTMSFNCDataGridCreateCustomFilterEditorEvent;
    procedure SetOnAfterCreateFilterEditor(
      const Value: TTMSFNCDataGridAfterCreateFilterEditorEvent);
    procedure SetOnBeforeCreateFilterEditor(
      const Value: TTMSFNCDataGridBeforeCreateFilterEditorEvent);
    procedure SetOnCreateCustomFilterEditor(
      const Value: TTMSFNCDataGridCreateCustomFilterEditorEvent);
    function GetColors(AColumn, ARow: Integer): TTMSFNCGraphicsColor;
    procedure SetColors(AColumn, ARow: Integer;
      const Value: TTMSFNCGraphicsColor);
    function GetFontColors(AColumn, ARow: Integer): TTMSFNCGraphicsColor;
    function GetFontNames(AColumn, ARow: Integer): string;
    function GetFontSizes(AColumn, ARow: Integer): Single;
    function GetFontStyles(AColumn, ARow: Integer): TFontStyles;
    function GetTextAligns(AColumn,
      ARow: Integer): TTMSFNCGraphicsTextAlign;
    function GetTextAngles(AColumn, ARow: Integer): Double;
    function GetVerticalTextAligns(AColumn,
      ARow: Integer): TTMSFNCGraphicsTextAlign;
    procedure SetFontColors(AColumn, ARow: Integer;
      const Value: TTMSFNCGraphicsColor);
    procedure SetFontNames(AColumn, ARow: Integer; const Value: string);
    procedure SetFontSizes(AColumn, ARow: Integer; const Value: Single);
    procedure SetFontStyles(AColumn, ARow: Integer; const Value: TFontStyles);
    procedure SetTextAligns(AColumn, ARow: Integer;
      const Value: TTMSFNCGraphicsTextAlign);
    procedure SetTextAngles(AColumn, ARow: Integer; const Value: Double);
    procedure SetVerticalTextAligns(AColumn, ARow: Integer;
      const Value: TTMSFNCGraphicsTextAlign);
    function GetOnGetCellLayoutHasPriority: TTMSFNCDataGridGetCellLayoutHasPriorityEvent;
    procedure SetOnGetCellLayoutHasPriority(
      const Value: TTMSFNCDataGridGetCellLayoutHasPriorityEvent);
    function GetEditingCell: TTMSFNCDataGridCellCoord;
    function GetPageCount: Integer;
    function GetPageIndex: Integer;
    procedure SetPageIndex(const Value: Integer);
  protected
    {$IFDEF SUPPORTABSTRACT}
    procedure Activate(AValue: Boolean);
    procedure AddRecord(ARecord: TValue);
    procedure DeleteRecord(AIndex: Integer);
    procedure InsertRecord(AIndex: Integer; ARecord: TValue);
    function CreateDefaultRecord: TValue;
    procedure InitializeFieldDefs(AFieldDefs: TFieldDefs);
    function GetRecordCount: Integer;
    function GetRecordIndex(ARecord: TValue): Integer;
    function GetRecord(AIndex: Integer): TValue;
    function GetRecordValue(ARecord: TValue; AField: string; AFieldType: TFieldType): ValueType;
    procedure SetRecordValue(ARecordIndex: Integer; AField: string; AValue: ValueType);
    function CompareRecords(AField: string; ARecord1, ARecord2: TValue): Integer;
    procedure SetNotification(ANotifyObject: ITMSFNCDataObjectNotification);
    function Clone: TObject;
    {$ENDIF}
    function DoRootIsFocused(Sender: TObject): Boolean; override;
    function GetCalculationRect: TRectF; override;
    function GetDocURL: string; override;
    function GetFooterRect: TRectF;
    function GetFooterSize: Double;
    function GetHeaderRect: TRectF;
    function GetHeaderSize: Double;
    function GetDesignerLeftSize: Double;
    function GetDesignerTopSize: Double;
    function GetDesignerRightSize: Double;
    function GetDesignerBottomSize: Double;
    function GetHintString: string; override;
    function GetVersion: string; override;
    function HasHint: Boolean; override;
    procedure FirstPage;
    procedure LastPage;
    procedure NextPage;
    procedure GoToPage(APageIndex: Integer);
    procedure PreviousPage;
    procedure ApplyStyle; override;
    procedure SetAdaptToStyle(const Value: Boolean); override;
    procedure ChangeDPIScale(M, D: Integer); override;
    {$IFDEF FMXLIB}
    procedure DoAbsoluteChanged; override;
    {$ENDIF}
    procedure DoEnter; override;
    procedure DoExit; override;
    procedure DoRootFocus(Sender: TObject); override;
    procedure DoRootUpdateCursor(Sender: TObject; ACursor: TCursor); override;
    procedure DrawFooter(AGraphics: TTMSFNCGraphics);
    procedure DrawHeader(AGraphics: TTMSFNCGraphics);
    procedure FooterChangedRebuild(Sender: TObject);
    procedure HandleDblClick(X, Y: Single); override;
    procedure HandleDialogKey(var Key: Word; Shift: TShiftState); override;
    procedure HandleKeyDown(var Key: Word; Shift: TShiftState); override;
    procedure HandleKeyPress(var Key: Char); override;
    procedure HandleKeyUp(var Key: Word; Shift: TShiftState); override;
    procedure HandleMouseDown(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseEnter; override;
    procedure HandleMouseLeave; override;
    procedure HandleMouseMove(Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseUp(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseWheel(Shift: TShiftState; WheelDelta: Integer; var Handled: Boolean); override;
    procedure HeaderChangedRebuild(Sender: TObject);
    procedure Notification(AComponent: TComponent; Operation: TOperation); override;
    procedure ResetToDefaultStyle; override;
    property Adapter: TTMSFNCDataGridDataAdapter read GetAdapter write SetAdapter;
    property BitmapContainer: TTMSFNCBitmapContainer read GetBitmapContainer write SetBitmapContainer;
    property BitmapNames[AColumn, ARow: Integer]: string read GetBitmapNames;
    property Bitmaps[AColumn, ARow: Integer]: TTMSFNCBitmap read GetBitmaps;
    property Booleans[AColumn, ARow: Integer]: Boolean read GetBooleans write SetBooleans;
    property BottomRow: Integer read GetBottomRow;
    property CellAppearance: TTMSFNCDataGridCellAppearance read GetCellAppearance write SetCellAppearance;
    property CellData[AColumn, ARow: Integer]: TTMSFNCDataGridCellItem read GetCellData;
    property CellPopupMenu: TPopupMenu read GetCellPopupMenu write SetCellPopupMenu;
    property Cells[AColumn, ARow: Integer]: TTMSFNCDataGridCellValue read GetCells write SetCells;
    property ColumnCalculations[AColumn: Integer; AName: string]: TTMSFNCDataGridDataColumnCalculationArray read GetColumnCalculation write SetColumnCalculation;
    property RowCalculations[ARow: Integer; AName: string]: TTMSFNCDataGridDataRowCalculationArray read GetRowCalculation write SetRowCalculation;
    property ColumnCount: Integer read GetColumnCount write SetColumnCount default 4;
    property ColumnOrders[AColumn: Integer]: Integer read GetColumnOrder;
    property ColumnPositions[AColumn: Integer]: Double read GetColumnPos;
    property ColumnReverseOrders[AColumn: Integer]: Integer read GetColumnReverseOrder;
    property Columns: TTMSFNCDataGridColumns read GetColumns write SetColumns;
    property ColumnWidths[AColumn: Integer]: Double read GetColumnWidths write SetColumnWidths;
    property Comments[AColumn, ARow: Integer]: string read GetComments write SetComments;
    property Colors[AColumn, ARow: Integer]: TTMSFNCGraphicsColor read GetColors write SetColors;
    property Controls[AColumn, ARow: Integer]: TTMSFNCDataGridCellControl read GetControls write SetControls;
    property ControlWidths[AColumn, ARow: Integer]: Integer read GetControlWidths write SetControlWidths;
    property ControlHeights[AColumn, ARow: Integer]: Integer read GetControlHeights write SetControlHeights;
    property ControlAligns[AColumn, ARow: Integer]: TTMSFNCDataGridCellControlAlign read GetControlAligns write SetControlAligns;
    property ControlMargins[AColumn, ARow: Integer]: TTMSFNCMargins read GetControlMargins;
    property ControlPositions[AColumn, ARow: Integer]: TTMSFNCDataGridCellControlPosition read GetControlPositions write SetControlPositions;
    property ControlTexts[AColumn, ARow: Integer]: string read GetControlTexts write SetControlTexts;
    property CustomHorizontalScrollBar: TTMSFNCScrollBar read GetCustomHorizontalScrollBar;
    property CustomScrollBars: Boolean read GetCustomScrollBars write SetCustomScrollBars;
    property CustomVerticalScrollBar: TTMSFNCScrollBar read GetCustomVerticalScrollBar;
    property DefaultColumnWidth: Double read GetDefaultColumnWidth write SetDefaultColumnWidth stored IsDefaultColumnWidthStored nodefault;
    property DefaultRowHeight: Double read GetDefaultRowHeight write SetDefaultRowHeight stored IsDefaultRowHeightStored nodefault;
    property DragAppearance: TTMSFNCDataGridDragAppearance read GetDragAppearance write SetDragAppearance;
    property EditingCell: TTMSFNCDataGridCellCoord read GetEditingCell;
    property Filter: TTMSFNCDataGridDataFilter read GetFilter;
    property FilteredRowCount: Integer read GetFilteredRowCount;
    property FilterAppearance: TTMSFNCDataGridFilterAppearance read GetFilterAppearance write SetFilterAppearance;
    property FixedBottomRowCount: Integer read GetFixedBottomRowCount write SetFixedBottomRowCount default 0;
    property FixedColumnCount: Integer read GetFixedColumnCount write SetFixedColumnCount default 0;
    property FixedRightColumnCount: Integer read GetFixedRightColumnCount write SetFixedRightColumnCount default 0;
    property FixedRowCount: Integer read GetFixedRowCount write SetFixedRowCount default 1;
    property Floats[AColumn, ARow: Integer]: Double read GetFloats write SetFloats;
    property FocusedCell: TTMSFNCDataGridCellCoord read GetFocusedCell write SetFocusedCell;
    property Footer: TTMSFNCDataGridFooter read FFooter write SetFooter;
    property FreezeColumnCount: Integer read GetFreezeColumnCount write SetFreezeColumnCount default 0;
    property FreezeRowCount: Integer read GetFreezeRowCount write SetFreezeRowCount default 0;
    property GlobalFont: TTMSFNCAppearanceGlobalFont read GetGlobalFont write SetGlobalFont;
    property TextAligns[AColumn, ARow: Integer]: TTMSFNCGraphicsTextAlign read GetTextAligns write SetTextAligns;
    property VerticalTextAligns[AColumn, ARow: Integer]: TTMSFNCGraphicsTextAlign read GetVerticalTextAligns write SetVerticalTextAligns;
    property FontSizes[AColumn, ARow: Integer]: Single read GetFontSizes write SetFontSizes;
    property FontStyles[AColumn, ARow: Integer]: TFontStyles read GetFontStyles write SetFontStyles;
    property FontNames[AColumn, ARow: Integer]: string read GetFontNames write SetFontNames;
    property FontColors[AColumn, ARow: Integer]: TTMSFNCGraphicsColor read GetFontColors write SetFontColors;
    property TextAngles[AColumn, ARow: Integer]: Double read GetTextAngles write SetTextAngles;
    property Grids[AColumn, ARow: Integer]: TTMSFNCDataGridRenderer read GetGrids;
    property HasRowOrder[ARow: Integer]: Boolean read GetHasRowOrder;
    property HasLayout[AColumn, ARow: Integer]: Boolean read GetHasLayout;
    property Header: TTMSFNCDataGridHeader read GetHeader write SetHeader;
    property HiddenColumnCount: Integer read GetHiddenColumnCount;
    property HiddenColumns[AColumn: Integer]: Boolean read GetHiddenColumns write SetHiddenColumns;
    property HiddenColumnWidths[AColumn: Integer]: Double read GetHiddenColumnWidths;
    property HiddenRowCount: Integer read GetHiddenRowCount;
    property HiddenRowHeights[ARow: Integer]: Double read GetHiddenRowHeights;
    property HiddenRows[ARow: Integer]: Boolean read GetHiddenRows write SetHiddenRows;
    property Hints[AColumn, ARow: Integer]: string read GetHints write SetHints;
    property HorizontalScrollBar: TScrollBar read GetHorizontalScrollBar;
    property HorizontalScrollBarVisible: Boolean read GetHorizontalScrollBarVisible write SetHorizontalScrollBarVisible default True;
    property Ints[AColumn, ARow: Integer]: Int64 read GetInts write SetInts;
    property Layouts[AColumn, ARow: Integer]: TTMSFNCDataGridCellLayout read GetLayouts;
    property LeftColumn: Integer read GetLeftColumn write SetLeftColumn;
    property ManagedObjects[AColumn, ARow: Integer]: TObject read GetManagedObjects write SetManagedObjects;
    property MergedColumns[AColumn: Integer]: Boolean read GetMergedColumns write SetMergedColumns;
    property MergedRows[ARow: Integer]: Boolean read GetMergedRows write SetMergedRows;
    property Objects[AColumn, ARow: Integer]: TObject read GetObjects write SetObjects;
    property OnAfterCreateFilterEditor: TTMSFNCDataGridAfterCreateFilterEditorEvent read GetOnAfterCreateFilterEditor write SetOnAfterCreateFilterEditor;
    property OnAfterApplyFilter: TTMSFNCDataGridAfterApplyFilterEvent read GetOnAfterApplyFilter write SetOnAfterApplyFilter;
    property OnAfterCloseInplaceEditor: TTMSFNCDataGridAfterCloseInplaceEditorEvent read GetOnAfterCloseInplaceEditor write SetOnAfterCloseInplaceEditor;
    property OnAfterCopyToClipboard: TTMSFNCDataGridAfterClipboardEvent read GetOnAfterCopyToClipboard write SetOnAfterCopyToClipboard;
    property OnAfterCutToClipboard: TTMSFNCDataGridAfterClipboardEvent read GetOnAfterCutToClipboard write SetOnAfterCutToClipboard;
    property OnAfterDrawCell: TTMSFNCDataGridAfterDrawCellEvent read GetOnAfterDrawCell write SetOnAfterDrawCell;
    property OnAfterDrawEmptySpace: TTMSFNCDataGridAfterDrawEmptySpaceEvent read GetOnAfterDrawEmptySpace write SetOnAfterDrawEmptySpace;
    property OnAfterDrawGrid: TTMSFNCDataGridAfterDrawGridEvent read GetOnAfterDrawGrid write SetOnAfterDrawGrid;
    property OnAfterLookup: TTMSFNCDataGridAfterLookupEvent read GetOnAfterLookup write SetOnAfterLookup;
    property OnAfterOpenInplaceEditor: TTMSFNCDataGridAfterOpenInplaceEditorEvent read GetOnAfterOpenInplaceEditor write SetOnAfterOpenInplaceEditor;
    property OnAfterPasteFromClipboard: TTMSFNCDataGridAfterClipboardEvent read GetOnAfterPasteFromClipboard write SetOnAfterPasteFromClipboard;
    property OnAfterShowFilter: TTMSFNCDataGridAfterShowFilterEvent read GetOnAfterShowFilter write SetOnAfterShowFilter;
    property OnAfterShowFindDialog: TTMSFNCDataGridAfterShowFindDialogEvent read GetOnAfterShowFindDialog write SetOnAfterShowFindDialog;
    property OnAfterShowReplaceDialog: TTMSFNCDataGridAfterShowReplaceDialogEvent read GetOnAfterShowReplaceDialog write SetOnAfterShowReplaceDialog;
    property OnAfterSortColumn: TTMSFNCDataGridAfterSortColumnEvent read GetOnAfterSortColumn write SetOnAfterSortColumn;
    property OnAppendColumn: TTMSFNCDataGridAppendColumnEvent read GetOnAppendColumn write SetOnAppendColumn;
    property OnAppendRow: TTMSFNCDataGridAppendRowEvent read GetOnAppendRow write SetOnAppendRow;
    property OnBeforeCreateFilterEditor: TTMSFNCDataGridBeforeCreateFilterEditorEvent read GetOnBeforeCreateFilterEditor write SetOnBeforeCreateFilterEditor;
    property OnBeforeApplyFilter: TTMSFNCDataGridBeforeApplyFilterEvent read GetOnBeforeApplyFilter write SetOnBeforeApplyFilter;
    property OnBeforeCloseInplaceEditor: TTMSFNCDataGridBeforeCloseInplaceEditorEvent read GetOnBeforeCloseInplaceEditor write SetOnBeforeCloseInplaceEditor;
    property OnBeforeCopyToClipboard: TTMSFNCDataGridBeforeClipboardEvent read GetOnBeforeCopyToClipboard write SetOnBeforeCopyToClipboard;
    property OnBeforeCutToClipboard: TTMSFNCDataGridBeforeClipboardEvent read GetOnBeforeCutToClipboard write SetOnBeforeCutToClipboard;
    property OnBeforeDrawCell: TTMSFNCDataGridBeforeDrawCellEvent read GetOnBeforeDrawCell write SetOnBeforeDrawCell;
    property OnBeforeDrawEmptySpace: TTMSFNCDataGridBeforeDrawEmptySpaceEvent read GetOnBeforeDrawEmptySpace write SetOnBeforeDrawEmptySpace;
    property OnBeforeOpenInplaceEditor: TTMSFNCDataGridBeforeOpenInplaceEditorEvent read GetOnBeforeOpenInplaceEditor write SetOnBeforeOpenInplaceEditor;
    property OnBeforeLookup: TTMSFNCDataGridBeforeLookupEvent read GetOnBeforeLookup write SetOnBeforeLookup;
    property OnBeforePasteFromClipboard: TTMSFNCDataGridBeforeClipboardEvent read GetOnBeforePasteFromClipboard write SetOnBeforePasteFromClipboard;
    property OnBeforeShowFilter: TTMSFNCDataGridBeforeShowFilterEvent read GetOnBeforeShowFilter write SetOnBeforeShowFilter;
    property OnBeforeShowFindDialog: TTMSFNCDataGridBeforeShowFindDialogEvent read GetOnBeforeShowFindDialog write SetOnBeforeShowFindDialog;
    property OnBeforeShowReplaceDialog: TTMSFNCDataGridBeforeShowReplaceDialogEvent read GetOnBeforeShowReplaceDialog write SetOnBeforeShowReplaceDialog;
    property OnBeforeSortColumn: TTMSFNCDataGridBeforeSortColumnEvent read GetOnBeforeSortColumn write SetOnBeforeSortColumn;
    property OnCanAppendColumn: TTMSFNCDataGridCanAppendColumnEvent read GetOnCanAppendColumn write SetOnCanAppendColumn;
    property OnCanAppendRow: TTMSFNCDataGridCanAppendRowEvent read GetOnCanAppendRow write SetOnCanAppendRow;
    property OnCanDeleteColumn: TTMSFNCDataGridCanDeleteColumnEvent read GetOnCanDeleteColumn write SetOnCanDeleteColumn;
    property OnCanDeleteRow: TTMSFNCDataGridCanDeleteRowEvent read GetOnCanDeleteRow write SetOnCanDeleteRow;
    property OnCanDragColumn: TTMSFNCDataGridCanDragColumnEvent read GetOnCanDragColumn write SetOnCanDragColumn;
    property OnCanDragRow: TTMSFNCDataGridCanDragRowEvent read GetOnCanDragRow write SetOnCanDragRow;
    property OnCanDropColumn: TTMSFNCDataGridCanDropColumnEvent read GetOnCanDropColumn write SetOnCanDropColumn;
    property OnCanDropRow: TTMSFNCDataGridCanDropRowEvent read GetOnCanDropRow write SetOnCanDropRow;
    property OnCanEditCell: TTMSFNCDataGridCanEditCellEvent read GetOnCanEditCell write SetOnCanEditCell;
    property OnCanInsertColumn: TTMSFNCDataGridCanInsertColumnEvent read GetOnCanInsertColumn write SetOnCanInsertColumn;
    property OnCanInsertRow: TTMSFNCDataGridCanInsertRowEvent read GetOnCanInsertRow write SetOnCanInsertRow;
    property OnCanSizeColumn: TTMSFNCDataGridCanSizeColumnEvent read GetOnCanSizeColumn write SetOnCanSizeColumn;
    property OnCanSizeRow: TTMSFNCDataGridCanSizeRowEvent read GetOnCanSizeRow write SetOnCanSizeRow;
    property OnCellAnchorClick: TTMSFNCDataGridCellAnchorClickEvent read GetOnCellAnchorClick write SetOnCellAnchorClick;
    property OnCellButtonClick: TTMSFNCDataGridCellClickEvent read GetOnCellButtonClick write SetOnCellButtonClick;
    property OnCellCheckBoxChange: TTMSFNCDataGridCellClickEvent read GetOnCellCheckBoxChange write SetOnCellCheckBoxChange;
    property OnCellClick: TTMSFNCDataGridCellClickEvent read GetOnCellClick write SetOnCellClick;
    property OnCellDblClick: TTMSFNCDataGridCellClickEvent read GetOnCellDblClick write SetOnCellDblClick;
    property OnCellEditGetData: TTMSFNCDataGridCellEditGetDataEvent read GetOnCellEditGetData write SetOnCellEditGetData;
    property OnCellEditSetData: TTMSFNCDataGridCellEditSetDataEvent read GetOnCellEditSetData write SetOnCellEditSetData;
    property OnCellEditValidateData: TTMSFNCDataGridCellEditValidateDataEvent read GetOnCellEditValidateData write SetOnCellEditValidateData;
    property OnCellBeforeShowPopupMenu: TTMSFNCDataGridCellBeforeShowPopupMenuEvent read GetOnCellBeforeShowPopupMenu write SetOnCellBeforeShowPopupMenu;
    property OnCellBeforeNodeExpand: TTMSFNCDataGridCellBeforeNodeExpandEvent read GetOnCellBeforeNodeExpand write SetOnCellBeforeNodeExpand;
    property OnCellAfterNodeExpand: TTMSFNCDataGridCellAfterNodeExpandEvent read GetOnCellAfterNodeExpand write SetOnCellAfterNodeExpand;
    property OnCellBeforeNodeCollapse: TTMSFNCDataGridCellBeforeNodeCollapseEvent read GetOnCellBeforeNodeCollapse write SetOnCellBeforeNodeCollapse;
    property OnCellAfterNodeCollapse: TTMSFNCDataGridCellAfterNodeCollapseEvent read GetOnCellAfterNodeCollapse write SetOnCellAfterNodeCollapse;
    property OnCellRadioButtonChange: TTMSFNCDataGridCellClickEvent read GetOnCellRadioButtonChange write SetOnCellRadioButtonChange;
    property OnCellRightClick: TTMSFNCDataGridCellClickEvent read GetOnCellRightClick write SetOnCellRightClick;
    property OnCustomColumnCalculation: TTMSFNCDataGridDataCustomColumnCalculationEvent read GetOnCustomColumnCalculation write SetOnCustomColumnCalculation;
    property OnCustomRowCalculation: TTMSFNCDataGridDataCustomRowCalculationEvent read GetOnCustomRowCalculation write SetOnCustomRowCalculation;
    property OnColumnDropped: TTMSFNCDataGridColumnDroppedEvent read GetOnColumnDropped write SetOnColumnDropped;
    property OnColumnSized: TTMSFNCDataGridColumnSizedEvent read GetOnColumnSized write SetOnColumnSized;
    property OnCustomCompare: TTMSFNCDataGridDataCustomCompareEvent read GetOnCustomCompare write SetOnCustomCompare;
    property OnCreateCustomFilterEditor: TTMSFNCDataGridCreateCustomFilterEditorEvent read GetOnCreateCustomFilterEditor write SetOnCreateCustomFilterEditor;
    property OnDeleteColumn: TTMSFNCDataGridDeleteColumnEvent read GetOnDeleteColumn write SetOnDeleteColumn;
    property OnDeleteRow: TTMSFNCDataGridDeleteRowEvent read GetOnDeleteRow write SetOnDeleteRow;
    property OnGetColumnCalculationOptions: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent read GetOnGetColumnCalculationOptions write SetOnGetColumnCalculationOptions;
    property OnGetRowCalculationOptions: TTMSFNCDataGridDataGetRowCalculationOptionsEvent read GetOnGetRowCalculationOptions write SetOnGetRowCalculationOptions;
    property OnGetCellClass: TTMSFNCDataGridGetCellClassEvent read GetOnGetCellClass write SetOnGetCellClass;
    property OnGetCellData: TTMSFNCDataGridDataGetCellDataEvent read GetOnGetCellData write SetOnGetCellData;
    property OnGetCellDataProperties: TTMSFNCDataGridDataGetCellDataPropertiesEvent read GetOnGetCellDataProperties write SetOnGetCellDataProperties;
    property OnGetCellDisplayValue: TTMSFNCDataGridDataGetCellDisplayValueEvent read GetOnGetCellDisplayValue write SetOnGetCellDisplayValue;
    property OnGetCellFormatting: TTMSFNCDataGridDataGetCellFormattingEvent read GetOnGetCellFormatting write SetOnGetCellFormatting;
    property OnGetCellLayout: TTMSFNCDataGridGetCellLayoutEvent read GetOnGetCellLayout write SetOnGetCellLayout;
    property OnGetCellHint: TTMSFNCDataGridGetCellHintEvent read GetOnGetCellHint write SetOnGetCellHint;
    property OnGetCellProperties: TTMSFNCDataGridGetCellPropertiesEvent read GetOnGetCellProperties write SetOnGetCellProperties;
    property OnGetCellLayoutHasPriority: TTMSFNCDataGridGetCellLayoutHasPriorityEvent read GetOnGetCellLayoutHasPriority write SetOnGetCellLayoutHasPriority;
    property OnGetCustomGroup: TTMSFNCDataGridDataGetCustomGroupEvent read GetOnGetCustomGroup write SetOnGetCustomGroup;
    property OnGetGroupOptions: TTMSFNCDataGridDataGetGroupOptionsEvent read GetOnGetGroupOptions write SetOnGetGroupOptions;
    property OnGetInplaceEditorClass: TTMSFNCDataGridGetInplaceEditorClassEvent read GetOnGetInplaceEditorClass write SetOnGetInplaceEditorClass;
    property OnGetInplaceEditorProperties: TTMSFNCDataGridGetInplaceEditorPropertiesEvent read GetOnGetInplaceEditorProperties write SetOnGetInplaceEditorProperties;
    property OnGetInplaceEditorRect: TTMSFNCDataGridGetInplaceEditorRectEvent read GetOnGetInplaceEditorRect write SetOnGetInplaceEditorRect;
    property OnGetInplaceEditorTarget: TTMSFNCDataGridGetInplaceEditorTargetEvent read GetOnGetInplaceEditorTarget write SetOnGetInplaceEditorTarget;
    property OnGetInplaceEditorType: TTMSFNCDataGridGetInplaceEditorTypeEvent read GetOnGetInplaceEditorType write SetOnGetInplaceEditorType;
    property OnGetSortOptions: TTMSFNCDataGridDataGetSortOptionsEvent read GetOnGetSortOptions write SetOnGetSortOptions;
    property OnHScroll: TTMSFNCDataGridScrollEvent read GetOnHScroll write SetOnHScroll;
    property OnInsertColumn: TTMSFNCDataGridInsertColumnEvent read GetOnInsertColumn write SetOnInsertColumn;
    property OnInsertRow: TTMSFNCDataGridInsertRowEvent read GetOnInsertRow write SetOnInsertRow;
    property OnLoadCellData: TTMSFNCDataGridDataCellDataEvent read GetOnLoadCellData write SetOnLoadCellData;
    property OnLogMessage: TTMSFNCDataGridLogMessageEvent read GetOnLogMessage write SetOnLogMessage;
    property OnNeedFilterDropDownData: TTMSFNCDataGridNeedFilterDropDownData read GetOnNeedFilterDropDownData write SetOnNeedFilterDropDownData;
    property OnRowDropped: TTMSFNCDataGridRowDroppedEvent read GetOnRowDropped write SetOnRowDropped;
    property OnRowCollapse: TTMSFNCDataGridDataRowCollapseEvent read GetOnRowCollapse write SetOnRowCollapse;
    property OnRowExpand: TTMSFNCDataGridDataRowExpandEvent read GetOnRowExpand write SetOnRowExpand;
    property OnRowSized: TTMSFNCDataGridRowSizedEvent read GetOnRowSized write SetOnRowSized;
    property OnSaveCellData: TTMSFNCDataGridDataCellDataEvent read GetOnSaveCellData write SetOnSaveCellData;
    property OnSelectCell: TTMSFNCDataGridSelectCellEvent read GetOnSelectCell write SetOnSelectCell;
    property OnSelectingCell: TTMSFNCDataGridSelectingCellEvent read GetOnSelectingCell write SetOnSelectingCell;
    property OnSizeColumn: TTMSFNCDataGridSizeColumnEvent read GetOnSizeColumn write SetOnSizeColumn;
    property OnSizeRow: TTMSFNCDataGridSizeRowEvent read GetOnSizeRow write SetOnSizeRow;
    property OnVScroll: TTMSFNCDataGridScrollEvent read GetOnVScroll write SetOnVScroll;
    property Options: TTMSFNCDataGridOptions read GetOptions write SetOptions;
    property PageIndex: Integer read GetPageIndex write SetPageIndex;
    property PageCount: Integer read GetPageCount;
    property ReadOnlys[AColumn, ARow: Integer]: Boolean read GetReadOnlys write SetReadOnlys;
    property RightColumn: Integer read GetRightColumn;
    property RowCount: Integer read GetRowCount write SetRowCount default 24;
    property RowHeights[ARow: Integer]: Double read GetRowHeights write SetRowHeights;
    property RowNodeChildCount[ARow: Integer]: Integer read GetRowNodeChildCount write SetRowNodeChildCount;
    property RowNodeLevel[ARow: Integer]: Integer read GetRowNodeLevel write SetRowNodeLevel;
    property RowNodeState[ARow: Integer]: TTMSFNCDataGridRowNodeState read GetRowNodeState write SetRowNodeState;
    property RowOrders[ARow: Integer]: Integer read GetRowOrder;
    property RowPositions[ARow: Integer]: Double read GetRowPos;
    property RowReverseOrders[ARow: Integer]: Integer read GetRowReverseOrder;
    property RowText[ARow: Integer]: string read GetRowText write SetRowText;
    property RowTypes[ARow: Integer]: TTMSFNCDataGridRowType read GetRowTypes write SetRowTypes;
    property ScrollMode: TTMSFNCDataGridScrollMode read GetScrollMode write SetScrollMode default gsmPixelScrolling;
    property ScrollUpdate: TTMSFNCDataGridScrollUpdate read GetScrollUpdate write SetScrollUpdate default gsuContinuous;
    property Designer: Boolean read FDesigner write SetDesigner default True;
    property SelectedCellCount: Integer read GetSelectedCellCount;
    property SelectedCells[AColumn, ARow: Integer]: Boolean read GetSelectedCells write SetSelectedCells;
    property SelectedColumnCount: Integer read GetSelectedColumnCount;
    property SelectedColumns[AColumn: Integer]: Boolean read GetSelectedColumns write SetSelectedColumns;
    property SelectedRowCount: Integer read GetSelectedRowCount;
    property SelectedRows[ARow: Integer]: Boolean read GetSelectedRows write SetSelectedRows;
    property Selection: TTMSFNCDataGridCellCoordRange read GetSelection write SetSelection;
    property SortIndexDictionary: TTMSFNCDataGridSortIndexDictionary read GetSortIndexDictionary;
    property StartColumn: Integer read GetStartColumn;
    property StartRow: Integer read GetStartRow;
    property States[AColumn, ARow: Integer]: TTMSFNCDataGridCellState read GetStates;
    property StopColumn: Integer read GetStopColumn;
    property StopRow: Integer read GetStopRow;
    property StoredCells[AColumn, ARow: Integer]: TTMSFNCDataGridCellValue read GetStoredCells;
    property StretchScrollBars: Boolean read GetStretchScrollBars write SetStretchScrollBars default False;
    property Strings[AColumn, ARow: Integer]: string read GetStrings write SetStrings;
    property StrippedCells[AColumn, ARow: Integer]: string read GetStrippedCells;
    property TopRow: Integer read GetTopRow write SetTopRow;
    property TotalColumnWidth: Double read GetTotalColumnWidth;
    property TotalRowHeight: Double read GetTotalRowHeight;
    property Version: string read GetVersion;
    property VerticalScrollBar: TScrollBar read GetVerticalScrollBar;
    property VerticalScrollBarVisible: Boolean read GetVerticalScrollBarVisible write SetVerticalScrollBarVisible default True;
    property VisibleColumnCount: Integer read GetVisibleColumnCount;
    property VisibleRowCount: Integer read GetVisibleRowCount;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;

    function AddGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer; overload;
    function AddGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer; overload;
    function BaseCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function CanDeleteColumn(AColumn: Integer): Boolean;
    function CanDeleteRow(ARow: Integer): Boolean;
    function CancelEditing: Boolean;
    function CanInsertColumn(AColumn: Integer): Boolean;
    function CanInsertRow(ARow: Integer): Boolean;
    function CanMergeCells(AColumn, ARow, AColumnCount, ARowCount: Integer): Boolean;
    function CanMoveColumn(AFromColumn, AToColumn: Integer): Boolean;
    function CanMoveRow(AFromRow, AToRow: Integer): Boolean;
    function CanSwapColumn(AFromColumn, AToColumn: Integer): Boolean;
    function CanSwapRow(AFromRow, AToRow: Integer): Boolean;
    function ColumnByName(AName: string): TTMSFNCDataGridColumn;
    function ColumnByHeader(AHeader: string): TTMSFNCDataGridColumn;
    function ColumnIndexByName(AName: string): Integer;
    function ColumnIndexByHeader(AHeader: string): Integer;
    function ColumnAverage(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnCustomCalculation(AColumn: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnDistinct(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnMax(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnMin(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnSpan(ACell: TTMSFNCDataGridCellCoord): Integer;
    function ColumnStandardDeviation(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnSum(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function DisplayToRealCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function Find(AText: string): TTMSFNCDataGridCellCoord; overload;
    function Find(AText: string; AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord; overload;
    function Find(AText: string; AParameters: TTMSFNCDataGridDataFindParameters; AStartCell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord; overload;
    function FindAll(AText: string; AParameters: TTMSFNCDataGridDataFindParameters; var AResults: Integer): TTMSFNCDataGridCellCoord;
    function FindFirst(AText: string; AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
    function FindNext: TTMSFNCDataGridCellCoord;
    function GetCellKind(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellKind;
    function GetCellMergeType(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridDataCellMergeType;
    function GetCellObjectForCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
    function GetCellSize(ACell: TTMSFNCDataGridCellCoord): TSizeF;
    function GetHMax: Single;
    function GetHScrollValue: Single;
    function GetHViewPortSize: Single;
    function GetMaxLevel: Integer;
    function GetParentRow(ARow: Integer): Integer;
    function GetRootRow(ARow: Integer): Integer;
    function GetTotalFixedBottomRowHeight: Double;
    function GetTotalFixedColumnWidth: Double;
    function GetTotalFixedRightColumnWidth: Double;
    function GetTotalFixedRowHeight: Double;
    function GetVMax: Single;
    function GetVScrollValue: Single;
    function GetVViewPortSize: Single;
    function HasRowData(ARow: Integer): Boolean;
    function IsCellBase(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellDisabled(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellDisplayed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFixed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFixedRoot(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreeze(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreezeFixed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreezeRoot(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellHidden(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellMerged(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellSelected(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellValid(ACell: TTMSFNCDataGridCellCoord): Boolean; overload;
    function IsCellValid(AColumn, ARow: Integer): Boolean; overload;
    function IsCellVisible(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsColumnMerged(AColumn: Integer): Boolean;
    function IsColumnValid(AColumn: Integer): Boolean;
    function IsEditing: Boolean;
    function IsFixedCellSelected(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsGrouped: Boolean;
    function IsRowBand(ARow: Integer): Boolean;
    function IsRowFiltered(ARow: Integer): Boolean;
    function IsRowHidden(ARow: Integer): Boolean;
    function IsRowMerged(ARow: Integer): Boolean;
    function IsRowNode(ARow: Integer): Boolean;
    function IsRowNormal(ARow: Integer): Boolean;
    function IsRowSummary(ARow: Integer): Boolean;
    function IsRowFilter(ARow: Integer): Boolean;
    function IsRowValid(ARow: Integer): Boolean;
    function MatchFilter(ARow: Integer): Boolean;
    function NextSelectableColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextSelectableRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousSelectableColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousSelectableRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextVisibleColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextVisibleRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousVisibleColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousVisibleRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function RealToDisplayCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function Replace(AOriginalValue, ANewValue: string): Integer; overload;
    function Replace(AOriginalValue, ANewValue: string; AParameters: TTMSFNCDataGridDataFindParameters): Integer; overload;
    function RowAverage(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowCustomCalculation(ARow: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowDistinct(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowMax(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowMin(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowSpan(ACell: TTMSFNCDataGridCellCoord): Integer;
    function RowStandardDeviation(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowSum(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function SetGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer; overload;
    function SetGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer; overload;
    function StopEditing: Boolean;
    function TypeOfValue(AValue: TTMSFNCDataGridCellValue): TTypeKind;
    function ValueIsEmpty(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueIsFloat(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueIsInteger(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueIsString(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueToBoolean(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueToFloat(AValue: TTMSFNCDataGridCellValue): Double;
    function ValueToInt64(AValue: TTMSFNCDataGridCellValue): Int64;
    function ValueToInteger(AValue: TTMSFNCDataGridCellValue): Integer;
    function ValueToString(AValue: TTMSFNCDataGridCellValue): string;
    function XYToCell(X, Y: Double): TTMSFNCDataGridCellCoord;
    function XYToRow(X, Y: Double): Integer;
    function XYToColumn(X, Y: Double): Integer;
    function XYToRenderer(X, Y: Double): TTMSFNCDataGridRenderer;
    procedure AddBitmap(ACell: TTMSFNCDataGridCellCoord; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmap(ACell: TTMSFNCDataGridCellCoord; AName: string); overload;
    procedure AddBitmap(AColumn, ARow: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmap(AColumn, ARow: Integer; AName: string); overload;
    procedure AddBitmapColumn(AColumn: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmapColumn(AColumn: Integer; AName: string); overload;
    procedure AddButton(ACell: TTMSFNCDataGridCellCoord; AText: string = ''); overload;
    procedure AddButton(AColumn, ARow: Integer; AText: string = ''); overload;
    procedure AddButtonColumn(AColumn: Integer; AText: string = '');
    procedure AddCheckBox(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False); overload;
    procedure AddCheckBox(AColumn, ARow: Integer; AValue: Boolean = False); overload;
    procedure AddCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
    procedure AddDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure AddDataCheckBox(AColumn, ARow: Integer); overload;
    procedure AddDataCheckBoxColumn(AColumn: Integer);
    procedure AddColumn;
    procedure AddNodeRow(ARow: Integer; AChildCount: Integer; ALevel: Integer = 0; AMerge: Boolean = True);
    procedure AddProgressBar(ACell: TTMSFNCDataGridCellCoord; AValue: Integer = 0); overload;
    procedure AddProgressBar(AColumn, ARow: Integer; AValue: Integer = 0); overload;
    procedure AddProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
    procedure AddDataProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure AddDataProgressBar(AColumn, ARow: Integer); overload;
    procedure AddDataProgressBarColumn(AColumn: Integer);
    procedure AddRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure AddDataRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddDataRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure AddRow;
    procedure ApplyFilter;
    procedure Assign(Source: TPersistent); override;
    procedure AutoNumberColumn(AColumn: Integer; AStartRow: Integer = 0; AStartValue: Integer = 0);
    procedure AutoNumberRow(ARow: Integer; AStartColumn: Integer = 0; AStartValue: Integer = 0);
    procedure AutoSizeColumn(AColumn: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeColumns(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeGrid(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APaddingColumn: Integer = 5; const APaddingRow: Integer = 5);
    procedure AutoSizeRow(ARow: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeRows(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure ClearCells(ACellRange: TTMSFNCDataGridCellCoordRange);
    procedure ClearFilter;
    procedure ClearColumns;
    procedure ClearNodeRow(ARow: Integer);
    procedure ClearSelection;
    procedure ColumnCalculation(AColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure ColumnCalculation(AColumns: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure RowCalculation(ARow: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil); overload;
    procedure RowCalculation(ARows: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil); overload;
    procedure CollapseAllNodes;
    procedure CollapseNode(ARow: Integer);
    procedure CopyToClipboard(ASelectedCells: Boolean = True);
    procedure CutToClipboard(ASelectedCells: Boolean = True);
    procedure DeleteColumn(AColumn: Integer);
    procedure DeleteColumns(AColumn, ACount: Integer);
    procedure DeleteRow(ARow: Integer);
    procedure DeleteRows(ARow, ACount: Integer);
    procedure Draw(AGraphics: TTMSFNCGraphics; ARect: TRectF); override;
    procedure EditCell(ACell: TTMSFNCDataGridCellCoord; ACellStart: TTMSFNCDataGridEditCellStart = gcsNone; AInsertKey: Boolean = False; AValue: string = ''; AKey: Word = 0); virtual;
    procedure ExpandAllNodes;
    procedure ExpandNode(ARow: Integer);
    procedure GoToCell(ACell: TTMSFNCDataGridCellCoord; ASelect: Boolean = True);
    procedure Group(AColumn: Integer); overload;
    procedure Group(AColumns: TArray<Integer>; ASortColumns: TArray<Integer> = nil; ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback = nil; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); overload;
    procedure Group(AGroupInfo: TTMSFNCDataGridDataGroupInfo; ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback = nil; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); overload;
    procedure GroupAverage(AColumn: Integer); overload;
    procedure GroupAverage(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCalculation(AColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCalculation(AColumns: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCount(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCount(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCustomCalculation(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCustomCalculation(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupDistinct(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupDistinct(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMax(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMax(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMin(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMin(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection); overload;
    procedure GroupSort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure GroupStandardDeviation(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupStandardDeviation(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSum(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSum(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure HideColumn(AColumn: Integer);
    procedure HideRow(ARow: Integer);
    procedure HilightCells(AHilightText: string; AOptions: TTMSFNCDataGridHilightOptions);
    procedure InitSample;
    procedure InsertColumn(AColumn: Integer);
    procedure InsertColumns(AColumn, ACount: Integer);
    procedure InsertRow(ARow: Integer);
    procedure InsertRows(ARow, ACount: Integer); virtual;
    procedure LinearFill(ADoFixed: Boolean = False);
    procedure LoadFromJSONData(AFileName: string; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); overload; virtual;
    procedure LoadFromJSONStreamData(AStream: TStream; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); overload; virtual;
    procedure LoadFromJSONData(AFileName: string; AEncoding: TEncoding; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); overload; virtual;
    procedure LoadFromJSONStreamData(AStream: TStream; AEncoding: TEncoding; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); overload; virtual;
    procedure LoadFromJSONTextData(AText: string; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); virtual;
    procedure LoadFromCSVData(AFileName: string); overload; virtual;
    procedure LoadFromCSVData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromCSVStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromCSVStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromFileData(AFileName: string); overload; virtual;
    procedure LoadFromFileData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromFileStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromFileStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromXMLData(AFileName: string); overload; virtual;
    procedure LoadFromXMLData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromXMLStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromXMLStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadSampleData;
    procedure LogMessage(AMessage: string; AMessageType: TTMSFNCDataGridLogMessageType = glmInfo);
    procedure MarkCells(AHilightText: string; AOptions: TTMSFNCDataGridMarkOptions);
    procedure MergeCells(AColumn, ARow, AColumnCount, ARowCount: Integer);
    procedure MergeColumn(AColumn: Integer);
    procedure MergeColumns(AColumn, ACount: Integer);
    procedure MergeRange(ARange: TTMSFNCDataGridCellCoordRange);
    procedure MergeRow(ARow: Integer);
    procedure MergeRows(ARow, ACount: Integer);
    procedure MoveColumn(AFromColumn, AToColumn: Integer);
    procedure MoveRow(AFromRow, AToRow: Integer);
    procedure PasteFromClipboard;
    procedure RandomFill(ADoFixed: Boolean = False; ARandomNumber: Integer = 100);
    procedure RemoveBitmap(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveBitmap(AColumn, ARow: Integer); overload;
    procedure RemoveButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveButton(AColumn, ARow: Integer); overload;
    procedure RemoveCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveCheckBox(AColumn, ARow: Integer); overload;
    procedure RemoveDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveDataCheckBox(AColumn, ARow: Integer); overload;
    procedure RemoveFilter;
    procedure RemoveGrid(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveGrid(AColumn, ARow: Integer); overload;
    procedure RemoveFilterEditor(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveFilterEditor(AColumn, ARow: Integer); overload;
    procedure RemoveLayout(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveLayout(AColumn, ARow: Integer); overload;
    procedure RemoveNodeRow(ARow: Integer);
    procedure RemoveProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveProgressBar(AColumn, ARow: Integer); overload;
    procedure RemoveRadioButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveRadioButton(AColumn, ARow: Integer); overload;
    procedure RemoveDataRadioButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveDataRadioButton(AColumn, ARow: Integer); overload;
    procedure SaveToCSVData(AFileName: string); overload;
    procedure SaveToCSVData(AFileName: string; AEncoding: TEncoding); overload;
    procedure SaveToCSVStreamData(AStream: TStream); overload;
    procedure SaveToCSVStreamData(AStream: TStream; AEncoding: TEncoding); overload;
    procedure SaveToFileData(AFileName: string); overload; virtual;
    procedure SaveToFileData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure SaveToFileStreamData(AStream: TStream); overload;
    procedure SaveToFileStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure SaveToHTMLData(AFileName: string); overload;
    procedure SaveToHTMLData(AFileName: string; AEncoding: TEncoding); overload;
    procedure SaveToHTMLStreamData(AStream: TStream); overload;
    procedure SaveToHTMLStreamData(AStream: TStream; AEncoding: TEncoding); overload;
    procedure SaveToImageData(AFileName: string); overload;
    procedure SaveToImageStreamData(AStream: TStream); overload;
    procedure Scroll(AHorizontalPos, AVerticalPos: Double);
    procedure SelectAllCells;
    procedure SelectCell(ACell: TTMSFNCDataGridCellCoord);
    procedure SetBitmap(ACell: TTMSFNCDataGridCellCoord; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmap(ACell: TTMSFNCDataGridCellCoord; AName: string); overload;
    procedure SetBitmap(AColumn, ARow: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmap(AColumn, ARow: Integer; AName: string); overload;
    procedure SetBitmapColumn(AColumn: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmapColumn(AColumn: Integer; AName: string); overload;
    procedure SetButton(ACell: TTMSFNCDataGridCellCoord; AText: string = ''); overload;
    procedure SetButton(AColumn, ARow: Integer; AText: string = ''); overload;
    procedure SetButtonColumn(AColumn: Integer; AText: string = '');
    procedure SetCheckBox(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False); overload;
    procedure SetCheckBox(AColumn, ARow: Integer; AValue: Boolean = False); overload;
    procedure SetCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
    procedure SetDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure SetDataCheckBox(AColumn, ARow: Integer); overload;
    procedure SetDataCheckBoxColumn(AColumn: Integer);
    procedure SetNodeRow(ARow: Integer; AChildCount: Integer; ALevel: Integer = 0; AMerge: Boolean = True);
    procedure SetProgressBar(ACell: TTMSFNCDataGridCellCoord; AValue: Integer = 0); overload;
    procedure SetProgressBar(AColumn, ARow: Integer; AValue: Integer = 0); overload;
    procedure SetProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
    procedure SetDataProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure SetDataProgressBar(AColumn, ARow: Integer); overload;
    procedure SetDataProgressBarColumn(AColumn: Integer);
    procedure SetRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure SetDataRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetDataRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection); reintroduce; overload;
    procedure Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection; AFromRow, AToRow: Integer); reintroduce; overload;
    procedure Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; AFromRow, AToRow: Integer; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure SortIndexed;
    procedure SplitCell(ACell: TTMSFNCDataGridCellCoord);
    procedure StretchColumns(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewWidth: Double = -1);
    procedure StretchRows(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewHeight: Double = -1);
    procedure SwapCells(AFromCell, AToCell: TTMSFNCDataGridCellCoord);
    procedure SwapColumns(AFromColumn, AToColumn: Integer);
    procedure SwapRows(AFromRow, AToRow: Integer);
    procedure ToggleNode(ARow: Integer);
    procedure Ungroup;
    procedure UnhideAll;
    procedure UnhideAllColumns;
    procedure UnhideAllRows;
    procedure UnhideColumn(AColumn: Integer);
    procedure UnhideRow(ARow: Integer);
    procedure UnhilightCells(AOptions: TTMSFNCDataGridHilightOptions);
    procedure UnmarkCells(AOptions: TTMSFNCDataGridMarkOptions);
    procedure UpdateColumnCalculation(AColumn: Integer; AName: string);
    procedure UpdateColumnCalculations;
    procedure UpdateRowCalculation(ARow: Integer; AName: string);
    procedure UpdateRowCalculations;
    procedure UpdateCalculations;
  end;

  [ComponentPlatformsAttribute(TMSPlatformsWeb)]
  TTMSFNCDataGrid = class(TTMSFNCCustomDataGrid)
  protected
    procedure RegisterRuntimeClasses; override;
  public
    property Booleans;
    property BottomRow;
    property CellData;
    property Cells;
    property ColumnCalculations;
    property RowCalculations;
    property ColumnOrders;
    property ColumnPositions;
    property ColumnReverseOrders;
    property ColumnWidths;
    property Comments;
    property Controls;
    property Colors;
    property ControlTexts;
    property ControlAligns;
    property ControlPositions;
    property ControlWidths;
    property ControlHeights;
    property CustomHorizontalScrollBar;
    property CustomScrollBars;
    property CustomVerticalScrollBar;
    property EditingCell;
    property Filter;
    property FilteredRowCount;
    property Floats;
    property FocusedCell;
    property Grids;
    property FontColors;
    property FontNames;
    property FontSizes;
    property FontStyles;
    property TextAligns;
    property VerticalTextAligns;
    property TextAngles;
    property HasLayout;
    property HasRowOrder;
    property HiddenColumnCount;
    property HiddenColumns;
    property HiddenColumnWidths;
    property HiddenRowCount;
    property HiddenRowHeights;
    property HiddenRows;
    property Hints;
    property HorizontalScrollBar;
    property Ints;
    property Layouts;
    property LeftColumn;
    property ManagedObjects;
    property MergedColumns;
    property MergedRows;
    property Objects;
    property ReadOnlys;
    property RightColumn;
    property RowHeights;
    property RowNodeChildCount;
    property RowNodeLevel;
    property RowNodeState;
    property RowOrders;
    property RowPositions;
    property RowReverseOrders;
    property RowText;
    property RowTypes;
    property SelectedCellCount;
    property SelectedCells;
    property SelectedColumnCount;
    property SelectedColumns;
    property SelectedRowCount;
    property SelectedRows;
    property Selection;
    property SortIndexDictionary;
    property StartColumn;
    property StartRow;
    property States;
    property StopColumn;
    property StopRow;
    property StoredCells;
    property Strings;
    property StrippedCells;
    property TopRow;
    property TotalColumnWidth;
    property TotalRowHeight;
    property VerticalScrollBar;
    property VisibleColumnCount;
    property VisibleRowCount;
  published
    property Adapter;
    property BitmapContainer;
    property CellAppearance;
    property CellPopupMenu;
    property ColumnCount;
    property Columns;
    property DefaultColumnWidth;
    property DefaultRowHeight;
    property Designer;
    property DragAppearance;
    property Fill;
    property FilterAppearance;
    property FixedBottomRowCount;
    property FixedColumnCount;
    property FixedRightColumnCount;
    property FixedRowCount;
    //property Footer;
    property FreezeColumnCount;
    property FreezeRowCount;
    property GlobalFont;
    //property Header;
    property HorizontalScrollBarVisible;
    property OnAfterCreateFilterEditor;
    property OnAfterApplyFilter;
    property OnAfterCloseInplaceEditor;
    property OnAfterCopyToClipboard;
    property OnAfterCutToClipboard;
    property OnAfterDrawCell;
    property OnAfterDrawEmptySpace;
    property OnAfterDrawGrid;
    property OnAfterLookup;
    property OnAfterOpenInplaceEditor;
    property OnAfterPasteFromClipboard;
    property OnAfterShowFilter;
    property OnAfterShowFindDialog;
    property OnAfterShowReplaceDialog;
    property OnCellAfterNodeExpand;
    property OnCellAfterNodeCollapse;
    property OnAfterSortColumn;
    property OnAppendColumn;
    property OnAppendRow;
    property OnBeforeCreateFilterEditor;
    property OnBeforeApplyFilter;
    property OnBeforeCloseInplaceEditor;
    property OnBeforeCopyToClipboard;
    property OnBeforeCutToClipboard;
    property OnBeforeDrawCell;
    property OnBeforeDrawEmptySpace;
    property OnBeforeOpenInplaceEditor;
    property OnBeforePasteFromClipboard;
    property OnBeforeLookup;
    property OnBeforeShowFilter;
    property OnBeforeShowFindDialog;
    property OnBeforeShowReplaceDialog;
    property OnBeforeSortColumn;
    property OnCanAppendColumn;
    property OnCanAppendRow;
    property OnCanDeleteColumn;
    property OnCanDeleteRow;
    property OnCanDragColumn;
    property OnCanDragRow;
    property OnCanDropColumn;
    property OnCanDropRow;
    property OnCanEditCell;
    property OnCanInsertColumn;
    property OnCanInsertRow;
    property OnCanSizeColumn;
    property OnCanSizeRow;
    property OnCellAnchorClick;
    property OnCellButtonClick;
    property OnCellBeforeShowPopupMenu;
    property OnCellBeforeNodeExpand;
    property OnCellBeforeNodeCollapse;
    property OnCellCheckBoxChange;
    property OnCellClick;
    property OnCellDblClick;
    property OnCellEditGetData;
    property OnCellEditSetData;
    property OnCellEditValidateData;
    property OnCellRadioButtonChange;
    property OnCellRightClick;
    property OnCustomColumnCalculation;
    property OnCustomRowCalculation;
    property OnColumnDropped;
    property OnColumnSized;
    property OnCustomCompare;
    property OnCreateCustomFilterEditor;
    property OnDeleteColumn;
    property OnDeleteRow;
    property OnGetColumnCalculationOptions;
    property OnGetRowCalculationOptions;
    property OnGetCellClass;
    property OnGetCellData;
    property OnGetCellDataProperties;
    property OnGetCellDisplayValue;
    property OnGetCellFormatting;
    property OnGetCellHint;
    property OnGetCellLayout;
    property OnGetCellLayoutHasPriority;
    property OnGetCellProperties;
    property OnGetCustomGroup;
    property OnGetGroupOptions;
    property OnGetInplaceEditorClass;
    property OnGetInplaceEditorProperties;
    property OnGetInplaceEditorRect;
    property OnGetInplaceEditorTarget;
    property OnGetInplaceEditorType;
    property OnGetSortOptions;
    property OnHScroll;
    property OnInsertColumn;
    property OnInsertRow;
    property OnLoadCellData;
    property OnLogMessage;
    property OnNeedFilterDropDownData;
    property OnRowDropped;
    property OnRowSized;
    property OnRowCollapse;
    property OnRowExpand;
    property OnSaveCellData;
    property OnSelectCell;
    property OnSelectingCell;
    property OnSizeColumn;
    property OnSizeRow;
    property OnVScroll;
    property Options;
    property RowCount;
    property ScrollMode;
    property ScrollUpdate;
    property StretchScrollBars;
    property Stroke;
    property Version;
    property VerticalScrollBarVisible;
  end;

implementation

uses
  DateUtils, Variants, Math, WEBLib.Dialogs, WEBLib.TMSFNCImage, WEBLib.TMSFNCUtils, WEBLib.TMSFNCStyles
  {$IFDEF FMXLIB}
  ,FMX.Layouts
  {$ENDIF}
  ;

type
  TTMSFNCDataGridRendererOpen = class(TTMSFNCDataGridRenderer);

  {$IFNDEF WEBLIB}
  TTMSFNCDataGridDesignerOpenDialog = TOpenDialog;
  TTMSFNCDataGridDesignerControlOpen = class(TTMSFNCDataGridCellControl);
  TTMSFNCImageOpen = class(TTMSFNCImage);
  {$ENDIF}

{$IFDEF WEBLIB}
const
  TTMSFNCDATAGRIDCLOSE =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOm5vbmU7fS5jbHMtMntmaWxsOiNlY'+
  'TNmNDE7fS5jbHMtM3tjbGlwLXBhdGg6dXJsKCNjbGlwLXBhdGgpO30uY2xzLTR7ZmlsbDojZmFmYWZhO30uY2xzLTV7Y2xpcC1wYXRoOnVybCgjY2xpcC1wYXRoLTIpO30uY2xzLTZ7ZmlsbDp1cmwoI05ld19QYXR0ZXJuX1N3YXRjaF8zKTt9PC9zdHlsZT48Y2xpcFBhdGggaWQ9ImNsaXAtcGF0aCI+PHJlY3QgY2xhc3M9ImNscy0xIiB4'+
  'PSItNDUuNSIgeT0iLTQuNSIgd2lkdGg9IjM0IiBoZWlnaHQ9IjM0Ii8+PC9jbGlwUGF0aD48Y2xpcFBhdGggaWQ9ImNsaXAtcGF0aC0yIj48cmVjdCBjbGFzcz0iY2xzLTEiIHg9IjIuNSIgeT0iMi41IiB3aWR0aD0iMjciIGhlaWdodD0iMjciLz48L2NsaXBQYXRoPjxwYXR0ZXJuIGlkPSJOZXdfUGF0dGVybl9Td2F0Y2hfMyIgZGF0YS1'+
  'uYW1lPSJOZXcgUGF0dGVybiBTd2F0Y2ggMyIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4IiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIiB2aWV3Qm94PSIwIDAgNjggNjgiPjxyZWN0IGNsYXNzPSJjbHMtMSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0yIiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L3'+
  'BhdHRlcm4+PC9kZWZzPjx0aXRsZT5XaW5kb3ctQ2xvc2U8L3RpdGxlPjxnIGlkPSJJY29uIj48ZyBjbGFzcz0iY2xzLTMiPjxyZWN0IGNsYXNzPSJjbHMtMSIgeD0iLTU0IiB5PSItNDkiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD0iLTU0IiB5PSItNDkiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvP'+
  'jxyZWN0IGNsYXNzPSJjbHMtMSIgeD0iLTU0IiB5PSIxOSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy00IiB4PSItNTQiIHk9IjE5IiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L2c+PGcgY2xhc3M9ImNscy01Ij48cmVjdCBjbGFzcz0iY2xzLTEiIHg9IjE0IiB5PSItNDkiIHdpZHRoPSI2OCIgaGVpZ2h0'+
  'PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD0iMTQiIHk9Ii00OSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0xIiB4PSIxNCIgeT0iMTkiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD0iMTQiIHk9IjE5IiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48cmVjdCBjbGF'+
  'zcz0iY2xzLTEiIHg9Ii01NCIgeT0iLTQ5IiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48cmVjdCBjbGFzcz0iY2xzLTQiIHg9Ii01NCIgeT0iLTQ5IiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48cmVjdCBjbGFzcz0iY2xzLTEiIHg9Ii01NCIgeT0iMTkiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD'+
  '0iLTU0IiB5PSIxOSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PC9nPjxwYXRoIGNsYXNzPSJjbHMtNiIgZD0iTTMwLDMwSDJWMkgzMFpNMywyOUgyOVYzSDNaIi8+PHBvbHlnb24gY2xhc3M9ImNscy02IiBwb2ludHM9IjIyLjY1IDIzLjM1IDguNjUgOS4zNSA5LjM1IDguNjUgMjMuMzUgMjIuNjUgMjIuNjUgMjMuMzUiLz48cG9seWdvb'+
  'iBjbGFzcz0iY2xzLTYiIHBvaW50cz0iOS4zNSAyMy4zNSA4LjY1IDIyLjY1IDIyLjY1IDguNjUgMjMuMzUgOS4zNSA5LjM1IDIzLjM1Ii8+PC9nPjwvc3ZnPg';
{$ENDIF}

{ TTMSFNCCustomDataGrid }

procedure TTMSFNCCustomDataGrid.NextPage;
begin
  Root.NextPage;
end;

function TTMSFNCCustomDataGrid.NextSelectableColumn(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.NextSelectableColumn(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.NextSelectableRow(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.NextSelectableRow(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.NextVisibleColumn(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.NextVisibleColumn(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.NextVisibleRow(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.NextVisibleRow(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.Notification(AComponent: TComponent;
  Operation: TOperation);
begin
  inherited;
  if (Operation = opRemove) and not (csDestroying in ComponentState) then
  begin
    if (AComponent = CellPopupMenu) then
      CellPopupMenu := nil;

    if (AComponent = BitmapContainer) then
      BitmapContainer := nil;

    if (AComponent = Adapter) then
      Adapter := nil;
  end;
end;

function TTMSFNCCustomDataGrid.Find(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters;
  AStartCell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := Root.Find(AText, AParameters, AStartCell);
end;

function TTMSFNCCustomDataGrid.Find(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
begin
  Result := Root.Find(AText, AParameters);
end;

function TTMSFNCCustomDataGrid.Find(AText: string): TTMSFNCDataGridCellCoord;
begin
  Result := Root.Find(AText);
end;

function TTMSFNCCustomDataGrid.FindAll(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters;
  var AResults: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.FindAll(AText, AParameters, AResults);
end;

function TTMSFNCCustomDataGrid.FindFirst(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
begin
  Result := Root.FindFirst(AText, AParameters);
end;

function TTMSFNCCustomDataGrid.FindNext: TTMSFNCDataGridCellCoord;
begin
  Result := Root.FindNext;
end;

procedure TTMSFNCCustomDataGrid.FirstPage;
begin
  Root.FirstPage;
end;

procedure TTMSFNCCustomDataGrid.PasteFromClipboard;
begin
  Root.PasteFromClipboard;
end;

procedure TTMSFNCCustomDataGrid.PreviousPage;
begin
  Root.PreviousPage;
end;

function TTMSFNCCustomDataGrid.PreviousSelectableColumn(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.PreviousSelectableColumn(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.PreviousSelectableRow(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.PreviousSelectableRow(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.PreviousVisibleColumn(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.PreviousVisibleColumn(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.PreviousVisibleRow(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.PreviousVisibleRow(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.FooterChangedRebuild(Sender: TObject);
begin
  UpdateRoot;
end;

procedure TTMSFNCCustomDataGrid.HeaderChangedRebuild(Sender: TObject);
begin
  UpdateRoot;
end;

procedure TTMSFNCCustomDataGrid.HideColumn(AColumn: Integer);
begin
  Root.HideColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.HideRow(ARow: Integer);
begin
  Root.HideRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.HilightCells(AHilightText: string;
  AOptions: TTMSFNCDataGridHilightOptions);
begin
  Root.HilightCells(AHilightText, AOptions);
end;

{$IFDEF FMXLIB}
procedure TTMSFNCCustomDataGrid.DoAbsoluteChanged;
begin
  inherited;
  if Parent is TScaledLayout then
    UpdateRoot;
end;
{$ENDIF}

procedure TTMSFNCCustomDataGrid.DoEnter;
begin
  inherited;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleEnter
  else
    Root.HandleEnter;
end;

procedure TTMSFNCCustomDataGrid.DoExit;
begin
  inherited;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleExit
  else
    Root.HandleExit;
end;

procedure TTMSFNCCustomDataGrid.DoRootFocus(Sender: TObject);
begin
  if CanFocus then
    SetFocus;

  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.ClearFocus;

  if Assigned(FFocusedRenderer) and (FFocusedRenderer <> Sender) then
  begin
    FFocusedRenderer.ClearSelection;
    FFocusedRenderer.StopEditing;
  end;

  if Sender is TTMSFNCDataGridRenderer then
    FFocusedRenderer := (Sender as TTMSFNCDataGridRenderer);

  Root.Display;
end;

{$IFDEF SUPPORTABSTRACT}

function TTMSFNCCustomDataGrid.GetRecord(AIndex: Integer): TValue;
var
  i: ITMSFNCDataObject;
begin
  Result := TValue.Empty;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.GetRecord(AIndex);
end;

function TTMSFNCCustomDataGrid.GetRecordCount: Integer;
var
  i: ITMSFNCDataObject;
begin
  Result := 0;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.GetRecordCount;
end;

function TTMSFNCCustomDataGrid.GetRecordIndex(ARecord: TValue): Integer;
var
  i: ITMSFNCDataObject;
begin
  Result := -1;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.GetRecordIndex(ARecord);
end;

function TTMSFNCCustomDataGrid.GetRecordValue(ARecord: TValue;
  AField: string; AFieldType: TFieldType): ValueType;
var
  i: ITMSFNCDataObject;
begin
  Result := Null;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.GetRecordValue(ARecord, AField, AFieldType);
end;

function TTMSFNCCustomDataGrid.Clone: TObject;
var
  i: ITMSFNCDataObject;
begin
  Result := nil;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.Clone;
end;

procedure TTMSFNCCustomDataGrid.SetRecordValue(ARecordIndex: Integer;
  AField: string; AValue: ValueType);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.SetRecordValue(ARecordIndex, AField, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetNotification(
  ANotifyObject: ITMSFNCDataObjectNotification);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.SetNotification(ANotifyObject);
end;

procedure TTMSFNCCustomDataGrid.InitializeFieldDefs(AFieldDefs: TFieldDefs);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.InitializeFieldDefs(AFieldDefs);
end;

procedure TTMSFNCCustomDataGrid.Activate(AValue: Boolean);
var
  i: ITMSFNCDataObject;
begin
  if not (csDestroying in ComponentState) and Supports(Root, ITMSFNCDataObject, i) then
    i.Activate(AValue);
end;

procedure TTMSFNCCustomDataGrid.InsertRecord(AIndex: Integer; ARecord: TValue);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.InsertRecord(AIndex, ARecord);
end;

procedure TTMSFNCCustomDataGrid.AddRecord(ARecord: TValue);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.AddRecord(ARecord);
end;

procedure TTMSFNCCustomDataGrid.DeleteRecord(AIndex: Integer);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.DeleteRecord(AIndex);
end;

function TTMSFNCCustomDataGrid.CompareRecords(AField: string; ARecord1,
  ARecord2: TValue): Integer;
var
  i: ITMSFNCDataObject;
begin
  Result := 0;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.CompareRecords(AField, ARecord1, ARecord2);
end;

function TTMSFNCCustomDataGrid.CreateDefaultRecord: TValue;
var
  i: ITMSFNCDataObject;
begin
  Result := TValue.Empty;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.CreateDefaultRecord;
end;

{$ENDIF}

function TTMSFNCCustomDataGrid.DoRootIsFocused(Sender: TObject): Boolean;
begin
  Result := inherited;
  Result := Result and ((Assigned(FFocusedRenderer) and (FFocusedRenderer = Sender)) or (not Assigned(FFocusedRenderer) and (Root = Sender)));
end;

procedure TTMSFNCCustomDataGrid.DoRootUpdateCursor(Sender: TObject;
  ACursor: TCursor);
begin
  inherited;
  Cursor := ACursor;
end;

procedure TTMSFNCCustomDataGrid.ClearCells(
  ACellRange: TTMSFNCDataGridCellCoordRange);
begin
  Root.ClearCells(ACellRange);
end;

procedure TTMSFNCCustomDataGrid.ClearColumns;
begin
  Root.ClearColumns;
end;

procedure TTMSFNCCustomDataGrid.ClearFilter;
begin
  Root.ClearFilter;
end;

procedure TTMSFNCCustomDataGrid.ClearNodeRow(ARow: Integer);
begin
  Root.ClearNodeRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.ClearSelection;
begin
  Root.ClearSelection;
end;

procedure TTMSFNCCustomDataGrid.CollapseAllNodes;
begin
  Root.CollapseAllNodes;
end;

procedure TTMSFNCCustomDataGrid.CollapseNode(ARow: Integer);
begin
  Root.CollapseNode(ARow);
end;

function TTMSFNCCustomDataGrid.ColumnAverage(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnAverage(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnByHeader(
  AHeader: string): TTMSFNCDataGridColumn;
begin
  Result := Root.ColumnByHeader(AHeader);
end;

function TTMSFNCCustomDataGrid.ColumnByName(
  AName: string): TTMSFNCDataGridColumn;
begin
  Result := Root.ColumnByName(AName);
end;

procedure TTMSFNCCustomDataGrid.ColumnCalculation(AColumns: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.ColumnCalculation(AColumns, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.ColumnCalculation(AColumn: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.ColumnCalculation(AColumn, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

function TTMSFNCCustomDataGrid.RowAverage(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowAverage(ARow, AFromColumn, AToColumn);
end;

procedure TTMSFNCCustomDataGrid.RowCalculation(ARows: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback);
begin
  Root.RowCalculation(ARows, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.RowCalculation(ARow: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback);
begin
  Root.RowCalculation(ARow, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

function TTMSFNCCustomDataGrid.ColumnCustomCalculation(AColumn: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback; AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnCustomCalculation(AColumn, ACustomCalculationCallback, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnDistinct(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnDistinct(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnIndexByHeader(AHeader: string): Integer;
begin
  Result := Root.ColumnIndexByHeader(AHeader);
end;

function TTMSFNCCustomDataGrid.ColumnIndexByName(AName: string): Integer;
begin
  Result := Root.ColumnIndexByName(AName);
end;

function TTMSFNCCustomDataGrid.ColumnMax(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnMax(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnMin(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnMin(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnSpan(
  ACell: TTMSFNCDataGridCellCoord): Integer;
begin
  Result := Root.ColumnSpan(ACell);
end;

function TTMSFNCCustomDataGrid.ColumnStandardDeviation(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnStandardDeviation(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnSum(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnSum(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.RowCustomCalculation(ARow: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback; AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowCustomCalculation(ARow, ACustomCalculationCallback, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowDistinct(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowDistinct(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowMax(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowMax(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowMin(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowMin(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowSpan(
  ACell: TTMSFNCDataGridCellCoord): Integer;
begin
  Result := Root.RowSpan(ACell);
end;

function TTMSFNCCustomDataGrid.RowStandardDeviation(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowStandardDeviation(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowSum(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowSum(ARow, AFromColumn, AToColumn);
end;

procedure TTMSFNCCustomDataGrid.CopyToClipboard(ASelectedCells: Boolean);
begin
  Root.CopyToClipboard(ASelectedCells);
end;

procedure TTMSFNCCustomDataGrid.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCCustomDataGrid then
  begin
    FFooter.Assign((Source as TTMSFNCCustomDataGrid).Footer);
    FHeader.Assign((Source as TTMSFNCCustomDataGrid).Header);
    Root.Assign((Source as TTMSFNCCustomDataGrid).Root);
  end;
end;

procedure TTMSFNCCustomDataGrid.AutoNumberColumn(AColumn, AStartRow,
  AStartValue: Integer);
begin
  Root.AutoNumberColumn(AColumn, AStartRow, AStartValue);
end;

procedure TTMSFNCCustomDataGrid.AutoNumberRow(ARow, AStartColumn,
  AStartValue: Integer);
begin
  Root.AutoNumberRow(ARow, AStartColumn, AStartValue);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeColumn(AColumn: Integer;
  const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
begin
  Root.AutoSizeColumn(AColumn, AMode, APadding);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeColumns(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
begin
  Root.AutoSizeColumns(AMode, APadding);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeGrid(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APaddingColumn: Integer = 5; const APaddingRow: Integer = 5);
begin
  Root.AutoSizeGrid(AMode, APaddingColumn, APaddingRow);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeRow(ARow: Integer;
  const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
begin
  Root.AutoSizeRow(ARow, AMode, APadding);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeRows(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
begin
  Root.AutoSizeRows(AMode, APadding);
end;

function TTMSFNCCustomDataGrid.BaseCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := Root.BaseCell(ACell);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseDown(Button: TTMSFNCMouseButton; Shift: TShiftState;
  X, Y: Single);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
begin
  inherited;
  CaptureEx;
  m := DefaultMouseInfo;
  m.Shift := Shift;
  m.X := X;
  m.Y := Y;
  m.Button := Button;
  m.Kind := gmkDown;

  r := Root.XYToRenderer(X, Y);
  if not Assigned(r) then
    r := Root;

  FDownRenderer := r;

  if Assigned(r) then
    r.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseMove(Shift: TShiftState; X, Y: Single);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
begin
  inherited;
  m := DefaultMouseInfo;
  m.Shift := Shift;
  m.X := X;
  m.Y := Y;
  m.Kind := gmkMove;

  r := Root.XYToRenderer(X, Y);
  if not Assigned(r) then
    r := Root;

  if (FDownRenderer <> r) and Assigned(FDownRenderer) then
    TTMSFNCDataGridRendererOpen(FDownRenderer).ResetScrollBarCapture;

  if Assigned(r) then
    r.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseUp(Button: TTMSFNCMouseButton; Shift: TShiftState;
  X, Y: Single);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
begin
  inherited;
  ReleaseCaptureEx;
  m := DefaultMouseInfo;
  m.Shift := Shift;
  m.X := X;
  m.Y := Y;
  m.Button := Button;
  m.Kind := gmkUp;

  r := Root.XYToRenderer(X, Y);
  if not Assigned(r) then
    r := Root;

  if Assigned(FDownRenderer) and (FDownRenderer <> r) then
    FDownRenderer.HandleExit;

  FDownRenderer := nil;

  if Assigned(r) then
    r.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseWheel(Shift: TShiftState; WheelDelta: Integer;
  var Handled: Boolean);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
  c: TPointF;
begin
  inherited;
  if Handled or not Visible or not Assigned(Parent) then
    Exit;

  c := ScreenToLocalEx(TTMSFNCUtils.GetMousePos);
  m := DefaultMouseInfo;
  m.Kind := gmkWheel;
  m.Handled := Handled;
  m.WheelDelta := WheelDelta;
  m.Shift := Shift;
  m.X := c.X;
  m.Y := c.Y;

  r := Root.XYToRenderer(c.X, c.Y);
  if not Assigned(r) then
    r := Root;

  if Assigned(r) and not r.IsEditing then
  begin
    if not r.IsCellValid(r.FocusedCell) and not r.Options.Mouse.WheelScrollKeepSelection then
      r.FocusedCell := r.XYToCell(c.X, c.Y);

    r.Focus;
    r.HandleMouse(m);
    Handled := m.Handled;
  end;
end;

function TTMSFNCCustomDataGrid.ValueIsEmpty(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueIsEmpty(AValue);
end;

function TTMSFNCCustomDataGrid.ValueIsFloat(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueIsFloat(AValue);
end;

function TTMSFNCCustomDataGrid.ValueIsInteger(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueIsInteger(AValue);
end;

function TTMSFNCCustomDataGrid.ValueIsString(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueIsString(AValue);
end;

function TTMSFNCCustomDataGrid.ValueToBoolean(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueToBoolean(AValue, FormatSettings);
end;

function TTMSFNCCustomDataGrid.ValueToFloat(
  AValue: TTMSFNCDataGridCellValue): Double;
begin
  Result := Root.ValueToFloat(AValue, FormatSettings);
end;

function TTMSFNCCustomDataGrid.ValueToInt64(
  AValue: TTMSFNCDataGridCellValue): Int64;
begin
  Result := Root.ValueToInt64(AValue, FormatSettings);
end;

function TTMSFNCCustomDataGrid.ValueToInteger(
  AValue: TTMSFNCDataGridCellValue): Integer;
begin
  Result := Root.ValueToInt64(AValue, FormatSettings);
end;

function TTMSFNCCustomDataGrid.ValueToString(
  AValue: TTMSFNCDataGridCellValue): string;
begin
  Result := Root.ValueToString(AValue);
end;

function TTMSFNCCustomDataGrid.XYToCell(X, Y: Double): TTMSFNCDataGridCellCoord;
begin
  Result := Root.XYToCell(X, Y);
end;

function TTMSFNCCustomDataGrid.XYToColumn(X, Y: Double): Integer;
begin
  Result := Root.XYToColumn(X, Y);
end;

function TTMSFNCCustomDataGrid.XYToRenderer(X,
  Y: Double): TTMSFNCDataGridRenderer;
begin
  Result := Root.XYToRenderer(X, Y);
end;

function TTMSFNCCustomDataGrid.XYToRow(X, Y: Double): Integer;
begin
  Result := Root.XYToRow(X, Y);
end;

function TTMSFNCCustomDataGrid.CancelEditing: Boolean;
begin
  Result := Root.CancelEditing;
end;

function TTMSFNCCustomDataGrid.CanDeleteColumn(AColumn: Integer): Boolean;
begin
  Result := Root.CanDeleteColumn(AColumn);
end;

function TTMSFNCCustomDataGrid.CanDeleteRow(ARow: Integer): Boolean;
begin
  Result := Root.CanDeleteRow(ARow);
end;

function TTMSFNCCustomDataGrid.CanInsertColumn(AColumn: Integer): Boolean;
begin
  Result := Root.CanInsertColumn(AColumn);
end;

function TTMSFNCCustomDataGrid.CanInsertRow(ARow: Integer): Boolean;
begin
  Result := Root.CanInsertRow(ARow);
end;

function TTMSFNCCustomDataGrid.CanMergeCells(AColumn, ARow, AColumnCount,
  ARowCount: Integer): Boolean;
begin
  Result := Root.CanMergeCells(AColumn, ARow, AColumnCount, ARowCount);
end;

function TTMSFNCCustomDataGrid.CanMoveColumn(AFromColumn,
  AToColumn: Integer): Boolean;
begin
  Result := Root.CanMoveColumn(AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.CanMoveRow(AFromRow, AToRow: Integer): Boolean;
begin
  Result := Root.CanMoveRow(AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.CanSwapColumn(AFromColumn,
  AToColumn: Integer): Boolean;
begin
  Result := Root.CanSwapColumn(AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.CanSwapRow(AFromRow, AToRow: Integer): Boolean;
begin
  Result := Root.CanSwapRow(AFromRow, AToRow);
end;

constructor TTMSFNCCustomDataGrid.Create(AOwner: TComponent);
begin
  inherited;
  FDesigner := True;

  {$IFNDEF WEBLIB}
  if IsDesigning then
  begin
    FDesignerControl := TTMSFNCDataGridDesigner.Create(Self);
    FDesignerControl.HitTest := True;
    FDesignerControl.Stored := False;
    FDesignerControl.Parent := Self;
    FDesignerControl.ControlAlignment := caLeft;
    FDesignerControl.Stroke.Width := 1;
    FDesignerControl.Stroke.Color := gcGray;
    FDesignerControl.Fill.Color := Lighter(gcLightsteelblue, 80);
  end;
  {$ENDIF}

  FFooter := TTMSFNCDataGridFooter.Create;
  FFooter.OnChangeRebuild := FooterChangedRebuild;
  FHeader := TTMSFNCDataGridHeader.Create;
  FHeader.OnChangeRebuild := HeaderChangedRebuild;

  Width := 540;
  Height := 400;

  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) then
  begin
    case FDesignerControl.ControlAlignment of
      caTop, caBottom: Height := Height + FDesignerControl.Height;
      caLeft, caRight: Width := Width + FDesignerControl.Width;
    end;
  end;
  {$ENDIF}

  if (csDesigning in ComponentState) and not ((csReading in Owner.ComponentState) or (csLoading in Owner.ComponentState)) then
    InitSample;
end;

procedure TTMSFNCCustomDataGrid.CutToClipboard(ASelectedCells: Boolean);
begin
  Root.CutToClipboard(ASelectedCells);
end;

function TTMSFNCCustomDataGrid.GetAdapter: TTMSFNCDataGridDataAdapter;
begin
  Result := Root.Adapter;
end;

function TTMSFNCCustomDataGrid.GetBitmapContainer: TTMSFNCBitmapContainer;
begin
  Result := Root.BitmapContainer;
end;

function TTMSFNCCustomDataGrid.GetBitmapNames(AColumn, ARow: Integer): string;
begin
  Result := Root.BitmapNames[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetBitmaps(AColumn,
  ARow: Integer): TTMSFNCBitmap;
begin
  Result := Root.Bitmaps[AColumn, ARow];
end;

procedure TTMSFNCCustomDataGrid.SetBitmap(AColumn, ARow: Integer;
  AName: string);
begin
  Root.SetBitmap(AColumn, ARow, AName);
end;

procedure TTMSFNCCustomDataGrid.SetBitmap(ACell: TTMSFNCDataGridCellCoord;
  ABitmap: TTMSFNCBitmap);
begin
  Root.SetBitmap(ACell, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.SetBitmap(AColumn, ARow: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  Root.SetBitmap(AColumn, ARow, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.SetBitmap(ACell: TTMSFNCDataGridCellCoord;
  AName: string);
begin
  Root.SetBitmap(ACell, AName);
end;

procedure TTMSFNCCustomDataGrid.SetBitmapColumn(AColumn: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  Root.SetBitmapColumn(AColumn, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.SetBitmapColumn(AColumn: Integer;
  AName: string);
begin
  Root.SetBitmapColumn(AColumn, AName);
end;

procedure TTMSFNCCustomDataGrid.SetBitmapContainer(const Value: TTMSFNCBitmapContainer);
begin
  Root.BitmapContainer := Value;
end;

procedure TTMSFNCCustomDataGrid.AddButton(ACell: TTMSFNCDataGridCellCoord; AText: string = '');
begin
  Root.AddButton(ACell, AText);
end;

procedure TTMSFNCCustomDataGrid.AddBitmap(ACell: TTMSFNCDataGridCellCoord;
  AName: string);
begin
  Root.AddBitmap(ACell, AName);
end;

procedure TTMSFNCCustomDataGrid.AddBitmap(AColumn, ARow: Integer;
  AName: string);
begin
  Root.AddBitmap(AColumn, ARow, AName);
end;

procedure TTMSFNCCustomDataGrid.AddBitmap(ACell: TTMSFNCDataGridCellCoord;
  ABitmap: TTMSFNCBitmap);
begin
  Root.AddBitmap(ACell, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.AddBitmap(AColumn, ARow: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  Root.AddBitmap(AColumn, ARow, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.AddBitmapColumn(AColumn: Integer;
  AName: string);
begin
  Root.AddBitmapColumn(AColumn, AName);
end;

procedure TTMSFNCCustomDataGrid.AddBitmapColumn(AColumn: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  Root.AddBitmapColumn(AColumn, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.AddButton(AColumn, ARow: Integer; AText: string = '');
begin
  Root.AddButton(AColumn, ARow, AText);
end;

procedure TTMSFNCCustomDataGrid.AddButtonColumn(AColumn: Integer; AText: string = '');
begin
  Root.AddButtonColumn(AColumn, AText);
end;

procedure TTMSFNCCustomDataGrid.AddCheckBox(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean);
begin
  Root.AddCheckBox(ACell, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddCheckBox(AColumn, ARow: Integer;
  AValue: Boolean);
begin
  Root.AddCheckBox(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddCheckBoxColumn(AColumn: Integer;
  AValue: Boolean);
begin
  Root.AddCheckBoxColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddDataCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.AddDataCheckBox(ACell);
end;

procedure TTMSFNCCustomDataGrid.AddDataCheckBox(AColumn, ARow: Integer);
begin
  Root.AddDataCheckBox(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.AddDataCheckBoxColumn(AColumn: Integer);
begin
  Root.AddDataCheckBoxColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.AddColumn;
begin
  Root.AddColumn;
end;

function TTMSFNCCustomDataGrid.AddGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer;
begin
  Result := Root.AddGrid(ACell);
end;

function TTMSFNCCustomDataGrid.AddGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
begin
  Result := Root.AddGrid(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.AddNodeRow(ARow, AChildCount, ALevel: Integer;
  AMerge: Boolean);
begin
  Root.AddNodeRow(ARow, AChildCount, ALevel, AMerge);
end;

procedure TTMSFNCCustomDataGrid.AddProgressBar(ACell: TTMSFNCDataGridCellCoord;
  AValue: Integer);
begin
  Root.AddProgressBar(ACell, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddProgressBar(AColumn, ARow, AValue: Integer);
begin
  Root.AddProgressBar(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddProgressBarColumn(AColumn, AValue: Integer);
begin
  Root.AddProgressBarColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddDataProgressBar(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.AddDataProgressBar(ACell);
end;

procedure TTMSFNCCustomDataGrid.AddDataProgressBar(AColumn, ARow: Integer);
begin
  Root.AddDataProgressBar(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.AddDataProgressBarColumn(AColumn: Integer);
begin
  Root.AddDataProgressBarColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.AddRadioButton(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.AddRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.AddRadioButtonColumn(AColumn: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddRadioButtonColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddDataRadioButton(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddDataRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.AddDataRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddDataRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.AddDataRadioButtonColumn(AColumn: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddDataRadioButtonColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddRow;
begin
  Root.AddRow;
end;

procedure TTMSFNCCustomDataGrid.ApplyFilter;
begin
  Root.ApplyFilter;
end;

procedure TTMSFNCCustomDataGrid.ApplyStyle;
var
  c: TTMSFNCGraphicsColor;
begin
  inherited;
  BeginUpdate;
  try
    c := gcNull;
    if TTMSFNCStyles.GetStyleBackgroundFillColor(c) then
      Fill.Color := c;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCCustomDataGrid.RandomFill(ADoFixed: Boolean;
  ARandomNumber: Integer);
begin
  Root.RandomFill(ADoFixed, ARandomNumber);
end;

function TTMSFNCCustomDataGrid.RealToDisplayCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := Root.RealToDisplayCell(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveCheckBox(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveCheckBox(AColumn, ARow: Integer);
begin
  Root.RemoveCheckBox(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveDataCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveDataCheckBox(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveDataCheckBox(AColumn, ARow: Integer);
begin
  Root.RemoveDataCheckBox(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveProgressBar(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveProgressBar(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveProgressBar(AColumn, ARow: Integer);
begin
  Root.RemoveProgressBar(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveButton(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveButton(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveBitmap(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveBitmap(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveBitmap(AColumn, ARow: Integer);
begin
  Root.RemoveBitmap(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveButton(AColumn, ARow: Integer);
begin
  Root.RemoveButton(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveRadioButton(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveRadioButton(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveRadioButton(AColumn, ARow: Integer);
begin
  Root.RemoveRadioButton(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveDataRadioButton(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveDataRadioButton(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveDataRadioButton(AColumn, ARow: Integer);
begin
  Root.RemoveDataRadioButton(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveFilter;
begin
  Root.RemoveFilter;
end;

procedure TTMSFNCCustomDataGrid.RemoveLayout(AColumn, ARow: Integer);
begin
  Root.RemoveLayout(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveLayout(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveLayout(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveFilterEditor(AColumn, ARow: Integer);
begin
  Root.RemoveFilterEditor(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveFilterEditor(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveFilterEditor(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveGrid(AColumn, ARow: Integer);
begin
  Root.RemoveGrid(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveGrid(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveGrid(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveNodeRow(ARow: Integer);
begin
  Root.RemoveNodeRow(ARow);
end;

function TTMSFNCCustomDataGrid.Replace(AOriginalValue,
  ANewValue: string): Integer;
begin
  Result := Root.Replace(AOriginalValue, ANewValue);
end;

function TTMSFNCCustomDataGrid.Replace(AOriginalValue, ANewValue: string;
  AParameters: TTMSFNCDataGridDataFindParameters): Integer;
begin
  Result := Root.Replace(AOriginalValue, ANewValue, AParameters);
end;

procedure TTMSFNCCustomDataGrid.ResetToDefaultStyle;
begin
  BeginUpdate;
  try
    Fill.Kind := gfkSolid;
    Stroke.Kind := gskSolid;
    Fill.Color := gcWhite;
    Stroke.Color := gcDarkGray;
  finally
    EndUpdate;
  end;
end;

function TTMSFNCCustomDataGrid.GetHeader: TTMSFNCDataGridHeader;
begin
  Result := FHeader;
end;

function TTMSFNCCustomDataGrid.GetHeaderRect: TRectF;
begin
  Result := RectF(0, 0, Width, GetHeaderSize);
end;

function TTMSFNCCustomDataGrid.GetHeaderSize: Double;
begin
  Result := 0;
  if Header.Visible then
    Result := Header.Size;
end;

function TTMSFNCCustomDataGrid.GetHintString: string;
var
  h: string;
  r: TTMSFNCDataGridRendererOpen;
begin
  Result := inherited GetHintString;
  if Assigned(FFocusedRenderer) then
    r := TTMSFNCDataGridRendererOpen(FFocusedRenderer)
  else
    r := TTMSFNCDataGridRendererOpen(Root);

  if r.HasHint then
  begin
    h := r.GetHintString;
    Result := h;
  end;
end;

function TTMSFNCCustomDataGrid.GetHMax: Single;
begin
  Result := Root.GetHMax;
end;

function TTMSFNCCustomDataGrid.GetTextAligns(AColumn,
  ARow: Integer): TTMSFNCGraphicsTextAlign;
begin
  Result := Root.TextAligns[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetHorizontalScrollBar: TScrollBar;
begin
  Result := Root.HorizontalScrollBar;
end;

function TTMSFNCCustomDataGrid.GetHorizontalScrollBarVisible: Boolean;
begin
  Result := Root.HorizontalScrollBarVisible;
end;

function TTMSFNCCustomDataGrid.GetHScrollValue: Single;
begin
  Result := Root.GetHScrollValue;
end;

function TTMSFNCCustomDataGrid.GetHViewPortSize: Single;
begin
  Result := Root.GetHViewPortSize;
end;

function TTMSFNCCustomDataGrid.HasHint: Boolean;
var
  h: string;
  r: TTMSFNCDataGridRendererOpen;
begin
  Result := inherited HasHint;
  if Result then
  begin
    if Assigned(FFocusedRenderer) then
      r := TTMSFNCDataGridRendererOpen(FFocusedRenderer)
    else
      r := TTMSFNCDataGridRendererOpen(Root);

    if r.HasHint then
    begin
      h := r.GetHintString;
      Result := h <> '';
    end;
  end;
end;

function TTMSFNCCustomDataGrid.HasRowData(ARow: Integer): Boolean;
begin
  Result := Root.HasRowData(ARow);
end;

procedure TTMSFNCCustomDataGrid.ChangeDPIScale(M: Integer; D: Integer);
begin
  inherited;
  BeginUpdate;
  try
    Root.ChangeDPIScale(M, D);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCCustomDataGrid.HandleDblClick(X, Y: Single);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
  c: TPointF;
begin
  inherited;
  c := ScreenToLocalEx(TTMSFNCUtils.GetMousePos);
  m := DefaultMouseInfo;
  m.Kind := gmkDouble;
  m.Handled := False;
  m.X := c.X;
  m.Y := c.Y;

  r := Root.XYToRenderer(X, Y);
  if not Assigned(r) then
    r := Root;

  if Assigned(r) then
    r.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.DeleteColumn(AColumn: Integer);
begin
  Root.DeleteColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.DeleteColumns(AColumn, ACount: Integer);
begin
  Root.DeleteColumns(AColumn, ACount);
end;

procedure TTMSFNCCustomDataGrid.DeleteRow(ARow: Integer);
begin
  Root.DeleteRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.DeleteRows(ARow, ACount: Integer);
begin
  Root.DeleteRows(ARow, ACount);
end;

destructor TTMSFNCCustomDataGrid.Destroy;
begin
  {$IFNDEF WEBLIB}
  FreeAndNil(FDesignerControl);
  {$ENDIF}
  FHeader.Free;
  FFooter.Free;
  inherited;
end;

function TTMSFNCCustomDataGrid.DisplayToRealCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := Root.DisplayToRealCell(ACell);
end;

procedure TTMSFNCCustomDataGrid.HandleDialogKey(var Key: Word; Shift: TShiftState);
var
  k: TTMSFNCDataGridKeyInfo;
begin
  k := DefaultKeyInfo;
  k.Handled := True;
  k.Key := Key;
  k.Kind := gkkDialog;
  k.Shift := Shift;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleKey(k)
  else
    Root.HandleKey(k);

  Key := k.Key;

  if k.Handled then
    inherited;
end;

procedure TTMSFNCCustomDataGrid.HandleMouseEnter;
var
  m: TTMSFNCDataGridMouseInfo;
begin
  inherited;
  m := DefaultMouseInfo;
  m.Kind := gmkEnter;
  Root.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseLeave;
var
  m: TTMSFNCDataGridMouseInfo;
begin
  inherited;
  m := DefaultMouseInfo;
  m.Kind := gmkLeave;
  Root.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.DrawFooter(AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
begin
  if not Footer.Visible then
    Exit;

  r := GetFooterRect;
  AGraphics.Fill.Assign(Footer.Fill);
  AGraphics.Stroke.Assign(Footer.Stroke);
  AGraphics.DrawRectangle(r);
end;

procedure TTMSFNCCustomDataGrid.DrawHeader(AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
begin
  if not Header.Visible then
    Exit;

  r := GetHeaderRect;
  AGraphics.Fill.Assign(Header.Fill);
  AGraphics.Stroke.Assign(Header.Stroke);
  AGraphics.DrawRectangle(r);
end;

procedure TTMSFNCCustomDataGrid.EditCell(ACell: TTMSFNCDataGridCellCoord; ACellStart: TTMSFNCDataGridEditCellStart = gcsNone; AInsertKey: Boolean = False; AValue: string = ''; AKey: Word = 0);
begin
  Root.EditCell(ACell, ACellStart, AInsertKey, AValue, AKey);
end;

procedure TTMSFNCCustomDataGrid.ExpandAllNodes;
begin
  Root.ExpandAllNodes;
end;

procedure TTMSFNCCustomDataGrid.ExpandNode(ARow: Integer);
begin
  Root.ExpandNode(ARow);
end;

function TTMSFNCCustomDataGrid.GetDocURL: string;
begin
  Result := TTMSFNCDataGridDocURL;
end;

function TTMSFNCCustomDataGrid.GetFocusedCell: TTMSFNCDataGridCellCoord;
begin
  Result := Root.FocusedCell;
end;

function TTMSFNCCustomDataGrid.GetFontColors(AColumn,
  ARow: Integer): TTMSFNCGraphicsColor;
begin
  Result := Root.FontColors[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFontNames(AColumn, ARow: Integer): string;
begin
  Result := Root.FontNames[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFontSizes(AColumn, ARow: Integer): Single;
begin
  Result := Root.FontSizes[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFontStyles(AColumn,
  ARow: Integer): TFontStyles;
begin
  Result := Root.FontStyles[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFooterRect: TRectF;
begin
  Result := RectF(0, Height - GetFooterSize, Width, Height);
end;

function TTMSFNCCustomDataGrid.GetFooterSize: Double;
begin
  Result := 0;
  if Footer.Visible then
    Result := Footer.Size;
end;

function TTMSFNCCustomDataGrid.GetVersion: string;
begin
  Result := GetVersionNumber(MAJ_VER, MIN_VER, REL_VER, BLD_VER);
end;

function TTMSFNCCustomDataGrid.GetVerticalTextAligns(AColumn,
  ARow: Integer): TTMSFNCGraphicsTextAlign;
begin
  Result := Root.VerticalTextAligns[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetVerticalScrollBar: TScrollBar;
begin
  Result := Root.VerticalScrollBar;
end;

function TTMSFNCCustomDataGrid.GetVerticalScrollBarVisible: Boolean;
begin
  Result := Root.VerticalScrollBarVisible;
end;

procedure TTMSFNCCustomDataGrid.LastPage;
begin
  Root.LastPage;
end;

procedure TTMSFNCCustomDataGrid.LinearFill(ADoFixed: Boolean);
begin
  Root.LinearFill(ADoFixed);
end;

procedure TTMSFNCCustomDataGrid.LoadFromCSVData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.LoadFromCSVData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromCSVData(AFileName: string);
begin
  Root.LoadFromCSVData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.LoadFromCSVStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.LoadFromCSVStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromCSVStreamData(AStream: TStream);
begin
  Root.LoadFromCSVStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.LoadFromFileData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.LoadFromFileData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromFileData(AFileName: string);
begin
  Root.LoadFromFileData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.LoadFromFileStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.LoadFromFileStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONData(AFileName: string;
  AEncoding: TEncoding; ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONData(AFileName, AEncoding, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONData(AFileName, ARowDataName,
  AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONData(AFileName, TEncoding.Default, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONStreamData(AStream: TStream;
  ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONStreamData(AStream, TEncoding.Default, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONStreamData(AStream: TStream;
  AEncoding: TEncoding; ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONStreamData(AStream, AEncoding, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONTextData(AText, ARowDataName,
  AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONTextData(AText, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromFileStreamData(AStream: TStream);
begin
  Root.LoadFromFileStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.LoadFromXMLData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.LoadFromXMLData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromXMLData(AFileName: string);
begin
  Root.LoadFromXMLData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.LoadFromXMLStreamData(AStream: TStream);
begin
  Root.LoadFromXMLStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.LoadFromXMLStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.LoadFromXMLStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadSampleData;
begin
  Root.LoadSampleData;
end;

procedure TTMSFNCCustomDataGrid.LogMessage(AMessage: string;
  AMessageType: TTMSFNCDataGridLogMessageType);
begin
  Root.LogMessage(AMessage, AMessageType);
end;

procedure TTMSFNCCustomDataGrid.MarkCells(AHilightText: string;
  AOptions: TTMSFNCDataGridMarkOptions);
begin
  Root.MarkCells(AHilightText, AOptions);
end;

function TTMSFNCCustomDataGrid.MatchFilter(ARow: Integer): Boolean;
begin
  Result := Root.MatchFilter(ARow);
end;

procedure TTMSFNCCustomDataGrid.MergeCells(AColumn, ARow, AColumnCount,
  ARowCount: Integer);
begin
  Root.MergeCells(AColumn, ARow, AColumnCount, ARowCount);
end;

procedure TTMSFNCCustomDataGrid.MergeColumn(AColumn: Integer);
begin
  Root.MergeColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.MergeColumns(AColumn, ACount: Integer);
begin
  Root.MergeColumns(AColumn, ACount);
end;

procedure TTMSFNCCustomDataGrid.MergeRange(
  ARange: TTMSFNCDataGridCellCoordRange);
begin
  Root.MergeRange(ARange);
end;

procedure TTMSFNCCustomDataGrid.MergeRow(ARow: Integer);
begin
  Root.MergeRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.MergeRows(ARow, ACount: Integer);
begin
  Root.MergeRows(ARow, ACount);
end;

procedure TTMSFNCCustomDataGrid.MoveColumn(AFromColumn, AToColumn: Integer);
begin
  Root.MoveColumn(AFromColumn, AToColumn);
end;

procedure TTMSFNCCustomDataGrid.MoveRow(AFromRow, AToRow: Integer);
begin
  Root.MoveRow(AFromRow, AToRow);
end;

procedure TTMSFNCCustomDataGrid.InitSample;
var
  I: Integer;
begin
  BeginUpdate;
  try
    Clear;

    ResetToDefaultStyle;

    Root.DefaultRowHeight := ScalePaintValue(30);
    Root.FixedColumnCount := 0;

    Root.ColumnWidths[0] := ScalePaintValue(70);
    Root.ColumnWidths[1] := ScalePaintValue(250);
    Root.ColumnWidths[2] := ScalePaintValue(100);
    Root.ColumnWidths[3] := ScalePaintValue(100);

    Columns.Clear;
    for I := 0 to 3 do
      Columns.Add;

    LoadSampleData;

  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCCustomDataGrid.InsertColumn(AColumn: Integer);
begin
  Root.InsertColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.InsertColumns(AColumn, ACount: Integer);
begin
  Root.InsertColumns(AColumn, ACount);
end;

procedure TTMSFNCCustomDataGrid.InsertRow(ARow: Integer);
begin
  Root.InsertRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.InsertRows(ARow, ACount: Integer);
begin
  Root.InsertRows(ARow, ACount);
end;

function TTMSFNCCustomDataGrid.IsCellBase(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellBase(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellDisabled(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellDisabled(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellDisplayed(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellDisplayed(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFixed(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFixed(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFixedRoot(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFixedRoot(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFreeze(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFreeze(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFreezeFixed(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFreezeFixed(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFreezeRoot(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFreezeRoot(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellHidden(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellHidden(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellMerged(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellMerged(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellSelected(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellSelected(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellValid(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellValid(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellValid(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.IsCellValid(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.IsCellVisible(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellVisible(ACell);
end;

function TTMSFNCCustomDataGrid.IsColumnMerged(AColumn: Integer): Boolean;
begin
  Result := Root.IsColumnMerged(AColumn);
end;

function TTMSFNCCustomDataGrid.IsColumnValid(AColumn: Integer): Boolean;
begin
  Result := Root.IsColumnValid(AColumn);
end;

function TTMSFNCCustomDataGrid.IsDefaultColumnWidthStored: Boolean;
begin
  Result := DefaultColumnWidth <> 75;
end;

function TTMSFNCCustomDataGrid.IsDefaultRowHeightStored: Boolean;
begin
  Result := DefaultRowHeight <> 30;
end;

function TTMSFNCCustomDataGrid.IsEditing: Boolean;
begin
  Result := Root.IsEditing;
end;

function TTMSFNCCustomDataGrid.IsFixedCellSelected(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsFixedCellSelected(ACell);
end;

function TTMSFNCCustomDataGrid.IsGrouped: Boolean;
begin
  Result := Root.IsGrouped;
end;

function TTMSFNCCustomDataGrid.IsRowBand(ARow: Integer): Boolean;
begin
  Result := Root.IsRowBand(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowFiltered(ARow: Integer): Boolean;
begin
  Result := Root.IsRowFiltered(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowHidden(ARow: Integer): Boolean;
begin
  Result := Root.IsRowHidden(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowMerged(ARow: Integer): Boolean;
begin
  Result := Root.IsRowMerged(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowNode(ARow: Integer): Boolean;
begin
  Result := Root.IsRowNode(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowNormal(ARow: Integer): Boolean;
begin
  Result := Root.IsRowNormal(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowSummary(ARow: Integer): Boolean;
begin
  Result := Root.IsRowSummary(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowFilter(ARow: Integer): Boolean;
begin
  Result := Root.IsRowFilter(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowValid(ARow: Integer): Boolean;
begin
  Result := Root.IsRowValid(ARow);
end;

procedure TTMSFNCCustomDataGrid.HandleKeyDown(var Key: Word; Shift: TShiftState);
var
  k: TTMSFNCDataGridKeyInfo;
begin
  inherited;
  k := DefaultKeyInfo;
  k.Key := Key;
  k.Shift := Shift;
  k.Kind := gkkDown;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleKey(k)
  else
    Root.HandleKey(k);
  Key := k.Key;
end;

procedure TTMSFNCCustomDataGrid.HandleKeyPress(var Key: Char);
var
  k: TTMSFNCDataGridKeyInfo;
begin
  inherited;
  k := DefaultKeyInfo;
  k.Key := Ord(Key);
  k.KeyChar := Key;
  k.Kind := gkkPress;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleKey(k)
  else
    Root.HandleKey(k);
  Key := k.KeyChar;
end;

procedure TTMSFNCCustomDataGrid.HandleKeyUp(var Key: Word; Shift: TShiftState);
var
  k: TTMSFNCDataGridKeyInfo;
begin
  inherited;
  k := DefaultKeyInfo;
  k.Key := Key;
  k.Shift := Shift;
  k.Kind := gkkUp;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleKey(k)
  else
    Root.HandleKey(k);
  Key := k.Key;
end;

procedure TTMSFNCCustomDataGrid.Draw(AGraphics: TTMSFNCGraphics; ARect: TRectF);
begin
  inherited;
  if (Root.UpdateCount > 0) then
    Exit;

  AGraphics.BitmapContainer := BitmapContainer;
  Root.Stroke.Assign(Stroke);
  Root.Draw(AGraphics);
  DrawFooter(AGraphics);
  DrawHeader(AGraphics);
end;

procedure TTMSFNCCustomDataGrid.SetFocusedCell(
  const Value: TTMSFNCDataGridCellCoord);
begin
  Root.FocusedCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFontColors(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsColor);
begin
  Root.FontColors[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFontNames(AColumn, ARow: Integer;
  const Value: string);
begin
  Root.FontNames[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFontSizes(AColumn, ARow: Integer;
  const Value: Single);
begin
  Root.FontSizes[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFontStyles(AColumn, ARow: Integer;
  const Value: TFontStyles);
begin
  Root.FontStyles[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetLeftColumn(const Value: Integer);
begin
  Root.LeftColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFooter(const Value: TTMSFNCDataGridFooter);
begin
  FFooter.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetGlobalFont(const Value: TTMSFNCAppearanceGlobalFont);
begin
  Root.GlobalFont.Assign(Value);
end;

function TTMSFNCCustomDataGrid.SetGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
begin
  Result := Root.SetGrid(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.SetGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer;
begin
  Result := Root.SetGrid(ACell);
end;

procedure TTMSFNCCustomDataGrid.SetHeader(const Value: TTMSFNCDataGridHeader);
begin
  FHeader.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetHiddenColumns(AColumn: Integer;
  const Value: Boolean);
begin
  Root.HiddenColumns[AColumn] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetHiddenRows(ARow: Integer;
  const Value: Boolean);
begin
  Root.HiddenRows[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetTextAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsTextAlign);
begin
  Root.TextAligns[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetHorizontalScrollBarVisible(
  const Value: Boolean);
begin
  Root.HorizontalScrollBarVisible := Value;
end;

procedure TTMSFNCCustomDataGrid.SetInts(AColumn, ARow: Integer;
  const Value: Int64);
begin
  Root.Ints[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SaveToCSVData(AFileName: string);
begin
  Root.SaveToCSVData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.SaveToCSVData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.SaveToCSVData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToCSVStreamData(AStream: TStream);
begin
  Root.SaveToCSVStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.SaveToCSVStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.SaveToCSVStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToFileData(AFileName: string);
begin
  Root.SaveToFileData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.SaveToFileData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.SaveToFileData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToFileStreamData(AStream: TStream);
begin
  Root.SaveToFileStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.SaveToFileStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.SaveToFileStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToHTMLData(AFileName: string);
begin
  Root.SaveToHTMLData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.SaveToHTMLData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.SaveToHTMLData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToHTMLStreamData(AStream: TStream);
begin
  Root.SaveToHTMLStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.SaveToHTMLStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.SaveToHTMLStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToImageData(AFileName: string);
begin
  Root.SaveToImageData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.SaveToImageStreamData(AStream: TStream);
begin
  Root.SaveToImageStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.Scroll(AHorizontalPos, AVerticalPos: Double);
begin
  Root.Scroll(AHorizontalPos, AVerticalPos);
end;

procedure TTMSFNCCustomDataGrid.SelectAllCells;
begin
  Root.SelectAllCells;
end;

procedure TTMSFNCCustomDataGrid.SelectCell(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.SelectCell(ACell);
end;

procedure TTMSFNCCustomDataGrid.SetAdapter(const Value: TTMSFNCDataGridDataAdapter);
begin
  Root.Adapter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetAdaptToStyle(const Value: Boolean);
begin
  inherited;
  Root.AdaptToStyle := Value;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) then
    FDesignerControl.AdaptToStyle := Value;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.StopEditing: Boolean;
begin
  Result := Root.StopEditing;
end;

procedure TTMSFNCCustomDataGrid.StretchColumns(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewWidth: Double = -1);
begin
  Root.StretchColumns(AMode, ARange, AIndex, ANewWidth);
end;

procedure TTMSFNCCustomDataGrid.StretchRows(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewHeight: Double = -1);
begin
  Root.StretchRows(AMode, ARange, AIndex, ANewHeight);
end;

procedure TTMSFNCCustomDataGrid.SwapCells(AFromCell,
  AToCell: TTMSFNCDataGridCellCoord);
begin
  Root.SwapCells(AFromCell, AToCell);
end;

procedure TTMSFNCCustomDataGrid.SwapColumns(AFromColumn, AToColumn: Integer);
begin
  Root.SwapColumn(AFromColumn, AToColumn);
end;

procedure TTMSFNCCustomDataGrid.SwapRows(AFromRow, AToRow: Integer);
begin
  Root.SwapRow(AFromRow, AToRow);
end;

procedure TTMSFNCCustomDataGrid.ToggleNode(ARow: Integer);
begin
  Root.ToggleNode(ARow);
end;

function TTMSFNCCustomDataGrid.TypeOfValue(
  AValue: TTMSFNCDataGridCellValue): TTypeKind;
begin
  Result := Root.TypeOfValue(AValue);
end;

procedure TTMSFNCCustomDataGrid.Ungroup;
begin
  Root.Ungroup;
end;

procedure TTMSFNCCustomDataGrid.UnhideAll;
begin
  Root.UnhideAll;
end;

procedure TTMSFNCCustomDataGrid.UnhideAllColumns;
begin
  Root.UnhideAllColumns;
end;

procedure TTMSFNCCustomDataGrid.UnhideAllRows;
begin
  Root.UnhideAllRows;
end;

procedure TTMSFNCCustomDataGrid.UnhideColumn(AColumn: Integer);
begin
  Root.UnhideColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.UnhideRow(ARow: Integer);
begin
  Root.UnhideRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.UnhilightCells(
  AOptions: TTMSFNCDataGridHilightOptions);
begin
  Root.UnhilightCells(AOptions);
end;

procedure TTMSFNCCustomDataGrid.UnmarkCells(
  AOptions: TTMSFNCDataGridMarkOptions);
begin
  Root.UnmarkCells(AOptions);
end;

procedure TTMSFNCCustomDataGrid.UpdateColumnCalculation(AColumn: Integer; AName: string);
begin
  Root.UpdateColumnCalculation(AColumn, AName);
end;

procedure TTMSFNCCustomDataGrid.UpdateColumnCalculations;
begin
  Root.UpdateColumnCalculations;
end;

procedure TTMSFNCCustomDataGrid.UpdateRowCalculation(ARow: Integer; AName: string);
begin
  Root.UpdateRowCalculation(ARow, AName);
end;

procedure TTMSFNCCustomDataGrid.UpdateRowCalculations;
begin
  Root.UpdateRowCalculations;
end;

procedure TTMSFNCCustomDataGrid.UpdateCalculations;
begin
  Root.UpdateCalculations;
end;

{ TTMSFNCDataGrid }

function TTMSFNCCustomDataGrid.GetBooleans(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.Booleans[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetBottomRow: Integer;
begin
  Result := Root.BottomRow;
end;

function TTMSFNCCustomDataGrid.GetCalculationRect: TRectF;
begin
  Result := inherited GetCalculationRect;

  Result.Top := Result.Top + GetHeaderSize;
  Result.Top := Result.Top + GetDesignerTopSize;
  Result.Left := Result.Left + GetDesignerLeftSize;
  Result.Bottom := Result.Bottom - GetFooterSize;
  Result.Bottom := Result.Bottom - GetDesignerBottomSize;
  Result.Right := Result.Right - GetDesignerRightSize;

  Result.Right := Result.Left + Max(0, Result.Right - Result.Left);
  Result.Bottom := Result.Top + Max(0, Result.Bottom - Result.Top);
end;

function TTMSFNCCustomDataGrid.GetCellAppearance: TTMSFNCDataGridCellAppearance;
begin
  Result := Root.CellAppearance;
end;

function TTMSFNCCustomDataGrid.GetCellData(AColumn,
  ARow: Integer): TTMSFNCDataGridCellItem;
begin
  Result := Root.CellData[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetCellKind(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellKind;
begin
  Result := Root.GetCellKind(ACell);
end;

function TTMSFNCCustomDataGrid.GetCellMergeType(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridDataCellMergeType;
begin
  Result := Root.GetCellMergeType(ACell);
end;

function TTMSFNCCustomDataGrid.GetCellObjectForCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
begin
  Result := Root.GetCellObjectForCell(ACell);
end;

function TTMSFNCCustomDataGrid.GetCellPopupMenu: TPopupMenu;
begin
  Result := Root.CellPopupMenu;
end;

function TTMSFNCCustomDataGrid.GetCells(AColumn,
  ARow: Integer): TTMSFNCDataGridCellValue;
begin
  Result := Root.Cells[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetCellSize(
  ACell: TTMSFNCDataGridCellCoord): TSizeF;
begin
  Result := Root.GetCellSize(ACell);
end;

function TTMSFNCCustomDataGrid.GetColors(AColumn,
  ARow: Integer): TTMSFNCGraphicsColor;
begin
  Result := Root.Colors[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetColumnCalculation(
  AColumn: Integer; AName: string): TTMSFNCDataGridDataColumnCalculationArray;
begin
  Result := Root.ColumnCalculations[AColumn, AName];
end;

function TTMSFNCCustomDataGrid.GetRowCalculation(
  ARow: Integer; AName: string): TTMSFNCDataGridDataRowCalculationArray;
begin
  Result := Root.RowCalculations[ARow, AName];
end;

function TTMSFNCCustomDataGrid.GetColumnCount: Integer;
begin
  Result := Root.ColumnCount;
end;

function TTMSFNCCustomDataGrid.GetColumnOrder(AColumn: Integer): Integer;
begin
  Result := Root.ColumnOrders[AColumn];
end;

function TTMSFNCCustomDataGrid.GetColumnPos(AColumn: Integer): Double;
begin
  Result := Root.ColumnPositions[AColumn];
end;

function TTMSFNCCustomDataGrid.GetColumnReverseOrder(AColumn: Integer): Integer;
begin
  Result := Root.ColumnReverseOrders[AColumn];
end;

function TTMSFNCCustomDataGrid.GetColumns: TTMSFNCDataGridColumns;
begin
  Result := Root.Columns;
end;

function TTMSFNCCustomDataGrid.GetColumnWidths(AColumn: Integer): Double;
begin
  Result := Root.ColumnWidths[AColumn];
end;

function TTMSFNCCustomDataGrid.GetComments(AColumn, ARow: Integer): string;
begin
  Result := Root.Comments[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetHints(AColumn, ARow: Integer): string;
begin
  Result := Root.Hints[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlAligns(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControlAlign;
begin
  Result := Root.ControlAligns[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlPositions(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControlPosition;
begin
  Result := Root.ControlPositions[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlHeights(AColumn,
  ARow: Integer): Integer;
begin
  Result := Root.ControlHeights[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlMargins(AColumn,
  ARow: Integer): TTMSFNCMargins;
begin
  Result := Root.ControlMargins[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControls(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControl;
begin
  Result := Root.Controls[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlTexts(AColumn, ARow: Integer): string;
begin
  Result := Root.ControlTexts[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlWidths(AColumn,
  ARow: Integer): Integer;
begin
  Result := Root.ControlWidths[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetCustomHorizontalScrollBar: TTMSFNCScrollBar;
begin
  Result := Root.CustomHorizontalScrollBar;
end;

function TTMSFNCCustomDataGrid.GetCustomScrollBars: Boolean;
begin
  Result := Root.CustomScrollBars;
end;

function TTMSFNCCustomDataGrid.GetCustomVerticalScrollBar: TTMSFNCScrollBar;
begin
  Result := Root.CustomVerticalScrollBar;
end;

function TTMSFNCCustomDataGrid.GetDefaultColumnWidth: Double;
begin
  Result := Root.DefaultColumnWidth;
end;

function TTMSFNCCustomDataGrid.GetDefaultRowHeight: Double;
begin
  Result := Root.DefaultRowHeight;
end;

function TTMSFNCCustomDataGrid.GetDesignerTopSize: Double;
begin
  Result := 0;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) and Assigned(FDesignerControl.Parent) and (FDesignerControl.Visible) then
  begin
    case FDesignerControl.ControlAlignment of
      caTop: Result := FDesignerControl.Height - 1;
    end;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.GetDesignerBottomSize: Double;
begin
  Result := 0;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) and Assigned(FDesignerControl.Parent) and (FDesignerControl.Visible) then
  begin
    case FDesignerControl.ControlAlignment of
      caBottom: Result := FDesignerControl.Height - 1;
    end;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.GetDesignerRightSize: Double;
begin
  Result := 0;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) and Assigned(FDesignerControl.Parent) and (FDesignerControl.Visible) then
  begin
    case FDesignerControl.ControlAlignment of
      caRight: Result := FDesignerControl.Width - 1;
    end;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.GetDesignerLeftSize: Double;
begin
  Result := 0;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) and Assigned(FDesignerControl.Parent) and (FDesignerControl.Visible) then
  begin
    case FDesignerControl.ControlAlignment of
      caLeft: Result := FDesignerControl.Width - 1;
    end;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.GetFilter: TTMSFNCDataGridDataFilter;
begin
  Result := Root.Filter;
end;

function TTMSFNCCustomDataGrid.GetFilterAppearance: TTMSFNCDataGridFilterAppearance;
begin
  Result := Root.FilterAppearance;
end;

function TTMSFNCCustomDataGrid.GetFilteredRowCount: Integer;
begin
  Result := Root.FilteredRowCount;
end;

function TTMSFNCCustomDataGrid.GetDragAppearance: TTMSFNCDataGridDragAppearance;
begin
  Result := Root.DragAppearance;
end;

function TTMSFNCCustomDataGrid.GetEditingCell: TTMSFNCDataGridCellCoord;
begin
  Result := Root.EditingCell;
end;

function TTMSFNCCustomDataGrid.GetFixedBottomRowCount: Integer;
begin
  Result := Root.FixedBottomRowCount;
end;

function TTMSFNCCustomDataGrid.GetFixedColumnCount: Integer;
begin
  Result := Root.FixedColumnCount;
end;

function TTMSFNCCustomDataGrid.GetFixedRightColumnCount: Integer;
begin
  Result := Root.FixedRightColumnCount;
end;

function TTMSFNCCustomDataGrid.GetFixedRowCount: Integer;
begin
  Result := Root.FixedRowCount;
end;

function TTMSFNCCustomDataGrid.GetFloats(AColumn, ARow: Integer): Double;
begin
  Result := Root.Floats[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFreezeColumnCount: Integer;
begin
  Result := Root.FreezeColumnCount;
end;

function TTMSFNCCustomDataGrid.GetFreezeRowCount: Integer;
begin
  Result := Root.FreezeRowCount;
end;

function TTMSFNCCustomDataGrid.GetGlobalFont: TTMSFNCAppearanceGlobalFont;
begin
  Result := Root.GlobalFont;
end;

function TTMSFNCCustomDataGrid.GetGrids(AColumn,
  ARow: Integer): TTMSFNCDataGridRenderer;
begin
  Result := Root.Grids[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetHasLayout(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.HasLayout[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetHasRowOrder(ARow: Integer): Boolean;
begin
  Result := Root.HasRowOrder[ARow];
end;

function TTMSFNCCustomDataGrid.GetHiddenColumnCount: Integer;
begin
  Result := Root.HiddenColumnCount;
end;

function TTMSFNCCustomDataGrid.GetHiddenColumns(AColumn: Integer): Boolean;
begin
  Result := Root.HiddenColumns[AColumn];
end;

function TTMSFNCCustomDataGrid.GetHiddenColumnWidths(AColumn: Integer): Double;
begin
  Result := Root.HiddenColumnWidths[AColumn];
end;

function TTMSFNCCustomDataGrid.GetHiddenRowCount: Integer;
begin
  Result := Root.HiddenRowCount;
end;

function TTMSFNCCustomDataGrid.GetHiddenRowHeights(ARow: Integer): Double;
begin
  Result := Root.HiddenRowHeights[ARow];
end;

function TTMSFNCCustomDataGrid.GetHiddenRows(ARow: Integer): Boolean;
begin
  Result := Root.HiddenRows[ARow];
end;

function TTMSFNCCustomDataGrid.GetInts(AColumn, ARow: Integer): Int64;
begin
  Result := Root.Ints[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetLayouts(AColumn,
  ARow: Integer): TTMSFNCDataGridCellLayout;
begin
  Result := Root.Layouts[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetLeftColumn: Integer;
begin
  Result := Root.LeftColumn;
end;

function TTMSFNCCustomDataGrid.GetManagedObjects(AColumn, ARow: Integer): TObject;
begin
  Result := Root.ManagedObjects[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetMaxLevel: Integer;
begin
  Result := Root.GetMaxLevel;
end;

function TTMSFNCCustomDataGrid.GetMergedColumns(AColumn: Integer): Boolean;
begin
  Result := Root.MergedColumns[AColumn];
end;

function TTMSFNCCustomDataGrid.GetMergedRows(ARow: Integer): Boolean;
begin
  Result := Root.MergedRows[ARow];
end;

function TTMSFNCCustomDataGrid.GetObjects(AColumn, ARow: Integer): TObject;
begin
  Result := Root.Objects[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetOnAfterApplyFilter: TTMSFNCDataGridAfterApplyFilterEvent;
begin
  Result := Root.OnAfterApplyFilter;
end;

function TTMSFNCCustomDataGrid.GetOnAfterCloseInplaceEditor: TTMSFNCDataGridAfterCloseInplaceEditorEvent;
begin
  Result := Root.OnAfterCloseInplaceEditor;
end;

function TTMSFNCCustomDataGrid.GetOnAfterCopyToClipboard: TTMSFNCDataGridAfterClipboardEvent;
begin
  Result := Root.OnAfterCopyToClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnAfterCreateFilterEditor: TTMSFNCDataGridAfterCreateFilterEditorEvent;
begin
  Result := Root.OnAfterCreateFilterEditor;
end;

function TTMSFNCCustomDataGrid.GetOnAfterCutToClipboard: TTMSFNCDataGridAfterClipboardEvent;
begin
  Result := Root.OnAfterCutToClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnAfterDrawCell: TTMSFNCDataGridAfterDrawCellEvent;
begin
  Result := Root.OnAfterDrawCell;
end;

function TTMSFNCCustomDataGrid.GetOnAfterDrawEmptySpace: TTMSFNCDataGridAfterDrawEmptySpaceEvent;
begin
  Result := Root.OnAfterDrawEmptySpace;
end;

function TTMSFNCCustomDataGrid.GetOnAfterDrawGrid: TTMSFNCDataGridAfterDrawGridEvent;
begin
  Result := Root.OnAfterDrawGrid;
end;

function TTMSFNCCustomDataGrid.GetOnAfterLookup: TTMSFNCDataGridAfterLookupEvent;
begin
  Result := Root.OnAfterLookup;
end;

function TTMSFNCCustomDataGrid.GetOnAfterOpenInplaceEditor: TTMSFNCDataGridAfterOpenInplaceEditorEvent;
begin
  Result := Root.OnAfterOpenInplaceEditor;
end;

function TTMSFNCCustomDataGrid.GetOnAfterPasteFromClipboard: TTMSFNCDataGridAfterClipboardEvent;
begin
  Result := Root.OnAfterPasteFromClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnAfterShowFilter: TTMSFNCDataGridAfterShowFilterEvent;
begin
  Result := Root.OnAfterShowFilter;
end;

function TTMSFNCCustomDataGrid.GetOnAfterShowFindDialog: TTMSFNCDataGridAfterShowFindDialogEvent;
begin
  Result := Root.OnAfterShowFindDialog;
end;

function TTMSFNCCustomDataGrid.GetOnAfterShowReplaceDialog: TTMSFNCDataGridAfterShowReplaceDialogEvent;
begin
  Result := Root.OnAfterShowReplaceDialog;
end;

function TTMSFNCCustomDataGrid.GetOnAfterSortColumn: TTMSFNCDataGridAfterSortColumnEvent;
begin
  Result := Root.OnAfterSortColumn;
end;

function TTMSFNCCustomDataGrid.GetOnAppendColumn: TTMSFNCDataGridAppendColumnEvent;
begin
  Result := Root.OnAppendColumn;
end;

function TTMSFNCCustomDataGrid.GetOnAppendRow: TTMSFNCDataGridAppendRowEvent;
begin
  Result := Root.OnAppendRow;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeDrawCell: TTMSFNCDataGridBeforeDrawCellEvent;
begin
  Result := Root.OnBeforeDrawCell;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeDrawEmptySpace: TTMSFNCDataGridBeforeDrawEmptySpaceEvent;
begin
  Result := Root.OnBeforeDrawEmptySpace;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeLookup: TTMSFNCDataGridBeforeLookupEvent;
begin
  Result := Root.OnBeforeLookup;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeOpenInplaceEditor: TTMSFNCDataGridBeforeOpenInplaceEditorEvent;
begin
  Result := Root.OnBeforeOpenInplaceEditor;
end;

function TTMSFNCCustomDataGrid.GetOnBeforePasteFromClipboard: TTMSFNCDataGridBeforeClipboardEvent;
begin
  Result := Root.OnBeforePasteFromClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeShowFilter: TTMSFNCDataGridBeforeShowFilterEvent;
begin
  Result := Root.OnBeforeShowFilter;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeShowFindDialog: TTMSFNCDataGridBeforeShowFindDialogEvent;
begin
  Result := Root.OnBeforeShowFindDialog;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeShowReplaceDialog: TTMSFNCDataGridBeforeShowReplaceDialogEvent;
begin
  Result := Root.OnBeforeShowReplaceDialog;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeSortColumn: TTMSFNCDataGridBeforeSortColumnEvent;
begin
  Result := Root.OnBeforeSortColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanAppendColumn: TTMSFNCDataGridCanAppendColumnEvent;
begin
  Result := Root.OnCanAppendColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanAppendRow: TTMSFNCDataGridCanAppendRowEvent;
begin
  Result := Root.OnCanAppendRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanDeleteColumn: TTMSFNCDataGridCanDeleteColumnEvent;
begin
  Result := Root.OnCanDeleteColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanDeleteRow: TTMSFNCDataGridCanDeleteRowEvent;
begin
  Result := Root.OnCanDeleteRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanDragColumn: TTMSFNCDataGridCanDragColumnEvent;
begin
  Result := Root.OnCanDragColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanDragRow: TTMSFNCDataGridCanDragRowEvent;
begin
  Result := Root.OnCanDragRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanDropColumn: TTMSFNCDataGridCanDropColumnEvent;
begin
  Result := Root.OnCanDropColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanDropRow: TTMSFNCDataGridCanDropRowEvent;
begin
  Result := Root.OnCanDropRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanEditCell: TTMSFNCDataGridCanEditCellEvent;
begin
  Result := Root.OnCanEditCell;
end;

function TTMSFNCCustomDataGrid.GetOnCanInsertColumn: TTMSFNCDataGridCanInsertColumnEvent;
begin
  Result := Root.OnCanInsertColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanInsertRow: TTMSFNCDataGridCanInsertRowEvent;
begin
  Result := Root.OnCanInsertRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanSizeColumn: TTMSFNCDataGridCanSizeColumnEvent;
begin
  Result := Root.OnCanSizeColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanSizeRow: TTMSFNCDataGridCanSizeRowEvent;
begin
  Result := Root.OnCanSizeRow;
end;

function TTMSFNCCustomDataGrid.GetOnCellAnchorClick: TTMSFNCDataGridCellAnchorClickEvent;
begin
  Result := Root.OnCellAnchorClick;
end;

function TTMSFNCCustomDataGrid.GetOnCellButtonClick: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellButtonClick;
end;

function TTMSFNCCustomDataGrid.GetOnCellCheckBoxChange: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellCheckBoxChange;
end;

function TTMSFNCCustomDataGrid.GetOnCellClick: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellClick;
end;

function TTMSFNCCustomDataGrid.GetOnCellDblClick: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellDblClick;
end;

function TTMSFNCCustomDataGrid.GetOnCellEditGetData: TTMSFNCDataGridCellEditGetDataEvent;
begin
  Result := Root.OnCellEditGetData;
end;

function TTMSFNCCustomDataGrid.GetOnCellEditSetData: TTMSFNCDataGridCellEditSetDataEvent;
begin
  Result := Root.OnCellEditSetData;
end;

function TTMSFNCCustomDataGrid.GetOnCellEditValidateData: TTMSFNCDataGridCellEditValidateDataEvent;
begin
  Result := Root.OnCellEditValidateData;
end;

function TTMSFNCCustomDataGrid.GetOnCellBeforeShowPopupMenu: TTMSFNCDataGridCellBeforeShowPopupMenuEvent;
begin
  Result := Root.OnCellBeforeShowPopupMenu;
end;

function TTMSFNCCustomDataGrid.GetOnCellBeforeNodeExpand: TTMSFNCDataGridCellBeforeNodeExpandEvent;
begin
  Result := Root.OnCellBeforeNodeExpand;
end;

function TTMSFNCCustomDataGrid.GetOnCellBeforeNodeCollapse: TTMSFNCDataGridCellBeforeNodeCollapseEvent;
begin
  Result := Root.OnCellBeforeNodeCollapse;
end;

function TTMSFNCCustomDataGrid.GetOnCellAfterNodeExpand: TTMSFNCDataGridCellAfterNodeExpandEvent;
begin
  Result := Root.OnCellAfterNodeExpand;
end;

function TTMSFNCCustomDataGrid.GetOnCellAfterNodeCollapse: TTMSFNCDataGridCellAfterNodeCollapseEvent;
begin
  Result := Root.OnCellAfterNodeCollapse;
end;

function TTMSFNCCustomDataGrid.GetOnCellRadioButtonChange: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellRadioButtonChange;
end;

function TTMSFNCCustomDataGrid.GetOnCellRightClick: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellRightClick;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeApplyFilter: TTMSFNCDataGridBeforeApplyFilterEvent;
begin
  Result := Root.OnBeforeApplyFilter;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeCloseInplaceEditor: TTMSFNCDataGridBeforeCloseInplaceEditorEvent;
begin
  Result := Root.OnBeforeCloseInplaceEditor;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeCopyToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
begin
  Result := Root.OnBeforeCopyToClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeCreateFilterEditor: TTMSFNCDataGridBeforeCreateFilterEditorEvent;
begin
  Result := Root.OnBeforeCreateFilterEditor;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeCutToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
begin
  Result := Root.OnBeforeCutToClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnCustomColumnCalculation: TTMSFNCDataGridDataCustomColumnCalculationEvent;
begin
  Result := Root.OnCustomColumnCalculation;
end;

function TTMSFNCCustomDataGrid.GetOnCustomRowCalculation: TTMSFNCDataGridDataCustomRowCalculationEvent;
begin
  Result := Root.OnCustomRowCalculation;
end;

function TTMSFNCCustomDataGrid.GetOnColumnDropped: TTMSFNCDataGridColumnDroppedEvent;
begin
  Result := Root.OnColumnDropped;
end;

function TTMSFNCCustomDataGrid.GetOnColumnSized: TTMSFNCDataGridColumnSizedEvent;
begin
  Result := Root.OnColumnSized;
end;

function TTMSFNCCustomDataGrid.GetOnCreateCustomFilterEditor: TTMSFNCDataGridCreateCustomFilterEditorEvent;
begin
  Result := Root.OnCreateCustomFilterEditor;
end;

function TTMSFNCCustomDataGrid.GetOnCustomCompare: TTMSFNCDataGridDataCustomCompareEvent;
begin
  Result := Root.OnCustomCompare;
end;

function TTMSFNCCustomDataGrid.GetOnDeleteColumn: TTMSFNCDataGridDeleteColumnEvent;
begin
  Result := Root.OnDeleteColumn;
end;

function TTMSFNCCustomDataGrid.GetOnDeleteRow: TTMSFNCDataGridDeleteRowEvent;
begin
  Result := Root.OnDeleteRow;
end;

function TTMSFNCCustomDataGrid.GetOnGetColumnCalculationOptions: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent;
begin
  Result := Root.OnGetColumnCalculationOptions;
end;

function TTMSFNCCustomDataGrid.GetOnGetRowCalculationOptions: TTMSFNCDataGridDataGetRowCalculationOptionsEvent;
begin
  Result := Root.OnGetRowCalculationOptions;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellClass: TTMSFNCDataGridGetCellClassEvent;
begin
  Result := Root.OnGetCellClass;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellData: TTMSFNCDataGridDataGetCellDataEvent;
begin
  Result := Root.OnGetCellData;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellDataProperties: TTMSFNCDataGridDataGetCellDataPropertiesEvent;
begin
  Result := Root.OnGetCellDataProperties;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellDisplayValue: TTMSFNCDataGridDataGetCellDisplayValueEvent;
begin
  Result := Root.OnGetCellDisplayValue;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellFormatting: TTMSFNCDataGridDataGetCellFormattingEvent;
begin
  Result := Root.OnGetCellFormatting;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellHint: TTMSFNCDataGridGetCellHintEvent;
begin
  Result := Root.OnGetCellHint;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellLayout: TTMSFNCDataGridGetCellLayoutEvent;
begin
  Result := Root.OnGetCellLayout;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellLayoutHasPriority: TTMSFNCDataGridGetCellLayoutHasPriorityEvent;
begin
  Result := Root.OnGetCellLayoutHasPriority;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellProperties: TTMSFNCDataGridGetCellPropertiesEvent;
begin
  Result := Root.OnGetCellProperties;
end;

function TTMSFNCCustomDataGrid.GetOnGetCustomGroup: TTMSFNCDataGridDataGetCustomGroupEvent;
begin
  Result := Root.OnGetCustomGroup;
end;

function TTMSFNCCustomDataGrid.GetOnGetGroupOptions: TTMSFNCDataGridDataGetGroupOptionsEvent;
begin
  Result := Root.OnGetGroupOptions;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorClass: TTMSFNCDataGridGetInplaceEditorClassEvent;
begin
  Result := Root.OnGetInplaceEditorClass;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorProperties: TTMSFNCDataGridGetInplaceEditorPropertiesEvent;
begin
  Result := Root.OnGetInplaceEditorProperties;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorRect: TTMSFNCDataGridGetInplaceEditorRectEvent;
begin
  Result := Root.OnGetInplaceEditorRect;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorType: TTMSFNCDataGridGetInplaceEditorTypeEvent;
begin
  Result := Root.OnGetInplaceEditorType;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorTarget: TTMSFNCDataGridGetInplaceEditorTargetEvent;
begin
  Result := Root.OnGetInplaceEditorTarget;
end;

function TTMSFNCCustomDataGrid.GetOnGetSortOptions: TTMSFNCDataGridDataGetSortOptionsEvent;
begin
  Result := Root.OnGetSortOptions;
end;

function TTMSFNCCustomDataGrid.GetOnHScroll: TTMSFNCDataGridScrollEvent;
begin
  Result := Root.OnHScroll;
end;

function TTMSFNCCustomDataGrid.GetOnInsertColumn: TTMSFNCDataGridInsertColumnEvent;
begin
  Result := Root.OnInsertColumn;
end;

function TTMSFNCCustomDataGrid.GetOnInsertRow: TTMSFNCDataGridInsertRowEvent;
begin
  Result := Root.OnInsertRow;
end;

function TTMSFNCCustomDataGrid.GetOnLoadCellData: TTMSFNCDataGridDataCellDataEvent;
begin
  Result := Root.OnLoadCellData;
end;

function TTMSFNCCustomDataGrid.GetOnLogMessage: TTMSFNCDataGridLogMessageEvent;
begin
  Result := Root.OnLogMessage;
end;

function TTMSFNCCustomDataGrid.GetOnNeedFilterDropDownData: TTMSFNCDataGridNeedFilterDropDownData;
begin
  Result := Root.OnNeedFilterDropDownData;
end;

function TTMSFNCCustomDataGrid.GetOnRowDropped: TTMSFNCDataGridRowDroppedEvent;
begin
  Result := Root.OnRowDropped;
end;

function TTMSFNCCustomDataGrid.GetOnRowExpand: TTMSFNCDataGridDataRowExpandEvent;
begin
  Result := Root.OnRowExpand;
end;

function TTMSFNCCustomDataGrid.GetOnRowCollapse: TTMSFNCDataGridDataRowCollapseEvent;
begin
  Result := Root.OnRowCollapse;
end;

function TTMSFNCCustomDataGrid.GetOnRowSized: TTMSFNCDataGridRowSizedEvent;
begin
  Result := Root.OnRowSized;
end;

function TTMSFNCCustomDataGrid.GetOnSaveCellData: TTMSFNCDataGridDataCellDataEvent;
begin
  Result := Root.OnSaveCellData;
end;

function TTMSFNCCustomDataGrid.GetOnSelectCell: TTMSFNCDataGridSelectCellEvent;
begin
  Result := Root.OnSelectCell;
end;

function TTMSFNCCustomDataGrid.GetOnSelectingCell: TTMSFNCDataGridSelectingCellEvent;
begin
  Result := Root.OnSelectingCell;
end;

function TTMSFNCCustomDataGrid.GetOnSizeColumn: TTMSFNCDataGridSizeColumnEvent;
begin
  Result := Root.OnSizeColumn;
end;

function TTMSFNCCustomDataGrid.GetOnSizeRow: TTMSFNCDataGridSizeRowEvent;
begin
  Result := Root.OnSizeRow;
end;

function TTMSFNCCustomDataGrid.GetOnVScroll: TTMSFNCDataGridScrollEvent;
begin
  Result := Root.OnVScroll;
end;

function TTMSFNCCustomDataGrid.GetOptions: TTMSFNCDataGridOptions;
begin
  Result := Root.Options;
end;

function TTMSFNCCustomDataGrid.GetPageCount: Integer;
begin
  Result := Root.PageCount;
end;

function TTMSFNCCustomDataGrid.GetPageIndex: Integer;
begin
  Result := Root.PageIndex;
end;

function TTMSFNCCustomDataGrid.GetParentRow(ARow: Integer): Integer;
begin
  Result := Root.GetParentRow(ARow);
end;

function TTMSFNCCustomDataGrid.GetReadOnlys(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.ReadOnlys[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetRightColumn: Integer;
begin
  Result := Root.RightColumn;
end;

function TTMSFNCCustomDataGrid.GetRootRow(ARow: Integer): Integer;
begin
  Result := Root.GetRootRow(ARow);
end;

function TTMSFNCCustomDataGrid.GetRowCount: Integer;
begin
  Result := Root.RowCount;
end;

function TTMSFNCCustomDataGrid.GetRowHeights(ARow: Integer): Double;
begin
  Result := Root.RowHeights[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowNodeChildCount(ARow: Integer): Integer;
begin
  Result := Root.RowNodeChildCount[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowNodeLevel(ARow: Integer): Integer;
begin
  Result := Root.RowNodeLevel[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowNodeState(
  ARow: Integer): TTMSFNCDataGridRowNodeState;
begin
  Result := Root.RowNodeState[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowOrder(ARow: Integer): Integer;
begin
  Result := Root.RowOrders[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowPos(ARow: Integer): Double;
begin
  Result := Root.RowPositions[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowReverseOrder(ARow: Integer): Integer;
begin
  Result := Root.RowReverseOrders[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowText(ARow: Integer): string;
begin
  Result := Root.RowText[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowTypes(ARow: Integer): TTMSFNCDataGridRowType;
begin
  Result := Root.RowTypes[ARow];
end;

function TTMSFNCCustomDataGrid.GetScrollMode: TTMSFNCDataGridScrollMode;
begin
  Result := Root.ScrollMode;
end;

function TTMSFNCCustomDataGrid.GetScrollUpdate: TTMSFNCDataGridScrollUpdate;
begin
  Result := Root.ScrollUpdate;
end;

function TTMSFNCCustomDataGrid.GetSelectedCellCount: Integer;
begin
  Result := Root.SelectedCellCount;
end;

function TTMSFNCCustomDataGrid.GetSelectedCells(AColumn,
  ARow: Integer): Boolean;
begin
  Result := Root.SelectedCells[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetSelectedColumnCount: Integer;
begin
  Result := Root.SelectedColumnCount;
end;

function TTMSFNCCustomDataGrid.GetSelectedColumns(AColumn: Integer): Boolean;
begin
  Result := Root.SelectedColumns[AColumn];
end;

function TTMSFNCCustomDataGrid.GetSelectedRowCount: Integer;
begin
  Result := Root.SelectedRowCount;
end;

function TTMSFNCCustomDataGrid.GetSelectedRows(ARow: Integer): Boolean;
begin
  Result := Root.SelectedRows[ARow];
end;

function TTMSFNCCustomDataGrid.GetSelection: TTMSFNCDataGridCellCoordRange;
begin
  Result := Root.Selection;
end;

function TTMSFNCCustomDataGrid.GetSortIndexDictionary: TTMSFNCDataGridSortIndexDictionary;
begin
  Result := Root.SortIndexDictionary;
end;

function TTMSFNCCustomDataGrid.GetStartColumn: Integer;
begin
  Result := Root.StartColumn;
end;

function TTMSFNCCustomDataGrid.GetStartRow: Integer;
begin
  Result := Root.StartRow;
end;

function TTMSFNCCustomDataGrid.GetStates(AColumn,
  ARow: Integer): TTMSFNCDataGridCellState;
begin
  Result := Root.States[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetStopColumn: Integer;
begin
  Result := Root.StopColumn;
end;

function TTMSFNCCustomDataGrid.GetStopRow: Integer;
begin
  Result := Root.StopRow;
end;

function TTMSFNCCustomDataGrid.GetStoredCells(AColumn,
  ARow: Integer): TTMSFNCDataGridCellValue;
begin
  Result := Root.StoredCells[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetStretchScrollBars: Boolean;
begin
  Result := Root.StretchScrollBars;
end;

function TTMSFNCCustomDataGrid.GetStrings(AColumn, ARow: Integer): string;
begin
  Result := Root.Strings[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetStrippedCells(AColumn, ARow: Integer): string;
begin
  Result := Root.StrippedCells[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetTextAngles(AColumn, ARow: Integer): Double;
begin
  Result := Root.TextAngles[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetTopRow: Integer;
begin
  Result := Root.TopRow;
end;

function TTMSFNCCustomDataGrid.GetTotalColumnWidth: Double;
begin
  Result := Root.TotalColumnWidth;
end;

function TTMSFNCCustomDataGrid.GetTotalFixedBottomRowHeight: Double;
begin
  Result := Root.GetTotalFixedBottomRowHeight;
end;

function TTMSFNCCustomDataGrid.GetTotalFixedColumnWidth: Double;
begin
  Result := Root.GetTotalFixedColumnWidth;
end;

function TTMSFNCCustomDataGrid.GetTotalFixedRightColumnWidth: Double;
begin
  Result := Root.GetTotalFixedRightColumnWidth;
end;

function TTMSFNCCustomDataGrid.GetTotalFixedRowHeight: Double;
begin
  Result := Root.GetTotalFixedRowHeight;
end;

function TTMSFNCCustomDataGrid.GetTotalRowHeight: Double;
begin
  Result := Root.TotalRowHeight;
end;

function TTMSFNCCustomDataGrid.GetVisibleColumnCount: Integer;
begin
  Result := Root.VisibleColumnCount;
end;

function TTMSFNCCustomDataGrid.GetVisibleRowCount: Integer;
begin
  Result := Root.VisibleRowCount;
end;

function TTMSFNCCustomDataGrid.GetVMax: Single;
begin
  Result := Root.GetVMax;
end;

function TTMSFNCCustomDataGrid.GetVScrollValue: Single;
begin
  Result := Root.GetVScrollValue;
end;

function TTMSFNCCustomDataGrid.GetVViewPortSize: Single;
begin
  Result := Root.GetVViewPortSize;
end;

procedure TTMSFNCCustomDataGrid.GoToCell(ACell: TTMSFNCDataGridCellCoord; ASelect: Boolean = True);
begin
  Root.GoToCell(ACell, ASelect);
end;

procedure TTMSFNCCustomDataGrid.GoToPage(APageIndex: Integer);
begin
  Root.GoToPage(APageIndex);
end;

procedure TTMSFNCCustomDataGrid.Group(AColumn: Integer);
begin
  Root.Group(AColumn);
end;

procedure TTMSFNCCustomDataGrid.Group(AGroupInfo: TTMSFNCDataGridDataGroupInfo;
  ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.Group(AGroupInfo, ACustomGroupCallback, ACustomCompareCallback, AGroupOptionsCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.Group(AColumns, ASortColumns: TArray<Integer>;
  ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.Group(AColumns, ASortColumns, ACustomGroupCallback, ACustomCompareCallback, AGroupOptionsCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupAverage(AColumn: Integer);
begin
  Root.GroupAverage(AColumn);
end;

procedure TTMSFNCCustomDataGrid.GroupAverage(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupAverage(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCalculation(AColumns: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.GroupCalculation(AColumns, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCalculation(AColumn: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.GroupCalculation(AColumn, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCount(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupCount(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCount(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupCount(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCustomCalculation(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.GroupCustomCalculation(AColumn, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCustomCalculation(
  AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.GroupCustomCalculation(AColumns, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupDistinct(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupDistinct(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupDistinct(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupDistinct(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupMax(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupMax(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupMax(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupMax(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupMin(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupMin(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupMin(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupMin(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupSort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection);
begin
  Root.GroupSort(AColumn, ADirection);
end;

procedure TTMSFNCCustomDataGrid.GroupSort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.GroupSort(AColumns, ADirections, ACustomCompareCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupStandardDeviation(
  AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupStandardDeviation(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupStandardDeviation(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupStandardDeviation(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupSum(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupSum(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupSum(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupSum(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.SetBooleans(AColumn, ARow: Integer;
  const Value: Boolean);
begin
  Root.Booleans[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetCellAppearance(
  const Value: TTMSFNCDataGridCellAppearance);
begin
  Root.CellAppearance.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetCellPopupMenu(const Value: TPopupMenu);
begin
  Root.CellPopupMenu := Value;
end;

procedure TTMSFNCCustomDataGrid.SetCells(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellValue);
begin
  Root.Cells[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetCheckBox(AColumn, ARow: Integer;
  AValue: Boolean);
begin
  Root.SetCheckBox(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetCheckBox(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean);
begin
  Root.SetCheckBox(ACell, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
begin
  Root.SetCheckBoxColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetDataCheckBox(AColumn, ARow: Integer);
begin
  Root.SetDataCheckBox(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.SetDataCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.SetDataCheckBox(ACell);
end;

procedure TTMSFNCCustomDataGrid.SetDataCheckBoxColumn(AColumn: Integer);
begin
  Root.SetDataCheckBoxColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.SetDataProgressBar(AColumn, ARow: Integer);
begin
  Root.SetDataProgressBar(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.SetDataProgressBar(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.SetDataProgressBar(ACell);
end;

procedure TTMSFNCCustomDataGrid.SetDataProgressBarColumn(AColumn: Integer);
begin
  Root.SetDataProgressBarColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.SetPageIndex(const Value: Integer);
begin
  Root.PageIndex := Value;
end;

procedure TTMSFNCCustomDataGrid.SetProgressBar(AColumn, ARow: Integer;
  AValue: Integer = 0);
begin
  Root.SetProgressBar(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetProgressBar(ACell: TTMSFNCDataGridCellCoord;
  AValue: Integer = 0);
begin
  Root.SetProgressBar(ACell, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
begin
  Root.SetProgressBarColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetButton(AColumn, ARow: Integer; AText: string = '');
begin
  Root.SetButton(AColumn, ARow, AText);
end;

procedure TTMSFNCCustomDataGrid.SetButton(ACell: TTMSFNCDataGridCellCoord; AText: string = '');
begin
  Root.SetButton(ACell, AText);
end;

procedure TTMSFNCCustomDataGrid.SetButtonColumn(AColumn: Integer; AText: string = '');
begin
  Root.SetButtonColumn(AColumn, AText);
end;

procedure TTMSFNCCustomDataGrid.SetRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetRadioButton(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetRadioButtonColumn(AColumn, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetDataRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetDataRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetDataRadioButton(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetDataRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetDataRadioButtonColumn(AColumn, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetColors(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsColor);
begin
  Root.Colors[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetColumnCalculation(AColumn: Integer; AName: string;
  const Value: TTMSFNCDataGridDataColumnCalculationArray);
begin
  Root.ColumnCalculations[AColumn, AName] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowCalculation(ARow: Integer; AName: string;
  const Value: TTMSFNCDataGridDataRowCalculationArray);
begin
  Root.RowCalculations[ARow, AName] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetColumnCount(const Value: Integer);
begin
  Root.ColumnCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetColumns(const Value: TTMSFNCDataGridColumns);
begin
  Root.Columns.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetColumnWidths(AColumn: Integer;
  const Value: Double);
begin
  Root.ColumnWidths[AColumn] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetComments(AColumn, ARow: Integer;
  const Value: string);
begin
  Root.Comments[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetHints(AColumn, ARow: Integer;
  const Value: string);
begin
  Root.Hints[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControlAlign);
begin
  Root.ControlAligns[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlPositions(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControlPosition);
begin
  Root.ControlPositions[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlHeights(AColumn, ARow: Integer;
  const Value: Integer);
begin
  Root.ControlHeights[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControls(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControl);
begin
  Root.Controls[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlTexts(AColumn, ARow: Integer;
  const Value: string);
begin
  Root.ControlTexts[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlWidths(AColumn, ARow: Integer;
  const Value: Integer);
begin
  Root.ControlWidths[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetCustomScrollBars(const Value: Boolean);
begin
  Root.CustomScrollBars := Value;
end;

procedure TTMSFNCCustomDataGrid.SetDefaultColumnWidth(const Value: Double);
begin
  Root.DefaultColumnWidth := Value;
end;

procedure TTMSFNCCustomDataGrid.SetDefaultRowHeight(const Value: Double);
begin
  Root.DefaultRowHeight := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFilterAppearance(const Value: TTMSFNCDataGridFilterAppearance);
begin
  Root.FilterAppearance := Value;
end;

procedure TTMSFNCCustomDataGrid.SetDragAppearance(const Value: TTMSFNCDataGridDragAppearance);
begin
  Root.DragAppearance := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFixedBottomRowCount(const Value: Integer);
begin
  Root.FixedBottomRowCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFixedColumnCount(const Value: Integer);
begin
  Root.FixedColumnCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFixedRightColumnCount(const Value: Integer);
begin
  Root.FixedRightColumnCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFixedRowCount(const Value: Integer);
begin
  Root.FixedRowCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFloats(AColumn, ARow: Integer;
  const Value: Double);
begin
  Root.Floats[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFreezeColumnCount(const Value: Integer);
begin
  Root.FreezeColumnCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFreezeRowCount(const Value: Integer);
begin
  Root.FreezeRowCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetManagedObjects(AColumn, ARow: Integer;
  const Value: TObject);
begin
  Root.ManagedObjects[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetMergedColumns(AColumn: Integer;
  const Value: Boolean);
begin
  Root.MergedColumns[AColumn] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetMergedRows(ARow: Integer;
  const Value: Boolean);
begin
  Root.MergedRows[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetNodeRow(ARow, AChildCount, ALevel: Integer;
  AMerge: Boolean);
begin
  Root.SetNodeRow(ARow, AChildCount, ALevel, AMerge);
end;

procedure TTMSFNCCustomDataGrid.SetObjects(AColumn, ARow: Integer;
  const Value: TObject);
begin
  Root.Objects[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterApplyFilter(
  const Value: TTMSFNCDataGridAfterApplyFilterEvent);
begin
  Root.OnAfterApplyFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterCloseInplaceEditor(
  const Value: TTMSFNCDataGridAfterCloseInplaceEditorEvent);
begin
  Root.OnAfterCloseInplaceEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterCopyToClipboard(
  const Value: TTMSFNCDataGridAfterClipboardEvent);
begin
  Root.OnAfterCopyToClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterCreateFilterEditor(
  const Value: TTMSFNCDataGridAfterCreateFilterEditorEvent);
begin
  Root.OnAfterCreateFilterEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterCutToClipboard(
  const Value: TTMSFNCDataGridAfterClipboardEvent);
begin
  Root.OnAfterCutToClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterDrawCell(
  const Value: TTMSFNCDataGridAfterDrawCellEvent);
begin
  Root.OnAfterDrawCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterDrawEmptySpace(
  const Value: TTMSFNCDataGridAfterDrawEmptySpaceEvent);
begin
  Root.OnAfterDrawEmptySpace := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterDrawGrid(
  const Value: TTMSFNCDataGridAfterDrawGridEvent);
begin
  Root.OnAfterDrawGrid := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterLookup(
  const Value: TTMSFNCDataGridAfterLookupEvent);
begin
  Root.OnAfterLookup := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterOpenInplaceEditor(
  const Value: TTMSFNCDataGridAfterOpenInplaceEditorEvent);
begin
  Root.OnAfterOpenInplaceEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterPasteFromClipboard(
  const Value: TTMSFNCDataGridAfterClipboardEvent);
begin
  Root.OnAfterPasteFromClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterShowFilter(
  const Value: TTMSFNCDataGridAfterShowFilterEvent);
begin
  Root.OnAfterShowFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterShowFindDialog(
  const Value: TTMSFNCDataGridAfterShowFindDialogEvent);
begin
  Root.OnAfterShowFindDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterShowReplaceDialog(
  const Value: TTMSFNCDataGridAfterShowReplaceDialogEvent);
begin
  Root.OnAfterShowReplaceDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterSortColumn(
  const Value: TTMSFNCDataGridAfterSortColumnEvent);
begin
  Root.OnAfterSortColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAppendColumn(
  const Value: TTMSFNCDataGridAppendColumnEvent);
begin
  Root.OnAppendColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAppendRow(
  const Value: TTMSFNCDataGridAppendRowEvent);
begin
  Root.OnAppendRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeDrawCell(
  const Value: TTMSFNCDataGridBeforeDrawCellEvent);
begin
  Root.OnBeforeDrawCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeDrawEmptySpace(
  const Value: TTMSFNCDataGridBeforeDrawEmptySpaceEvent);
begin
  Root.OnBeforeDrawEmptySpace := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeLookup(
  const Value: TTMSFNCDataGridBeforeLookupEvent);
begin
  Root.OnBeforeLookup := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeOpenInplaceEditor(
  const Value: TTMSFNCDataGridBeforeOpenInplaceEditorEvent);
begin
  Root.OnBeforeOpenInplaceEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforePasteFromClipboard(
  const Value: TTMSFNCDataGridBeforeClipboardEvent);
begin
  Root.OnBeforePasteFromClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeShowFilter(
  const Value: TTMSFNCDataGridBeforeShowFilterEvent);
begin
  Root.OnBeforeShowFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeShowFindDialog(
  const Value: TTMSFNCDataGridBeforeShowFindDialogEvent);
begin
  Root.OnBeforeShowFindDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeShowReplaceDialog(
  const Value: TTMSFNCDataGridBeforeShowReplaceDialogEvent);
begin
  Root.OnBeforeShowReplaceDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeSortColumn(
  const Value: TTMSFNCDataGridBeforeSortColumnEvent);
begin
  Root.OnBeforeSortColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanAppendColumn(
  const Value: TTMSFNCDataGridCanAppendColumnEvent);
begin
  Root.OnCanAppendColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanAppendRow(
  const Value: TTMSFNCDataGridCanAppendRowEvent);
begin
  Root.OnCanAppendRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDeleteColumn(
  const Value: TTMSFNCDataGridCanDeleteColumnEvent);
begin
  Root.OnCanDeleteColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDeleteRow(
  const Value: TTMSFNCDataGridCanDeleteRowEvent);
begin
  Root.OnCanDeleteRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDragColumn(
  const Value: TTMSFNCDataGridCanDragColumnEvent);
begin
  Root.OnCanDragColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDragRow(
  const Value: TTMSFNCDataGridCanDragRowEvent);
begin
  Root.OnCanDragRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDropColumn(
  const Value: TTMSFNCDataGridCanDropColumnEvent);
begin
  Root.OnCanDropColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDropRow(
  const Value: TTMSFNCDataGridCanDropRowEvent);
begin
  Root.OnCanDropRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanEditCell(
  const Value: TTMSFNCDataGridCanEditCellEvent);
begin
  Root.OnCanEditCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanInsertColumn(
  const Value: TTMSFNCDataGridCanInsertColumnEvent);
begin
  Root.OnCanInsertColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanInsertRow(
  const Value: TTMSFNCDataGridCanInsertRowEvent);
begin
  Root.OnCanInsertRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanSizeColumn(
  const Value: TTMSFNCDataGridCanSizeColumnEvent);
begin
  Root.OnCanSizeColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanSizeRow(
  const Value: TTMSFNCDataGridCanSizeRowEvent);
begin
  Root.OnCanSizeRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellAnchorClick(
  const Value: TTMSFNCDataGridCellAnchorClickEvent);
begin
  Root.OnCellAnchorClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellButtonClick(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellButtonClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellCheckBoxChange(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellCheckBoxChange := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellClick(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellDblClick(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellDblClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellEditGetData(
  const Value: TTMSFNCDataGridCellEditGetDataEvent);
begin
  Root.OnCellEditGetData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellEditSetData(
  const Value: TTMSFNCDataGridCellEditSetDataEvent);
begin
  Root.OnCellEditSetData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellEditValidateData(
  const Value: TTMSFNCDataGridCellEditValidateDataEvent);
begin
  Root.OnCellEditValidateData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellBeforeShowPopupMenu(
  const Value: TTMSFNCDataGridCellBeforeShowPopupMenuEvent);
begin
  Root.OnCellBeforeShowPopupMenu := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellBeforeNodeExpand(
  const Value: TTMSFNCDataGridCellBeforeNodeExpandEvent);
begin
  Root.OnCellBeforeNodeExpand := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellBeforeNodeCollapse(
  const Value: TTMSFNCDataGridCellBeforeNodeCollapseEvent);
begin
  Root.OnCellBeforeNodeCollapse := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellAfterNodeExpand(
  const Value: TTMSFNCDataGridCellAfterNodeExpandEvent);
begin
  Root.OnCellAfterNodeExpand := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellAfterNodeCollapse(
  const Value: TTMSFNCDataGridCellAfterNodeCollapseEvent);
begin
  Root.OnCellAfterNodeCollapse := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellRadioButtonChange(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellRadioButtonChange := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellRightClick(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellRightClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeApplyFilter(
  const Value: TTMSFNCDataGridBeforeApplyFilterEvent);
begin
  Root.OnBeforeApplyFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeCloseInplaceEditor(
  const Value: TTMSFNCDataGridBeforeCloseInplaceEditorEvent);
begin
  Root.OnBeforeCloseInplaceEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeCopyToClipboard(
  const Value: TTMSFNCDataGridBeforeClipboardEvent);
begin
  Root.OnBeforeCopyToClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeCreateFilterEditor(
  const Value: TTMSFNCDataGridBeforeCreateFilterEditorEvent);
begin
  Root.OnBeforeCreateFilterEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeCutToClipboard(
  const Value: TTMSFNCDataGridBeforeClipboardEvent);
begin
  Root.OnBeforeCutToClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCustomColumnCalculation(
  const Value: TTMSFNCDataGridDataCustomColumnCalculationEvent);
begin
  Root.OnCustomColumnCalculation := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCustomRowCalculation(
  const Value: TTMSFNCDataGridDataCustomRowCalculationEvent);
begin
  Root.OnCustomRowCalculation := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnColumnDropped(
  const Value: TTMSFNCDataGridColumnDroppedEvent);
begin
  Root.OnColumnDropped := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnColumnSized(
  const Value: TTMSFNCDataGridColumnSizedEvent);
begin
  Root.OnColumnSized := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCreateCustomFilterEditor(
  const Value: TTMSFNCDataGridCreateCustomFilterEditorEvent);
begin
  Root.OnCreateCustomFilterEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCustomCompare(
  const Value: TTMSFNCDataGridDataCustomCompareEvent);
begin
  Root.OnCustomCompare := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnDeleteColumn(
  const Value: TTMSFNCDataGridDeleteColumnEvent);
begin
  Root.OnDeleteColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnDeleteRow(
  const Value: TTMSFNCDataGridDeleteRowEvent);
begin
  Root.OnDeleteRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetColumnCalculationOptions(
  const Value: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent);
begin
  Root.OnGetColumnCalculationOptions := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetRowCalculationOptions(
  const Value: TTMSFNCDataGridDataGetRowCalculationOptionsEvent);
begin
  Root.OnGetRowCalculationOptions := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellClass(
  const Value: TTMSFNCDataGridGetCellClassEvent);
begin
  Root.OnGetCellClass := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellData(
  const Value: TTMSFNCDataGridDataGetCellDataEvent);
begin
  Root.OnGetCellData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellDataProperties(
  const Value: TTMSFNCDataGridDataGetCellDataPropertiesEvent);
begin
  Root.OnGetCellDataProperties := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellDisplayValue(
  const Value: TTMSFNCDataGridDataGetCellDisplayValueEvent);
begin
  Root.OnGetCellDisplayValue := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellFormatting(
  const Value: TTMSFNCDataGridDataGetCellFormattingEvent);
begin
  Root.OnGetCellFormatting := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellHint(
  const Value: TTMSFNCDataGridGetCellHintEvent);
begin
  Root.OnGetCellHint := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellLayout(
  const Value: TTMSFNCDataGridGetCellLayoutEvent);
begin
  Root.OnGetCellLayout := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellLayoutHasPriority(
  const Value: TTMSFNCDataGridGetCellLayoutHasPriorityEvent);
begin
  Root.OnGetCellLayoutHasPriority := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellProperties(
  const Value: TTMSFNCDataGridGetCellPropertiesEvent);
begin
  Root.OnGetCellProperties := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCustomGroup(
  const Value: TTMSFNCDataGridDataGetCustomGroupEvent);
begin
  Root.OnGetCustomGroup := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetGroupOptions(
  const Value: TTMSFNCDataGridDataGetGroupOptionsEvent);
begin
  Root.OnGetGroupOptions := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorClass(
  const Value: TTMSFNCDataGridGetInplaceEditorClassEvent);
begin
  Root.OnGetInplaceEditorClass := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorType(
  const Value: TTMSFNCDataGridGetInplaceEditorTypeEvent);
begin
  Root.OnGetInplaceEditorType := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorTarget(
  const Value: TTMSFNCDataGridGetInplaceEditorTargetEvent);
begin
  Root.OnGetInplaceEditorTarget := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorRect(
  const Value: TTMSFNCDataGridGetInplaceEditorRectEvent);
begin
  Root.OnGetInplaceEditorRect := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorProperties(
  const Value: TTMSFNCDataGridGetInplaceEditorPropertiesEvent);
begin
  Root.OnGetInplaceEditorProperties := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetSortOptions(
  const Value: TTMSFNCDataGridDataGetSortOptionsEvent);
begin
  Root.OnGetSortOptions := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnHScroll(
  const Value: TTMSFNCDataGridScrollEvent);
begin
  Root.OnHScroll := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnInsertColumn(
  const Value: TTMSFNCDataGridInsertColumnEvent);
begin
  Root.OnInsertColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnInsertRow(
  const Value: TTMSFNCDataGridInsertRowEvent);
begin
  Root.OnInsertRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnLoadCellData(
  const Value: TTMSFNCDataGridDataCellDataEvent);
begin
  Root.OnLoadCellData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnLogMessage(
  const Value: TTMSFNCDataGridLogMessageEvent);
begin
  Root.OnLogMessage := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnNeedFilterDropDownData(
  const Value: TTMSFNCDataGridNeedFilterDropDownData);
begin
  Root.OnNeedFilterDropDownData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRowDropped(
  const Value: TTMSFNCDataGridRowDroppedEvent);
begin
  Root.OnRowDropped := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRowCollapse(
  const Value: TTMSFNCDataGridDataRowCollapseEvent);
begin
  Root.OnRowCollapse := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRowExpand(
  const Value: TTMSFNCDataGridDataRowExpandEvent);
begin
  Root.OnRowExpand := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRowSized(
  const Value: TTMSFNCDataGridRowSizedEvent);
begin
  Root.OnRowSized := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSaveCellData(
  const Value: TTMSFNCDataGridDataCellDataEvent);
begin
  Root.OnSaveCellData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSelectCell(
  const Value: TTMSFNCDataGridSelectCellEvent);
begin
  Root.OnSelectCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSelectingCell(
  const Value: TTMSFNCDataGridSelectingCellEvent);
begin
  Root.OnSelectingCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSizeColumn(
  const Value: TTMSFNCDataGridSizeColumnEvent);
begin
  Root.OnSizeColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSizeRow(
  const Value: TTMSFNCDataGridSizeRowEvent);
begin
  Root.OnSizeRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnVScroll(
  const Value: TTMSFNCDataGridScrollEvent);
begin
  Root.OnVScroll := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOptions(const Value: TTMSFNCDataGridOptions);
begin
  Root.Options.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetReadOnlys(AColumn, ARow: Integer;
  const Value: Boolean);
begin
  Root.ReadOnlys[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowCount(const Value: Integer);
begin
  Root.RowCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowHeights(ARow: Integer; const Value: Double);
begin
  Root.RowHeights[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowNodeChildCount(ARow: Integer;
  const Value: Integer);
begin
  Root.RowNodeChildCount[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowNodeLevel(ARow: Integer; const Value: Integer);
begin
  Root.RowNodeLevel[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowNodeState(ARow: Integer;
  const Value: TTMSFNCDataGridRowNodeState);
begin
  Root.RowNodeState[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowText(ARow: Integer; const Value: string);
begin
  Root.RowText[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowTypes(ARow: Integer;
  const Value: TTMSFNCDataGridRowType);
begin
  Root.RowTypes[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetScrollMode(
  const Value: TTMSFNCDataGridScrollMode);
begin
  Root.ScrollMode := Value;
end;

procedure TTMSFNCCustomDataGrid.SetScrollUpdate(
  const Value: TTMSFNCDataGridScrollUpdate);
begin
  Root.ScrollUpdate := Value;
end;

procedure TTMSFNCCustomDataGrid.SetSelectedCells(AColumn, ARow: Integer;
  const Value: Boolean);
begin
  Root.SelectedCells[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetSelectedColumns(AColumn: Integer;
  const Value: Boolean);
begin
  Root.SelectedColumns[AColumn] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetSelectedRows(ARow: Integer;
  const Value: Boolean);
begin
  Root.SelectedRows[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetSelection(
  const Value: TTMSFNCDataGridCellCoordRange);
begin
  Root.Selection := Value;
end;

procedure TTMSFNCCustomDataGrid.SetDesigner(const Value: Boolean);
begin
  if FDesigner <> Value then
  begin
    FDesigner := Value;
    {$IFNDEF WEBLIB}
    if Assigned(FDesignerControl) then
    begin
      FDesignerControl.Visible := Designer;
      if FDesignerControl.Visible then
      begin
        FDesignerControl.Parent := Self;
        FDesignerControl.FRecreate := True;
        if not IsLoading then
        begin
          case FDesignerControl.ControlAlignment of
            caTop, caBottom: Height := Height + FDesignerControl.Height;
            caLeft, caRight: Width := Width + FDesignerControl.Width;
          end;
        end;
      end
      else
      begin
        FDesignerControl.Parent := nil;
        FDesignerControl.FRecreate := True;
        if not IsLoading then
        begin
          case FDesignerControl.ControlAlignment of
            caTop, caBottom: Height := Height - FDesignerControl.Height;
            caLeft, caRight: Width := Width - FDesignerControl.Width;
          end;
        end;
      end;
    end;
    {$ENDIF}
  end;
end;

procedure TTMSFNCCustomDataGrid.SetStretchScrollBars(const Value: Boolean);
begin
  Root.StretchScrollBars := Value;
end;

procedure TTMSFNCCustomDataGrid.SetStrings(AColumn, ARow: Integer; const Value: string);
begin
  Root.Strings[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetTextAngles(AColumn, ARow: Integer;
  const Value: Double);
begin
  Root.TextAngles[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetTopRow(const Value: Integer);
begin
  Root.TopRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetVerticalTextAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsTextAlign);
begin
  Root.VerticalTextAligns[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetVerticalScrollBarVisible(
  const Value: Boolean);
begin
  Root.VerticalScrollBarVisible := Value;
end;

procedure TTMSFNCCustomDataGrid.Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; AFromRow,
  AToRow: Integer; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.Sort(AColumns, ADirections, AFromRow, AToRow, ACustomCompareCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.SortIndexed;
begin
  Root.SortIndexed;
end;

procedure TTMSFNCCustomDataGrid.Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection);
begin
  Root.Sort(AColumn, ADirection);
end;

procedure TTMSFNCCustomDataGrid.Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection; AFromRow, AToRow: Integer);
begin
  Root.Sort(AColumn, ADirection, AFromRow, AToRow);
end;

procedure TTMSFNCCustomDataGrid.Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.Sort(AColumns, ADirections, ACustomCompareCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.SplitCell(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.SplitCell(ACell);
end;

procedure TTMSFNCDataGrid.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClasses([TTMSFNCDataGrid]);
end;

{ TTMSFNCDataGridHeaderFooter }

procedure TTMSFNCDataGridHeaderFooter.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridHeaderFooter then
  begin
    FVisible := (Source as TTMSFNCDataGridHeaderFooter).Visible;
    FSize := (Source as TTMSFNCDataGridHeaderFooter).Size;
    FFill.Assign((Source as TTMSFNCDataGridHeaderFooter).Fill);
    FStroke.Assign((Source as TTMSFNCDataGridHeaderFooter).Stroke);
  end;
end;

constructor TTMSFNCDataGridHeaderFooter.Create;
begin
  FSize := 50;
  FVisible := False;
  FFill := TTMSFNCGraphicsFill.Create;
  FFill.OnChanged := @DoFillChanged;
  FStroke := TTMSFNCGraphicsStroke.Create;
  FStroke.OnChanged := @DoStrokeChanged;
end;

destructor TTMSFNCDataGridHeaderFooter.Destroy;
begin
  FStroke.Free;
  FFill.Free;
  inherited;
end;

procedure TTMSFNCDataGridHeaderFooter.DoFillChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeaderFooter.DoStrokeChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeaderFooter.SetFill(const Value: TTMSFNCGraphicsFill);
begin
  FFill.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderFooter.SetSize(const Value: Integer);
begin
  if FSize <> Value then
  begin
    FSize := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridHeaderFooter.SetStroke(
  const Value: TTMSFNCGraphicsStroke);
begin
  FStroke.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderFooter.SetVisible(const Value: Boolean);
begin
  if FVisible <> Value then
  begin
    FVisible := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

{$IFNDEF WEBLIB}

{ TTMSFNCDataGridDesigner }

procedure TTMSFNCDataGridDesigner.AddControls;
begin
  FClose.Parent := Self;
  FLoadSample.Parent := Self;
  FFilter.Parent := Self;
  FSort.Parent := Self;
  FEdit.Parent := Self;

  FClose.SetBounds(5, 5, FClose.Width, FClose.Height);

  case ControlAlignment of
    caTop, caBottom:
    begin
      FLoadSample.SetBounds(30, 5, FLoadSample.Width, Height - 10);
      FFilter.SetBounds(35 + FLoadSample.Width, 5, FFilter.Width, Height - 10);
      FSort.SetBounds(40 + FLoadSample.Width + FFilter.Width, 5, FSort.Width, Height - 10);
      FEdit.SetBounds(45 + FLoadSample.Width + FFilter.Width + FSort.Width, 5, FEdit.Width, Height - 10);
    end;
    caLeft, caRight:
    begin
      FLoadSample.SetBounds(5, 40, FLoadSample.Width, FLoadSample.Height);
      FFilter.SetBounds(5, 45 + FLoadSample.Height, FFilter.Width, FFilter.Height);
      FSort.SetBounds(5, 50 + FLoadSample.Height + FFilter.Height, FSort.Width, FSort.Height);
      FEdit.SetBounds(5, 55 + FLoadSample.Height + FFilter.Height + FSort.Height, FEdit.Width, FEdit.Height);
    end;
  end;

  if Assigned(FGrid) then
  begin
    {$IFDEF FMXLIB}
    FFilter.IsChecked := FGrid.Options.Filtering.Enabled;
    FSort.IsChecked := FGrid.Options.Sorting.Enabled;
    FEdit.IsChecked := FGrid.Options.Editing.Enabled;
    {$ELSE}
    FFilter.Checked := FGrid.Options.Filtering.Enabled;
    FSort.Checked := FGrid.Options.Sorting.Enabled;
    FEdit.Checked := FGrid.Options.Editing.Enabled;
    {$ENDIF}
  end;
end;

procedure TTMSFNCDataGridDesigner.ApplyStyle;
begin
  inherited;
  FRecreate := True;
  Invalidate;
end;

constructor TTMSFNCDataGridDesigner.Create(AOwner: TComponent);
begin
  inherited;

  if AOwner is TTMSFNCCustomDataGrid then
    FGrid := AOwner as TTMSFNCCustomDataGrid;

  {$IFDEF FMXLIB}
  FDesignInteractive := True;
  FFirstMouseDown := False;
  FFirstMouseUp := False;
  {$ENDIF}

  FLoadSample := TButton.Create(nil);
  FLoadSample.Parent := Self;
  FLoadSample.OnClick := DoLoadSampleClick;
  FLoadSample.Width := 90;

  {$IFDEF FMXLIB}
  FLoadSample.Stored := False;
  FLoadSample.Text := 'Load Sample';
  FLoadSample.StaysPressed := True;
  FLoadSample.CanFocus := False;
  {$ELSE}
  FLoadSample.Caption := 'Load Sample';
  {$ENDIF}

  FClose := TTMSFNCButton.Create(nil);
  FClose.Parent := Self;
  FClose.OnClick := DoCloseClick;
  FClose.Width := 20;
  FClose.Height := 20;
  FClose.Bitmap.LoadFromResource(TTMSFNCDATAGRIDCLOSE, HInstance);
  FClose.ShowImage := True;
  FClose.ShowText := False;

  {$IFDEF FMXLIB}
  FClose.ImageAlign := TAlignLayout.Client;
  FClose.Stored := False;
  FClose.StaysPressed := True;
  FClose.CanFocus := False;
  {$ELSE}
  FClose.ImageAlign := alClient;
  {$ENDIF}
  TTMSFNCImageOpen(FClose.Image).DottedDesigntimeRectangle := False;

  FSort := TCheckBox.Create(nil);
  FSort.Parent := Self;
  FSort.OnClick := DoSortClick;
  FSort.Width := 75;
  {$IFDEF FMXLIB}
  FSort.Stored := False;
  FSort.Text := 'Sorting';
  FSort.CanFocus := False;
  {$ELSE}
  FSort.Caption := 'Sorting';
  {$ENDIF}

  FFilter := TCheckBox.Create(nil);
  FFilter.Parent := Self;
  FFilter.OnClick := DoFilterClick;
  FFilter.Width := 75;
  {$IFDEF FMXLIB}
  FFilter.Stored := False;
  FFilter.Text := 'Filtering';
  FFilter.CanFocus := False;
  {$ELSE}
  FFilter.Caption := 'Filtering';
  {$ENDIF}

  FEdit := TCheckBox.Create(nil);
  FEdit.Parent := Self;
  FEdit.OnClick := DoEditClick;
  FEdit.Width := 75;
  {$IFDEF FMXLIB}
  FEdit.Stored := False;
  FEdit.Text := 'Editing';
  FEdit.CanFocus := False;
  {$ELSE}
  FEdit.Caption := 'Editing';
  {$ENDIF}

  Width := 120;
  Height := 35;
  FCreateTimer := TTimer.Create(nil);
  FCreateTimer.Interval := 1;
  FCreateTimer.OnTimer := DoCreateTimer;
  FCreateTimer.Enabled := True;
end;

destructor TTMSFNCDataGridDesigner.Destroy;
begin
  FreeAndNil(FCreateTimer);
  FreeAndNil(FLoadSampleBitmap);
  FreeAndNil(FLoadSample);
  FreeAndNil(FCloseBitmap);
  FreeAndNil(FClose);
  FreeAndNil(FSortBitmap);
  FreeAndNil(FSort);
  FreeAndNil(FEditBitmap);
  FreeAndNil(FEdit);
  FreeAndNil(FFilterBitmap);
  FreeAndNil(FFilter);
  inherited;
end;

procedure TTMSFNCDataGridDesigner.DoCloseClick(Sender: TObject);
begin
  if Assigned(FGrid) then
    FGrid.Designer := False;
end;

procedure TTMSFNCDataGridDesigner.DoCreateTimer(Sender: TObject);
begin
  FCreateTimer.Enabled := False;
  FRecreate := True;
  Invalidate;
end;

procedure TTMSFNCDataGridDesigner.DoEditClick(Sender: TObject);
begin
  if Assigned(FGrid) then
  begin
    {$IFDEF FMXLIB}
    FGrid.Options.Editing.Enabled := FEdit.IsChecked;
    {$ELSE}
    FGrid.Options.Editing.Enabled := FEdit.Checked;
    {$ENDIF}
    FGrid.Width := FGrid.Width + 1;
    FGrid.Width := FGrid.Width - 1;
  end;
end;

procedure TTMSFNCDataGridDesigner.DoFilterClick(Sender: TObject);
begin
  if Assigned(FGrid) then
  begin
    {$IFDEF FMXLIB}
    FGrid.Options.Filtering.Enabled := FFilter.IsChecked;
    {$ELSE}
    FGrid.Options.Filtering.Enabled := FFilter.Checked;
    {$ENDIF}
    FGrid.Width := FGrid.Width + 1;
    FGrid.Width := FGrid.Width - 1;
  end;
end;

procedure TTMSFNCDataGridDesigner.DoLoadSampleClick(Sender: TObject);
var
  i: integer;
  str: String;
  ofd: TTMSFNCDataGridDesignerOpenDialog;
  {$IFDEF WEBLIB}
  res: boolean;
  {$ENDIF}
  ext: string;
begin
  if not Assigned(FGrid) then
    Exit;

  ofd := TTMSFNCDataGridDesignerOpenDialog.Create(Self);
  ofd.Options := [TOpenOption.ofFileMustExist];
  ofd.Filter := 'All (*.csv;*.xml;*.txt)|*.csv;'+
  '*.xml;*.txt|CSV Files (*.csv)|*.csv|TEXT Files (*.txt)|*.txt|'+
  'XML Files (*.xml)|*.xml';
  ofd.FilterIndex := 0;
  try
    {$IFNDEF WEBLIB}
    if ofd.Execute then
    {$ENDIF}
    {$IFDEF WEBLIB}
    res := await(Boolean, ofd.Perform);
    if res then
    {$ENDIF}
    begin
      for I := 0 to ofd.Files.Count - 1 do
      begin
        str := ofd.Files[I]{$IFDEF WEBLIB}.Name{$ENDIF};
        ext := ExtractFileExt(str);
        {$IFNDEF WEBLIB}
        str := ofd.Files[I];
        if UpperCase(ext) = '.CSV' then
          FGrid.LoadFromCSVData(str)
        else if UpperCase(ext) = '.TXT' then
          FGrid.LoadFromFileData(str)
        else if UpperCase(ext) = '.XML' then
          FGrid.LoadFromXMLData(str);

        {$ENDIF}
        {$IFDEF WEBLIB}
//        str := await(string, ofd.Files[I].FileAsBase);
//        str := 'data:' + ofd.Files[I].MimeType + ';base64,' + str;
//        bi.Bitmap.LoadFromResource(str);
        {$ENDIF}
      end;
    end;
  finally
    ofd.Free;
  end;
end;

procedure TTMSFNCDataGridDesigner.DoSortClick(Sender: TObject);
begin
  if Assigned(FGrid) then
  begin
    {$IFDEF FMXLIB}
    FGrid.Options.Sorting.Enabled := FSort.IsChecked;
    {$ELSE}
    FGrid.Options.Sorting.Enabled := FSort.Checked;
    {$ENDIF}
    FGrid.Width := FGrid.Width + 1;
    FGrid.Width := FGrid.Width - 1;
  end;
end;

procedure TTMSFNCDataGridDesigner.Draw(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
begin
  inherited;
  RecreateControls;

  AGraphics.DrawBitmap(FClose.BoundsRect, FCloseBitmap);
  AGraphics.DrawBitmap(FLoadSample.BoundsRect, FLoadSampleBitmap);
  AGraphics.DrawBitmap(FSort.BoundsRect, FSortBitmap);
  AGraphics.DrawBitmap(FFilter.BoundsRect, FFilterBitmap);
  AGraphics.DrawBitmap(FEdit.BoundsRect, FEditBitmap);

  if ControlAlignment in [caLeft, caRight] then
    AGraphics.DrawLine(PointF(5, 32.5), PointF(Width - 10, 32.5));
end;

function TTMSFNCDataGridDesigner.GetControlBitmap(AControl: TControl): TTMSFNCBitmap;
begin
  Result := nil;

  if Assigned(AControl) then
  begin
    {$IFDEF FMXLIB}
    if AControl is TStyledControl then
    begin
      TStyledControl(AControl).NeedStyleLookup;
      TStyledControl(AControl).ApplyStyleLookup;
    end;
    {$ENDIF}

    Result := TTMSFNCBitmap(AControl.MakeScreenShot);
  end;
end;

function TTMSFNCDataGridDesigner.HandleDesignHitTest(
  const APoint: TPoint): Boolean;
begin
  Result := True;
end;

procedure TTMSFNCDataGridDesigner.HandleMouseDown(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
var
  pt: TPointF;
begin
  inherited;
  CaptureEx;

  {$IFDEF FMXLIB}
  if not FFirstMouseDown then
  begin
    FFirstMouseDown := True;
    Exit;
  end;
  {$ENDIF}

  pt := PointF(X, Y);

  if PtInRectEx(ConvertToRectF(FLoadSample.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FLoadSample.IsPressed := True;
    {$ELSE}
    {$ENDIF}
  end;

  if PtInRectEx(ConvertToRectF(FClose.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FClose.IsPressed := True;
    {$ELSE}
    {$ENDIF}
  end;

  FRecreate := True;
  Invalidate;

  {$IFDEF FMXLIB}
  FFirstMouseDown := False;
  {$ENDIF}
end;

procedure TTMSFNCDataGridDesigner.HandleMouseUp(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
var
  pt: TPointF;
begin
  inherited;

  ReleaseCaptureEx;

  {$IFDEF FMXLIB}
  if not FFirstMouseUp and FFirstMouseDown then
  begin
    FFirstMouseUp := True;
    Exit;
  end;
  {$ENDIF}

  pt := PointF(X, Y);

  {$IFDEF FMXLIB}
  FLoadSample.IsPressed := False;
  FClose.IsPressed := False;
  {$ENDIF}

  if PtInRectEx(ConvertToRectF(FLoadSample.BoundsRect), pt) then
    TTMSFNCDataGridDesignerControlOpen(FLoadSample).Click;

  if PtInRectEx(ConvertToRectF(FClose.BoundsRect), pt) then
    TTMSFNCDataGridDesignerControlOpen(FClose).Click;

  if PtInRectEx(ConvertToRectF(FSort.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FSort.IsChecked := not FSort.IsChecked;
    {$ELSE}
    FSort.Checked := not FSort.Checked;
    {$ENDIF}
    TTMSFNCDataGridDesignerControlOpen(FSort).Click;
  end;

  if PtInRectEx(ConvertToRectF(FEdit.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FEdit.IsChecked := not FEdit.IsChecked;
    {$ELSE}
    FEdit.Checked := not FEdit.Checked;
    {$ENDIF}
    TTMSFNCDataGridDesignerControlOpen(FEdit).Click;
  end;

  if PtInRectEx(ConvertToRectF(FFilter.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FFilter.IsChecked := not FFilter.IsChecked;
    {$ELSE}
    FFilter.Checked := not FFilter.Checked;
    {$ENDIF}
    TTMSFNCDataGridDesignerControlOpen(FFilter).Click;
  end;

  FRecreate := True;
  Invalidate;

  {$IFDEF FMXLIB}
  FFirstMouseUp := False;
  {$ENDIF}
end;

procedure TTMSFNCDataGridDesigner.RecreateControls;
begin
  if not FRecreate then
    Exit;

  FRecreate := False;

  AddControls;

  FreeAndNil(FLoadSampleBitmap);
  FLoadSampleBitmap := GetControlBitmap(FLoadSample);

  FreeAndNil(FCloseBitmap);
  FCloseBitmap := GetControlBitmap(FClose);

  FreeAndNil(FSortBitmap);
  FSortBitmap := GetControlBitmap(FSort);

  FreeAndNil(FEditBitmap);
  FEditBitmap := GetControlBitmap(FEdit);

  FreeAndNil(FFilterBitmap);
  FFilterBitmap := GetControlBitmap(FFilter);

  RemoveControls;
end;

procedure TTMSFNCDataGridDesigner.RemoveControls;
begin
  FClose.Parent := nil;
  FLoadSample.Parent := nil;
  FSort.Parent := nil;
  FEdit.Parent := nil;
  FFilter.Parent := nil;
end;

procedure TTMSFNCDataGridDesigner.ResetToDefaultStyle;
begin
  inherited;
  FRecreate := True;
  Invalidate;
end;

procedure TTMSFNCDataGridDesigner.UpdateControlAfterResize;
begin
  inherited;
  FRecreate := True;
  Invalidate;
end;

{$ENDIF}

initialization
begin
  {$IFDEF WEBLIB}
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDCLOSE);
  {$ENDIF}
end;

end.
